import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { folderDisplayTitle } from '../utils/folderDisplay';
import { dropClearedRegrade, REGRADE_CLEARED_EVENT } from '../utils/regradeBatchTitle';
import RegradeChip from './RegradeChip';
import { formatQuestionLabel } from '../utils/tableToOneLine';
import { ocrMissingFolderTitle, parseMissingPages } from '../utils/ocrMissing';
import { isSuspectedNote, suspectedPagesFromNotes } from '../utils/ocrNoise';
import { LOCAL_MODELS, GEMINI_MODELS, isKnownLocalModel } from '../utils/localModels';
import ProviderSelect from './ProviderSelect';
import { SORT_PROVIDER_KEY, readPageProvider, writePageProvider } from '../utils/pageProvider';
import { renderPdfToJpegBlobs } from '../utils/pdfText';
import useAiStatus from '../utils/useAiStatus';
import {
  clearAllWorkingTables,
  notifyWorkingTablesReloaded
} from '../utils/clearWorkingTables';
import ZoomableImage from './ZoomableImage';
import DraggableImagePanel from './DraggableImagePanel';
import QuestionReportSheet from './QuestionReportSheet';
import AssessmentTab from './AssessmentTab';
import FailureNotice from './FailureNotice';
import { latePaperOpenFailure } from '../utils/latePapers';
import MultiCheckSelect from './MultiCheckSelect';
import { ClassSectionFilters, SubjectColumn, itemMatchesClass, itemMatchesAssignmentWord, itemsInClass } from './ClassFolderSearch';
import { MatrixText } from './FractionText';
import '../styles/AnalyzeTab.css';

// Same clock the Check tab ("Last Assessed") and User History use.
const formatFolderPkt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-PK', {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }) + ' PKT';
};

function reLateClassLabel(row) {
  const cls = String(row.class_name || '').trim();
  const sub = String(row.subject || '').trim();
  const base = cls && sub
    ? `${cls} - ${sub}`
    : String(row.title || cls || sub || `Folder #${row.rubric_id}`).trim();
  const match = String(row.test_date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
      .toLocaleDateString('en-PK', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' })
    : '';
  return [base, date].filter(Boolean).join(' ');
}

function reLateRemovedLines(detail) {
  if (!detail || typeof detail !== 'object') return [];
  const lines = [];
  for (const row of Array.isArray(detail.sort) ? detail.sort : []) {
    const who = String(row.roll_name || row.unique_code || '').trim();
    const image = String(row.image_no || '').trim();
    const label = row.where === 'open' ? 'Sort (working)' : 'Sort';
    lines.push(`${label}: ${[who, image].filter(Boolean).join(' · ')}`);
  }
  for (const row of Array.isArray(detail.assessment) ? detail.assessment : []) {
    const label = row.where === 'saved' ? 'Assessment (saved)' : 'Assessment';
    const marks = row.marks == null || row.marks === '' ? '' : String(row.marks);
    const bits = [row.student_name, marks, row.image_name].map((value) => String(value || '').trim()).filter(Boolean);
    let line = `${label}: ${bits.join(' · ')}`;
    const comment = String(row.comments || '').replace(/\s+/g, ' ').trim();
    if (comment && !comment.startsWith('{') && !comment.startsWith('[')) {
      line += ` — ${comment.length > 140 ? `${comment.slice(0, 137)}…` : comment}`;
    }
    lines.push(line);
  }
  return lines;
}

const URDU1_GEMINI_MODELS = GEMINI_MODELS;

// "page 3 is cut off" / "pages 3 and 5 are unclear" → [3] or [3, 5].
function checkFailedDetail(stepInfo, failedStudents) {
  const steps = (stepInfo && stepInfo.steps) || {};
  const summary = ['run', 'save', 'scans', 'approve', 'absent']
    .map((key) => {
      const step = steps[key] || {};
      return step.status === 'error' && step.error ? String(step.error).trim() : '';
    })
    .filter(Boolean);
  const people = (failedStudents || []).map((student) => {
    const name = String(student.name || '').trim() || 'Unknown student';
    const cause = (Array.isArray(student.errors) ? student.errors : []).filter(Boolean).join('; ') || 'Check failed';
    return `${name} — ${cause}`;
  });
  return { summary, people };
}

function problemPagesFromReason(errors) {
  const text = (Array.isArray(errors) ? errors : []).join(' ');
  const named = text.match(/pages?\s+[\d, and]+/i);
  if (!named) return [];
  const pages = [];
  const re = /\d+/g;
  let match;
  while ((match = re.exec(named[0]))) {
    const n = Number(match[0]);
    if (n > 0 && !pages.includes(n)) pages.push(n);
  }
  return pages;
}

export default function AnalyzeTab({ recheckRequest, onRecheckComplete }) {
  // Backend AI pipeline mutex state. `sortBusy === true` means either
  // the auto-sort daemon (utils/autoSort.daemon.js) or another teacher's
  // manual Start Sorting click is currently using the local LLM. We
  // render a yellow banner and disable the kickoff button so we don't
  // queue conflicting AI calls.
  const aiStatus = useAiStatus(5000);
  const navigate = useNavigate();
  const location = useLocation();
  const sortBusy = !!(aiStatus && aiStatus.sort && aiStatus.sort.busy);
  const sortBusyMsg = sortBusy
    ? `Auto-sort ${aiStatus.sort.source === 'auto' ? 'daemon' : 'request'} is running${aiStatus.sort.label ? ' on ' + aiStatus.sort.label : ''}. Please wait until it finishes.`
    : '';
  const [scansFolders, setScansFolders] = useState([]);
  const [rubricPreviewOpenIds, setRubricPreviewOpenIds] = useState(() => new Set());
  const [rubricPreviewCache, setRubricPreviewCache] = useState({});
  const [editingRubricId, setEditingRubricId] = useState(null);
  const [rubricEditDraft, setRubricEditDraft] = useState('');
  const [rubricEditSaving, setRubricEditSaving] = useState(false);
  const [rubricEditNote, setRubricEditNote] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(() => {
    try { return localStorage.getItem('analyzeTab_selectedFolderId') || ''; } catch { return ''; }
  });
  const [folderPath, setFolderPath] = useState('');
  const [batchSize, setBatchSize] = useState(15);
  const [limit, setLimit] = useState('MR1');
  const [sortProvider, setSortProvider] = useState(() => readPageProvider(SORT_PROVIDER_KEY));
  const [model, setModel] = useState(() => {
    // Restore the user's last-picked model from localStorage so reloads
    // don't snap back to a hardcoded fallback. Two separate keys —
    // `sortLocalModel` for local-provider runs, `sortGeminiModel` for
    // Gemini — because switching providers requires a model from a
    // different family and we want each path's last choice remembered.
    const isLocal = readPageProvider(SORT_PROVIDER_KEY) === 'local';
    const stored = isLocal
      ? localStorage.getItem('sortLocalModel')
      : localStorage.getItem('sortGeminiModel');
    if (isLocal) return isKnownLocalModel(stored) ? stored : 'qwen35_4b_revised';
    if (stored) return stored;
    return 'gemini-2.5-flash-lite';
  });

  // Persist the user's model choice on every change so the next reload
  // restores it (default-restore loop above). Keys are provider-scoped
  // so swapping providers doesn't clobber the other family's choice.
  useEffect(() => {
    if (!model) return;
    try {
      if (sortProvider === 'local' && isKnownLocalModel(model)) {
        localStorage.setItem('sortLocalModel', model);
      } else if (sortProvider === 'gemini' && String(model).startsWith('gemini-')) {
        localStorage.setItem('sortGeminiModel', model);
      }
    } catch (_) { /* quota — ignore */ }
  }, [model, sortProvider]);

  // Gemini model sent with each UrOcr click. Kept apart from the Sort model.
  const URDU_OCR_DEFAULT_MODEL = 'gemini-3.8-flash';
  const [urdu1Model, setUrdu1Model] = useState(() => {
    try {
      if (localStorage.getItem('urdu1BatchModelDefault') !== URDU_OCR_DEFAULT_MODEL) {
        localStorage.setItem('urdu1BatchModelDefault', URDU_OCR_DEFAULT_MODEL);
        localStorage.setItem('urdu1BatchModel', URDU_OCR_DEFAULT_MODEL);
        return URDU_OCR_DEFAULT_MODEL;
      }
      const stored = localStorage.getItem('urdu1BatchModel') || '';
      if (URDU1_GEMINI_MODELS.some((m) => m.value === stored)) return stored;
    } catch { /* ignore */ }
    return URDU_OCR_DEFAULT_MODEL;
  });
  useEffect(() => {
    try { localStorage.setItem('urdu1BatchModel', urdu1Model); } catch { /* ignore */ }
  }, [urdu1Model]);

  // Persist the user's current Sort-tab model + provider to the server so
  // the backend auto-sort daemon (utils/autoSort.daemon.js) can use the
  // same model when it picks up folders this user uploaded. Debounced
  // 500ms so rapid dropdown clicks don't hammer the endpoint. Fire-and-
  // forget — daemon falls back to env defaults when the field is NULL,
  // so a failed sync doesn't break anything.
  useEffect(() => {
    const handle = setTimeout(() => {
      const provider = sortProvider;
      const localModel = provider === 'local' ? model : null;
      const token = localStorage.getItem('authToken');
      if (!token) return;
      fetch(`${window.API_BASE_URL}/auth/me/sort-prefs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ provider, local_model: localModel })
      }).catch(err => console.warn('[AnalyzeTab] sort-prefs sync failed:', err));
    }, 500);
    return () => clearTimeout(handle);
  }, [model, sortProvider]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // "Get PDF" import (online students who submit handwritten PDFs) in flight.
  const [gettingPdf, setGettingPdf] = useState(false);
  const [gettingOcr, setGettingOcr] = useState(false);
  const [blockedDialog, setBlockedDialog] = useState(null); // { title, message, suggestion? } | null
  const [ocrSortDialog, setOcrSortDialog] = useState(null); // null | { folderTitle, hasExistingList }
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [message, setMessage] = useState({ show: false, text: '', type: '' });
  useEffect(() => {
    const notice = location.state && location.state.notice;
    if (!notice) return;
    setMessage({ show: true, text: String(notice), type: 'error' });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.key]);
  const [currentDateOffset, setCurrentDateOffset] = useState(0); // 0 = today, -1 = yesterday, +1 = tomorrow
  const [sessionId, setSessionId] = useState(null);
  const [progressDetails, setProgressDetails] = useState(null);
  const [progress, setProgress] = useState('');
  const [specificImageRange, setSpecificImageRange] = useState('');
  const [showReanalyzeOptions, setShowReanalyzeOptions] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [highlightSort, setHighlightSort] = useState(null); // null | 'zero' | 'error' | 'selected'
  const [editingCell, setEditingCell] = useState(null); // {id, field, value, token}
  const editTokenRef = useRef(0);
  const [previewImage, setPreviewImage] = useState(null); // { url, name }
  const [previewLoading, setPreviewLoading] = useState(null); // filename being loaded
  const [editedValues, setEditedValues] = useState({});
  const [copiedRollName, setCopiedRollName] = useState(null); // roll_name copied for paste
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  // End of a from–to range. Null means the filter is the single start day.
  const [rangeEndDate, setRangeEndDate] = useState(null);
  // Opening Sort Papers shows the calendar only. Cards appear after a day,
  // All, Today, or Future Cards is chosen.
  const [showAllFolders, setShowAllFolders] = useState(false);
  const [showFolderSection, setShowFolderSection] = useState(true); // hides when sorting starts, resets on tab remount
  // Collapse just the monthly calendar block (heading + filter chips + folder
  // list stay visible) once the user clicks Import Sorted List, so the
  // imported results table has more vertical space. Re-expanded via the
  // "Change folder" button or by Delete All.
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  // Filters at the top of the Scans Folder calendar — let the teacher whittle
  // down to "every paper this teacher uploaded" or "every paper for this class"
  // so the whole calendar + folder list collapses to one teacher's / class's
  // work in one go. Empty string === "no filter".
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterClass, setFilterClass] = useState([]);
  const [filterSubject, setFilterSubject] = useState([]);
  // Class / section / subject buttons, same as Student Lists. A class click
  // lists that class on every date. A calendar click clears these.
  const [gradeFilter, setGradeFilter] = useState(null);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [subjectPick, setSubjectPick] = useState(null);
  // First word of the assignment name. Narrows whichever list is showing.
  const [assignmentWord, setAssignmentWord] = useState(null);
  // Status filters — independent segmented controls that intersect with
  // the teacher/class filters. 'all' is the no-op state for each.
  //   filterSorted:  'all' | 'sorted' | 'unsorted'   (driven by snapshotFolderIdSet)
  //   filterChecked: 'all' | 'checked' | 'unchecked' (driven by completed_at/printed_at)
  //   filterErrors:  'all' | 'errors' | 'late' | 'regrade'
  //     errors  — folders that still show an error chip, including a marks
  //               warning (saved marks ≠ question-row sum)
  //     late    — folders with a Late papers chip
  //     regrade — folders with a pending Re-Grade request
  //   filterImageChanges: when on, only folders with Images added or
  //     Images deleted chips stay on the page
  const [filterSorted, setFilterSorted] = useState('all');
  const [filterChecked, setFilterChecked] = useState('all');
  const [filterErrors, setFilterErrors] = useState('all');
  const [filterImageChanges, setFilterImageChanges] = useState(false);
  // "Future Cards" view — when on, the folder grid shows only folders dated
  // tomorrow (00:00) or later, ignoring the single-date calendar filter.
  const [showFuture, setShowFuture] = useState(false);
  // Picking a specific calendar day (Today or a calendar cell) exits Future
  // mode. The "All" button and the Future toggle handle their own reset.
  useEffect(() => { if (selectedCalendarDate) setShowFuture(false); }, [selectedCalendarDate]);

  // ── Login Edupage extension bridge ──────────────────────────────────────
  // Each folder card carries a "Login" button that hands the card off to the
  // "Login Edupage" browser extension. The extension matches the creator
  // email against its credential CSV and auto-logs into EduPage for that
  // folder — the same result as the extension's manual "pick a card", but one
  // click straight from the card. Page scripts can't call chrome.* APIs, so we
  // post a window message that the extension's content_bridge.js (injected on
  // this origin) relays to its background service worker. `eduBridgeReady`
  // tracks whether that bridge answered our presence ping.
  const [eduBridgeReady, setEduBridgeReady] = useState(false);
  // Folder ids whose images are currently being uploaded on demand (the
  // per-card "Upload" button). Used to show a spinner / disable the button so
  // a folder can't be double-triggered mid-upload.
  const [uploadingFolderIds, setUploadingFolderIds] = useState(() => new Set());
  const [removingExtrasIds, setRemovingExtrasIds] = useState(() => new Set());
  // Folder ids that finished uploading this session — flips the button green
  // with an "Uploaded" label.
  const [uploadedFolderIds, setUploadedFolderIds] = useState(() => new Set());
  const [reportFolder, setReportFolder] = useState(null);
  const [folderReviews, setFolderReviews] = useState({ verified: {}, requests: {} });
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window) return;
      const d = ev.data;
      if (!d || typeof d !== 'object' || d.source !== 'edupage-selector') return;
      if (d.type === 'EDU_BRIDGE_READY') {
        setEduBridgeReady(true);
      } else if (d.type === 'EDU_LOGIN_RESULT') {
        if (d.ok) {
          showMessage(`Opening EduPage for ${d.username || 'this account'}…`, 'success');
        } else {
          showMessage(
            d.reason === 'not_found'
              ? "EduPage login: this folder's creator email isn't in the extension's credential list."
              : 'EduPage login failed — is the Login Edupage extension installed and updated?',
            'error'
          );
        }
      }
    };
    window.addEventListener('message', onMsg);
    // Ping the bridge in case its content script loaded (and broadcast READY)
    // before this listener attached.
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_BRIDGE_PING' }, '*');
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconciliation view: imports the StudentsTab list that matches the
  // selected folder's class+subject and lays the AI results out next to it.
  // Lets the teacher spot AI roll-number misreads at a glance.
  //
  // The list is never persisted — it is re-fetched from the server on every
  // mount / folder switch. Caching it per-folder in localStorage used to blow
  // through the 5MB quota (one entry per folder, never evicted) and served
  // stale rosters after StudentsTab edits.
  const [viewMode, setViewMode] = useState('reconciliation'); // 'flat' | 'reconciliation'
  const [importedStudents, setImportedStudents] = useState([]); // [{ student_name, ... }]
  const [importedStudentsLoading, setImportedStudentsLoading] = useState(false);
  const [importedListsMeta, setImportedListsMeta] = useState([]); // matched StudentsTab lists for header display
  // Folder whose imported list the teacher just cleared via the trash button.
  // Kept in memory only, so a scansFolders refresh doesn't immediately
  // re-populate the list. Reset as soon as the selected folder changes, which
  // is how the teacher re-imports.
  const clearedImportFolderRef = useRef(null);
  // True while the open folder is showing the shared saved sort. Edits then
  // write that list, which is what every account sees. A private in-progress
  // sort (Get Sorting / re-analysis) clears this and keeps its own rows.
  const viewingSavedSortRef = useRef(false);

  // Per-folder sorted-list snapshots. The teacher explicitly clicks "Save
  // Sorted List" after correcting roll-number mistakes; the snapshot is
  // pinned to the scan folder and survives even if the live analyzed_papers
  // rows for that folder are later deleted. "Import Sorted List" pulls the
  // snapshot back for the currently-selected folder — for example, when a
  // student requests a recheck days later.
  const [snapshotMeta, setSnapshotMeta] = useState(null); // null | { saved_at, record_count }
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  // Folder IDs (parsed from "rubrics/<id>" snapshot paths) that have a saved
  // snapshot. Drives the green badge + bold styling on folder cards in the
  // calendar so the teacher can see at a glance which folders are "pinned".
  const [snapshotFolderIdSet, setSnapshotFolderIdSet] = useState(() => new Set());
  // Map of rubric.id → snapshot workflow status ('saved' | 'reviewed') for
  // every folder that has a snapshot. Drives the two-state Review / Reviewed
  // badge on folder cards: a freshly (auto-)sorted folder is 'saved' →
  // "Review"; once an examiner clicks the "Reviewed" button it flips to
  // 'reviewed' → "Reviewed".
  const [snapshotFolderStatusMap, setSnapshotFolderStatusMap] = useState(() => new Map());
  // rubric.id → { added: ["82747"], deleted: ["82740"] }. Image numbers the
  // folder watcher uploaded or removed after this folder already had a
  // sorted list. The snapshot itself is not cleared.
  const [imageDeltaMap, setImageDeltaMap] = useState(() => new Map());
  const [reLateLogOpen, setReLateLogOpen] = useState(false);
  const [reLateLogRows, setReLateLogRows] = useState([]);
  const [reLateLogLoading, setReLateLogLoading] = useState(false);
  const [reLateLogError, setReLateLogError] = useState('');
  // Map of rubric.id → { saved_at, record_count } from the Check-tab saved
  // table. Drives the per-card "Last checked" time (same clock as Last Assessed).
  const [assessSnapshotMetaMap, setAssessSnapshotMetaMap] = useState(() => new Map());
  const [overMaxMarksMap, setOverMaxMarksMap] = useState(() => new Map());
  const [overMaxHoverId, setOverMaxHoverId] = useState(null);
  // rubric.id → { students: [{ name, phrases }] } when the saved marks
  // contain the grader's own reasoning ("Wait, looking closer…").
  const [thinkingMarksMap, setThinkingMarksMap] = useState(() => new Map());
  const [thinkingMarksHoverId, setThinkingMarksHoverId] = useState(null);
  // `${folderId}:${slot}` while the pointer is on a Re-Grade chip, so that
  // chip can list the students saved on the request.
  const [regradeHoverId, setRegradeHoverId] = useState(null);
  // rubric.id → { students: [{ name, labels }] } when a written question
  // label could not be placed on a rubric row. The card tag lists them.
  const [unmatchedLabelMap, setUnmatchedLabelMap] = useState(() => new Map());
  const [unmatchedHoverId, setUnmatchedHoverId] = useState(null);
  // rubric.id → { students: [{ name, notes }] } when the saved OCR transcript
  // is model reasoning or other text that is not the handwriting.
  const [ocrNoiseMap, setOcrNoiseMap] = useState(() => new Map());
  const [ocrNoiseHoverId, setOcrNoiseHoverId] = useState(null);
  // rubric.id → { students: [{ name, pages }] } when OCR marked a page
  // before the last image as "blank page". The last page may be blank.
  const [unexpectedBlankMap, setUnexpectedBlankMap] = useState(() => new Map());
  const [unexpectedBlankHoverId, setUnexpectedBlankHoverId] = useState(null);
  // rubric.id → { students: [{ name, sheets }] } reviewed rolls missing
  // from that class's saved assessment table.
  const [latePaperMap, setLatePaperMap] = useState(() => new Map());
  const [latePaperHoverId, setLatePaperHoverId] = useState(null);
  // Transcript opened on top of this page. Sort stays mounted.
  const [quickTranscript, setQuickTranscript] = useState(null);
  // rubric.id → { students: [{ name, errors }] } when the latest saved
  // row for that student failed. The Check failed chip counts them.
  const [checkErrorMap, setCheckErrorMap] = useState(() => new Map());
  const [checkErrorHoverId, setCheckErrorHoverId] = useState(null);
  // Map of rubric.id → { attempts, last_error } for folders where the
  // backend auto-sort daemon has tried at least once. Drives the
  // per-card "Retry auto-sort" button when attempts >= 3 (the daemon's
  // failure ceiling). Empty map = nothing to surface; daemon hasn't
  // touched any folder yet.
  const [autoSortStatusMap, setAutoSortStatusMap] = useState(() => new Map());
  // Global auto-sort daemon toggle (server-side). Defaults ON; the daemon
  // only picks folders whose examiner clicked Start on the card.
  const [autoSortEnabled, setAutoSortEnabled] = useState(true);
  const [autoSortDaemonBusy, setAutoSortDaemonBusy] = useState(false);
  const [autoSortToggleBusy, setAutoSortToggleBusy] = useState(false);
  const [startAutoSortBusyId, setStartAutoSortBusyId] = useState(null);
  // Set of rubric.id (as String) whose LOCAL folder and WEBSITE image counts
  // disagree — the folder-server reported an incomplete upload and paused
  // sorting/checking for it. These cards render RED so the mismatch is obvious.
  // The set is primed from the auto-sort status feed (the block reason is stored
  // in auto_sort_last_error as "Upload incomplete: …") so it survives reloads,
  // and updated live from each Upload button response.
  const [uploadMismatchIds, setUploadMismatchIds] = useState(() => new Set());
  // Map of rubric.id → { by, run_at, steps } for folders an auto-check ai user
  // (ai1 / ai2 / ai3) has run. `by` is the ai username; `run_at` is when
  // grading started; `steps` is a map of run|save|scans|approve|absent →
  // { status: 'running'|'done'|'error', error, at }.
  // Drives the per-card "AI1 ☑☑☒☐" step boxes so the progress + failure point of an
  // automatic check is visible on the website (not just the ai-browser console).
  const [autoCheckStepsMap, setAutoCheckStepsMap] = useState(() => new Map());
  // Map of rubric.id → auto-check medium ('english' | 'urdu' | 'urog') for folders
  // the teacher has opted into automatic checking. Drives the selected state
  // of the per-card Urdu/English buttons. Absent id = auto-check OFF.
  const [autoCheckMediumMap, setAutoCheckMediumMap] = useState(() => new Map());
  // Guards the per-card Urdu/English buttons from double-clicks while the
  // set-medium request is in flight (rubric.id currently saving, or null).
  const [autoCheckMediumBusyId, setAutoCheckMediumBusyId] = useState(null);
  // rubric.id → { state, model, error, regrade_at } for the Urdu-1 Gemini
  // batch. Independent of the English/Urdu medium buttons.
  const [urdu1BatchMap, setUrdu1BatchMap] = useState(() => new Map());
  const [urdu1BusyId, setUrdu1BusyId] = useState(null);
  // Map of rubric.id → { images, docx, ready } from the local watcher's
  // /docx-counts, for URDU-medium folders only. Shows "N images · M docx" on
  // the card and gates auto-check (Urdu needs images === docx typed-answer
  // .docx before checking). Empty/absent = watcher unreachable → fail-open.
  const [docxReadyMap, setDocxReadyMap] = useState(() => new Map());

  const isLocalProvider = sortProvider === 'local';
  const isDeepseekProvider = sortProvider === 'deepseek';
  const isMinimaxProvider = sortProvider === 'minimax';
  const isGeminiProvider = sortProvider === 'gemini';
  const changeSortProvider = (value) => {
    setSortProvider(value);
    writePageProvider(SORT_PROVIDER_KEY, value);
    if (value === 'local') {
      const stored = localStorage.getItem('sortLocalModel');
      setModel(isKnownLocalModel(stored) ? stored : 'qwen35_4b_revised');
    } else if (value === 'gemini') {
      const stored = localStorage.getItem('sortGeminiModel');
      setModel(stored && String(stored).startsWith('gemini-') ? stored : 'gemini-2.5-flash-lite');
    }
  };
  const API_BASE_URL = window.API_BASE_URL;

  const openReLateLog = async () => {
    setReLateLogOpen(true);
    setReLateLogLoading(true);
    setReLateLogError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/folders/image-delta-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Could not load the Re-Late log');
      }
      setReLateLogRows(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setReLateLogRows([]);
      setReLateLogError(err.message || 'Could not load the Re-Late log');
    } finally {
      setReLateLogLoading(false);
    }
  };

  // Tracks how many papers the backend had finished the last time we refreshed the table.
  // When prog.completed goes up, we know a new paper finished and refetch to stream it into the UI.
  const lastStreamedCountRef = useRef(0);

  // Tracks which result IDs we've already pushed to the Mistakes tab so the
  // auto-send doesn't re-post the same row every time analysis completes /
  // re-analysis runs / the page refetches. Reset is not needed across the
  // session — the backend dedupes-by-id implicitly via the row's identity.
  const sentMistakesRef = useRef(new Set());

  // Auto-forward every result row with an invalid roll code (yellow rows —
  // the ones the user explicitly said should always flow to MistakesTab) to
  // the unmatched_students table as a PERMANENT record. Using PERMANENT:
  // means the entry is never auto-purged by the approve cleanup window, so
  // MistakesTab keeps a durable record of wrong roll numbers.
  //
  // We use the same payload shape StudentsTab → "Save to Mistakes" uses, so
  // MistakesTab renders these the same way it renders any other source.
  const autoSendInvalidRollsToMistakes = async (rowsToScan) => {
    const rows = Array.isArray(rowsToScan) ? rowsToScan : [];
    if (rows.length === 0) return;

    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    if (!folder) return;
    const folderClass = folder.class || '';
    const folderSubject = folder.subject || '';
    if (!folderClass && !folderSubject) return; // nothing useful to attach the record to

    // Yellow-row test mirrors the one used for sort highlighting below.
    const isInvalidRoll = r => !/^[A-Za-z]\d{2}$/.test(r?.unique_code || '');

    // AI-side processing errors (backend `analyze.controller.js` pushes rows
    // with `unique_code: '000'` and `roll_name: '(000)Processing error: …'`
    // or '(000)AI connection issue …' when Gemini fails on a sheet). These
    // are system failures, not data-entry mistakes, so they must not flow
    // into MistakesTab — otherwise every transient AI hiccup permanently
    // pollutes the unmatched_students table.
    const isAIProcessingError = r => /^\(000\)\s*(Processing error|AI connection issue)/i.test(r?.roll_name || '');

    const candidates = rows.filter(r => isInvalidRoll(r) && !isAIProcessingError(r) && r.id && !sentMistakesRef.current.has(r.id));
    if (candidates.length === 0) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    for (const r of candidates) {
      // Mark optimistically so a concurrent stream tick can't double-send the
      // same id. If the POST fails we leave it marked anyway — better than
      // an unbounded retry loop on an endpoint the user can't see.
      sentMistakesRef.current.add(r.id);
      try {
        const commentParts = [];
        if (r.image_no) commentParts.push(`Image: ${r.image_no}`);
        if (r.unique_code) commentParts.push(`Read roll as '${r.unique_code}'`);
        const reason = `Invalid roll code on auto-sort${commentParts.length ? ' — ' + commentParts.join(', ') : ''}`;

        await fetch(`${API_BASE_URL}/students/unmatched`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            class: folderClass,
            section: '',
            subject: folderSubject,
            name: r.roll_name || '',
            marks: r.marks || '',
            comments: 'PERMANENT:' + reason,
            teacher_name: folder.created_by || '',
            test_date: folder.test_date || ''
          })
        });
      } catch (err) {
        console.error('[AnalyzeTab] Failed to auto-send invalid-roll row to Mistakes:', err);
      }
    }
  };

  const fetchFolderReviews = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/folder-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) return;
      setFolderReviews({
        verified: data.verified || {},
        requests: data.requests || {}
      });
    } catch (error) {
      console.error('Error fetching folder reviews:', error);
    }
  };

  const applyFolderReview = (folderId, review) => {
    if (!review) return;
    const id = String(folderId);
    setFolderReviews((prev) => ({
      verified: { ...prev.verified, [id]: !!review.result_ok },
      requests: { ...prev.requests, [id]: Array.isArray(review.requests) ? review.requests : [] }
    }));
  };

  useEffect(() => {
    const onClear = (event) => {
      const detail = (event && event.detail) || {};
      if (!detail.folderId || detail.slot == null) return;
      setFolderReviews((prev) => dropClearedRegrade(prev, detail.folderId, detail.slot));
    };
    window.addEventListener(REGRADE_CLEARED_EVENT, onClear);
    return () => window.removeEventListener(REGRADE_CLEARED_EVENT, onClear);
  }, []);

  // Load scans folders and results on component mount
  useEffect(() => {
    fetchScansFolders();
    fetchFolderReviews();
    fetchSnapshotFolderSet();
    // Re-poll the snapshot folder set every 30s so snapshots saved by the
    // backend auto-sort daemon (utils/autoSort.daemon.js) flip the green
    // "📌 Sorted" badge on without needing a manual refresh. Same
    // interval brings back the auto-sort status map (Retry button on
    // failures) since fetchSnapshotFolderSet now also fetches that.
    const refreshHandle = setInterval(() => {
      fetchSnapshotFolderSet();
    }, 30_000);
    // Refresh immediately when the tab regains focus — covers the case
    // where the user came back to a long-idle tab and wants to see the
    // latest auto-sort progress without waiting up to 30s.
    const onFocus = () => {
      fetchSnapshotFolderSet();
      fetchFolderReviews();
    };
    const onMissingCleared = () => { fetchScansFolders(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('ocr-missing-cleared', onMissingCleared);
    return () => {
      clearInterval(refreshHandle);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ocr-missing-cleared', onMissingCleared);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop the imported-student tables earlier builds wrote to localStorage
  // (one `analyzeTab_importedStudents_<folderId>` entry per folder, never
  // evicted). They are dead weight now and were filling the 5MB quota, which
  // made unrelated writes such as the auth token silently fail.
  useEffect(() => {
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('analyzeTab_importedStudents_'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* localStorage unavailable — non-fatal */ }
  }, []);

  // Persist the selected folder so switching tabs / refreshing doesn't reset
  // it to empty (which would also lose the imported reconciliation list).
  useEffect(() => {
    try {
      if (selectedFolderId) localStorage.setItem('analyzeTab_selectedFolderId', selectedFolderId);
      else localStorage.removeItem('analyzeTab_selectedFolderId');
    } catch { /* localStorage unavailable — non-fatal */ }
    return () => { clearedImportFolderRef.current = null; };
  }, [selectedFolderId]);

  // Check whether the selected folder has a saved snapshot so the Import
  // button can light up / disable, and so we can show "Last saved …" hint.
  useEffect(() => {
    if (folderPath) fetchSnapshotMeta(folderPath);
    else setSnapshotMeta(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath]);

  // The clicked card is the only folder whose AI rows belong on screen.
  useEffect(() => {
    const next = selectedFolderId ? `rubrics/${selectedFolderId}` : '';
    setFolderPath((prev) => (prev === next ? prev : next));
  }, [selectedFolderId]);

  // When the user picks a folder, find every StudentsTab list whose class +
  // subject match the folder, then pull all students from those lists. We
  // merge across sections because the scan folder doesn't track section, so
  // the teacher gets one combined "expected students" list to reconcile
  // against the AI output. Names are de-duplicated by lowercase student_name.
  useEffect(() => {
    let cancelled = false;
    if (!selectedFolderId) {
      setImportedStudents([]);
      setImportedListsMeta([]);
      return;
    }

    if (clearedImportFolderRef.current === String(selectedFolderId)) return;

    // Bail out (without wiping) if the folder list hasn't arrived yet — the
    // effect will re-fire when scansFolders populates.
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    if (!folder) return;
    const folderClass = (folder.class || '').trim();
    const folderSubject = (folder.subject || '').trim();
    if (!folderClass || !folderSubject) return;

    (async () => {
      setImportedStudentsLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const listsRes = await fetch(`${API_BASE_URL}/students/lists`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!listsRes.ok) {
          if (!cancelled) { setImportedStudents([]); setImportedListsMeta([]); }
          return;
        }
        const allLists = await listsRes.json();
        const matchedLists = (Array.isArray(allLists) ? allLists : []).filter(l =>
          (l.class || '').trim().toLowerCase() === folderClass.toLowerCase() &&
          (l.subject || '').trim().toLowerCase() === folderSubject.toLowerCase()
        );
        if (cancelled) return;

        if (matchedLists.length === 0) {
          setImportedListsMeta([]);
          setImportedStudents([]);
          return;
        }

        setImportedListsMeta(matchedLists);

        const studentResponses = await Promise.all(matchedLists.map(l =>
          fetch(
            `${API_BASE_URL}/students?class=${encodeURIComponent(l.class)}&section=${encodeURIComponent(l.section || '')}&subject=${encodeURIComponent(l.subject)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          ).then(r => r.ok ? r.json() : []).catch(() => [])
        ));
        if (cancelled) return;

        const seen = new Set();
        const merged = [];
        for (const arr of studentResponses) {
          for (const s of (arr || [])) {
            const key = (s.student_name || '').trim().toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            merged.push(s);
          }
        }
        merged.sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));
        setImportedStudents(merged);
      } catch {
        if (!cancelled) { setImportedStudents([]); setImportedListsMeta([]); }
      } finally {
        if (!cancelled) setImportedStudentsLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId, scansFolders]);

  // Handle recheck request from AssessmentTab
  useEffect(() => {
    if (!recheckRequest || !recheckRequest.imageNames || recheckRequest.imageNames.length === 0) {
      return;
    }

    const handleRecheckRequest = async () => {
      console.log('[AnalyzeTab] Received recheck request:', recheckRequest);
      
      // Wait for results to be loaded
      if (results.length === 0) {
        console.log('[AnalyzeTab] Waiting for results to load...');
        await fetchResults();
      }

      // Set the folder path from the request
      if (recheckRequest.folderPath) {
        setFolderPath(recheckRequest.folderPath);
        
        // Try to find and select the matching folder
        const folderIdMatch = recheckRequest.folderPath.match(/rubrics\/(\d+)/);
        if (folderIdMatch) {
          setSelectedFolderId(folderIdMatch[1]);
        }
      }

      // Find images in results that match the requested image names
      const matchingResults = results.filter(r => {
        // Check if any of the image names match
        return recheckRequest.imageNames.some(imgName => {
          // Match against image_no field (could be single or comma-separated)
          const imageNos = (r.image_no || '').split(',').map(n => n.trim());
          return imageNos.some(img => 
            img.toLowerCase().includes(imgName.toLowerCase()) || 
            imgName.toLowerCase().includes(img.toLowerCase())
          );
        });
      });

      console.log('[AnalyzeTab] Matching results found:', matchingResults.length);

      if (matchingResults.length === 0) {
        showMessage(`Could not find matching images in Sort data. Please select images manually.`, 'error');
        if (onRecheckComplete) onRecheckComplete();
        return;
      }

      // Get unique codes from matching results
      const uniqueCodes = [...new Set(matchingResults.map(r => r.unique_code).filter(Boolean))];
      console.log('[AnalyzeTab] Unique codes found:', uniqueCodes);

      // Find ALL images for these students (by unique_code)
      const allStudentImages = results.filter(r => uniqueCodes.includes(r.unique_code));
      console.log('[AnalyzeTab] All images for students:', allStudentImages.length);

      // Select all image names for these students
      const imagesToReanalyze = allStudentImages.map(r => r.image_no);
      setSelectedImages(imagesToReanalyze);

      // Show message about what will be reanalyzed
      const studentNames = [...new Set(allStudentImages.map(r => r.roll_name).filter(Boolean))];
      showMessage(
        `Found ${allStudentImages.length} image(s) for ${studentNames.length} student(s): ${studentNames.slice(0, 3).join(', ')}${studentNames.length > 3 ? '...' : ''}. Click "Re-analyze Selected" to proceed.`,
        'success'
      );

      // Clear the request
      if (onRecheckComplete) onRecheckComplete();
    };

    handleRecheckRequest();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckRequest]);

  // Poll for progress updates
  useEffect(() => {
    if (!sessionId || !isAnalyzing) return;

    const pollProgress = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/analyze/progress/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const prog = data.progress;
          setProgressDetails(prog);

          if (prog.status === 'completed') {
            setProgress('Sorting completed! Go to Check tab to view results.');
            showMessage(
              `Successfully analyzed ${prog.completed} scans. Results loaded.`,
              'success'
            );
            setIsAnalyzing(false);
            setSessionId(null);
            lastStreamedCountRef.current = 0;
            const finalRows = await fetchResults(); // Final refresh
            await autoSendInvalidRollsToMistakes(finalRows);
          } else if (prog.status === 'failed') {
            setProgress('');
            showMessage(prog.error || 'Sorting failed', 'error');
            setIsAnalyzing(false);
            setSessionId(null);
            lastStreamedCountRef.current = 0;
          } else if (prog.status === 'cancelled') {
            setProgress('Sorting cancelled');
            showMessage('Sorting was cancelled', 'info');
            setIsAnalyzing(false);
            setSessionId(null);
            lastStreamedCountRef.current = 0;
            const partialRows = await fetchResults(); // Refresh to show partial results
            await autoSendInvalidRollsToMistakes(partialRows);
          } else if (prog.status === 'processing') {
            if (prog.currentStudent) {
              setProgress(`Processing ${prog.currentStudent} (${prog.completed}/${prog.total} completed)...`);
            } else {
              setProgress(`Processing... ${prog.completed}/${prog.total} completed`);
            }
            // Stream: whenever a new paper has finished, update the results
            // table so rows appear one-by-one instead of only at the end.
            const completedNow = Number(prog.completed) || 0;
            if (completedNow > lastStreamedCountRef.current) {
              lastStreamedCountRef.current = completedNow;

              // First, log what keys the progress payload actually carries, so we
              // can see in the browser console what the backend is exposing.
              console.log('[AnalyzeTab] Streaming tick — prog keys:', Object.keys(prog || {}));

              // Try several possible field names that a backend might use to
              // attach the partial rows to the progress object.
              const candidateKeys = ['results', 'data', 'records', 'items', 'rows', 'extracted'];
              let inlineRows = null;
              for (const key of candidateKeys) {
                if (Array.isArray(prog[key]) && prog[key].length > 0) {
                  inlineRows = prog[key];
                  console.log(`[AnalyzeTab] Using prog.${key} (${inlineRows.length} row(s)) for streaming`);
                  break;
                }
              }

              if (inlineRows) {
                setResults(inlineRows);
              } else {
                // Fall back to fetching /analyze/results, scoped to the active
                // folder (matches how AssessmentTab fetches analyzed data).
                try {
                  const token = localStorage.getItem('authToken');
                  const url = folderPath
                    ? `${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(folderPath)}`
                    : `${API_BASE_URL}/analyze/results`;
                  const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
                  if (res.ok) {
                    const payload = await res.json();
                    const rows = payload.data || [];
                    console.log(`[AnalyzeTab] Fallback fetch returned ${rows.length} row(s)`);
                    if (rows.length > 0) {
                      setResults(rows);
                    }
                  }
                } catch (err) {
                  console.error('[AnalyzeTab] Streaming fetch error:', err);
                }
              }
            }
          }
        } else {
          let errorMessage = 'Unable to fetch sorting progress';
          try {
            const errData = await response.json();
            if (errData?.message) errorMessage = errData.message;
          } catch (_) {
            // Ignore JSON parse errors for non-JSON responses
          }

          if (response.status === 404) {
            setIsAnalyzing(false);
            setSessionId(null);
            setProgress('');
            setProgressDetails(null);
            showMessage('Sorting session expired or server restarted. Please start again.', 'error');
          } else if (response.status === 401 || response.status === 403) {
            setIsAnalyzing(false);
            setSessionId(null);
            setProgress('');
            setProgressDetails(null);
            showMessage('Session unauthorized. Please login again.', 'error');
          } else {
            console.error('[Analyze] Progress polling failed:', response.status, errorMessage);
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    };

    // Poll every 2 seconds
    const interval = setInterval(pollProgress, 2000);
    pollProgress(); // Initial poll

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isAnalyzing]);

  // Pull the list of all folder_paths that currently have a snapshot, then
  // build a Set of folder IDs (extracted from "rubrics/<id>") so the
  // calendar/folder-list can mark those cards as "saved". Cheap one-shot
  // fetch — refreshed after any save / import / delete.
  const fetchSnapshotFolderSet = async () => {
    // The blank-page label reads a saved page list. Start it with the cards
    // so it does not wait for the other folder checks.
    refreshUnexpectedBlanks();
    refreshLatePapers();
    refreshMarkCardFlags();
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };
    // Each card detail paints when its own request returns. None of them
    // waits for the request in front of it.
    await Promise.all([
    (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/analyze/snapshots/folders`, {
        headers
      });
      if (!response.ok) return;
      const data = await response.json();
      const ids = new Set();
      const statusMap = new Map();
      for (const row of (data.data || [])) {
        const m = String(row.folder_path || '').match(/rubrics\/(\d+)/);
        if (m) {
          ids.add(m[1]);
          statusMap.set(m[1], row.status || 'saved');
        }
      }
      setSnapshotFolderIdSet(ids);
      setSnapshotFolderStatusMap(statusMap);
    } catch (err) {
      console.error('[AnalyzeTab] Failed to fetch snapshot folder set:', err);
    }
    })(),
    (async () => {
    try {
      const statusRes = await fetch(`${API_BASE_URL}/analyze/folders/auto-sort-status`, {
        headers
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        const map = new Map();
        const mismatch = new Set();
        for (const row of (statusData.data || [])) {
          map.set(String(row.id), {
            attempts: row.attempts || 0,
            last_error: row.last_error || null,
            last_run_at: row.last_run_at || null,
            locked_until: row.locked_until || null,
            restore_status: row.restore_status || null,
            requested_at: row.requested_at || null
          });
          // The folder-server parks incomplete-upload folders with this exact
          // last_error prefix — use it to persist the red card across reloads.
          if (/^Upload incomplete/i.test(row.last_error || '')) mismatch.add(String(row.id));
        }
        setAutoSortStatusMap(map);
        setUploadMismatchIds(mismatch);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch auto-sort status:', err);
    }
    })(),
    (async () => {
    try {
      const deltaRes = await fetch(`${API_BASE_URL}/analyze/folders/image-deltas`, {
        headers
      });
      if (deltaRes.ok) {
        const deltaData = await deltaRes.json();
        const deltaMap = new Map();
        for (const row of (deltaData.data || [])) {
          deltaMap.set(String(row.id), {
            added: Array.isArray(row.added) ? row.added : [],
            deleted: Array.isArray(row.deleted) ? row.deleted : []
          });
        }
        setImageDeltaMap(deltaMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch image deltas:', err);
    }
    })(),
    (async () => {
    try {
      const medRes = await fetch(`${API_BASE_URL}/analyze/auto-check/mediums`, {
        headers
      });
      if (medRes.ok) {
        const medData = await medRes.json();
        const medMap = new Map();
        for (const row of (medData.data || [])) {
          if (row.auto_check_medium) medMap.set(String(row.id), row.auto_check_medium);
        }
        setAutoCheckMediumMap(medMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch auto-check mediums:', err);
    }
    })(),
    (async () => {
    try {
      const urduRes = await fetch(`${API_BASE_URL}/analyze/urdu1-batches`, {
        headers
      });
      if (urduRes.ok) {
        const urduData = await urduRes.json();
        const urduMap = new Map();
        for (const row of (urduData.data || [])) {
          if (!row || row.id == null || !row.gemini_batch_state) continue;
          urduMap.set(String(row.id), {
            state: row.gemini_batch_state,
            model: row.gemini_batch_model || '',
            error: row.gemini_batch_error || '',
            succeeded_count: Number(row.succeeded_count) || 0,
            failed_count: Number(row.failed_count) || 0,
            total_count: Number(row.total_count) || 0
          });
        }
        setUrdu1BatchMap(urduMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch Urdu-1 batches:', err);
    }
    })(),
    (async () => {
    try {
      const stepRes = await fetch(`${API_BASE_URL}/analyze/auto-check/steps`, {
        headers
      });
      if (stepRes.ok) {
        const stepData = await stepRes.json();
        const stepMap = new Map();
        for (const row of (stepData.data || [])) {
          stepMap.set(String(row.id), {
            by: row.auto_check_by || null,
            run_at: row.run_at || null,
            regrade_at: row.regrade_at || null,
            steps: row.steps || {}
          });
        }
        setAutoCheckStepsMap(stepMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch auto-check steps:', err);
    }
    })(),
    (async () => {
    try {
      const assessRes = await fetch(`${API_BASE_URL}/assess/snapshots/folders`, {
        headers
      });
      if (assessRes.ok) {
        const assessData = await assessRes.json();
        const metaMap = new Map();
        for (const row of (assessData.data || [])) {
          const m = String(row.folder_path || '').match(/rubrics\/(\d+)/);
          if (m) metaMap.set(m[1], { saved_at: row.saved_at || null, record_count: row.record_count || 0 });
        }
        setAssessSnapshotMetaMap(metaMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch assessment snapshot folders:', err);
    }
    })(),
    (async () => {
    try {
      const noiseRes = await fetch(`${API_BASE_URL}/assess/ocr-noise`, {
        headers
      });
      if (noiseRes.ok) {
        const noiseData = await noiseRes.json();
        const noiseMap = new Map();
        for (const row of (noiseData.data || [])) {
          if (row && row.rubric_id != null) noiseMap.set(String(row.rubric_id), row);
        }
        setOcrNoiseMap(noiseMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch OCR noise:', err);
    }
    })(),
    (async () => {
    try {
      const errRes = await fetch(`${API_BASE_URL}/assess/check-errors`, {
        headers
      });
      if (errRes.ok) {
        const errData = await errRes.json();
        const errMap = new Map();
        for (const row of (errData.data || [])) {
          if (row && row.rubric_id != null) errMap.set(String(row.rubric_id), row);
        }
        setCheckErrorMap(errMap);
      }
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to fetch check errors:', err);
    }
    })()
    ]);
  };

  // Fetch typed-answer .docx counts for URDU-medium folders from the local
  // docx-server (/docx-counts on :3100). Fail-open: if unreachable we simply
  // don't annotate the cards (and the auto-check gate proceeds anyway).
  const fetchDocxCounts = async (folders, mediumMap) => {
    const urdu = (folders || []).filter(f => mediumMap.get(String(f.id)) === 'urdu' && f.test_date);
    if (urdu.length === 0) { setDocxReadyMap(new Map()); return; }
    try {
      const DOCX_SERVER_URL = `http://${window.location.hostname}:3100`;
      const res = await fetch(`${DOCX_SERVER_URL}/docx-counts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folders: urdu.map(f => ({
            id: f.id, folder_name: f.title, class: f.class, section: f.section, subject: f.subject, test_date: f.test_date
          }))
        })
      });
      if (!res.ok) return; // fail-open — keep whatever we had
      const data = await res.json();
      const map = new Map();
      for (const r of (data.results || [])) {
        if (r && r.id != null) map.set(String(r.id), r);
      }
      setDocxReadyMap(map);
    } catch (err) {
      // Docx server down / not on this machine — leave the annotation off.
      console.warn('[AnalyzeTab] docx-counts fetch failed:', err.message);
    }
  };

  // Refresh docx counts whenever the folder list or the Urdu/English opt-ins
  // change. (Cheap on the watcher — a readdir + one `dir` per Urdu folder.)
  useEffect(() => {
    fetchDocxCounts(scansFolders, autoCheckMediumMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scansFolders, autoCheckMediumMap]);

  // Set or clear a folder's auto-check medium (the per-card Urdu/English
  // buttons). Clicking the already-selected medium clears it (auto-check
  // OFF). Optimistically updates the local map, then persists; on failure
  // the next fetchSnapshotFolderSet tick reconciles.
  const handleSetAutoCheckMedium = async (folderId, medium) => {
    const id = String(folderId);
    const current = autoCheckMediumMap.get(id) || null;
    const next = current === medium ? null : medium; // toggle off if same
    setAutoCheckMediumBusyId(id);
    // Optimistic update
    setAutoCheckMediumMap(prev => {
      const m = new Map(prev);
      if (next) m.set(id, next); else m.delete(id);
      return m;
    });
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/auto-check-medium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ medium: next })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ show: true, text: data.message || (next ? `Auto-check medium: ${next}` : 'Auto-check turned off'), type: 'success' });
      } else {
        // Roll back the optimistic change
        setAutoCheckMediumMap(prev => {
          const m = new Map(prev);
          if (current) m.set(id, current); else m.delete(id);
          return m;
        });
        setMessage({ show: true, text: data.message || 'Failed to set auto-check medium', type: 'error' });
      }
    } catch (err) {
      setAutoCheckMediumMap(prev => {
        const m = new Map(prev);
        if (current) m.set(id, current); else m.delete(id);
        return m;
      });
      setMessage({ show: true, text: 'Failed to set auto-check medium: ' + err.message, type: 'error' });
    } finally {
      setAutoCheckMediumBusyId(null);
    }
  };

  // Urdu-1 only queues local Gemini Batch OCR. It does not set the
  // English/Urdu medium and it does not grade.
  const handleUrdu1Batch = async (folderId, again) => {
    const id = String(folderId);
    const current = urdu1BatchMap.get(id);
    const inFlight = current && ['queued', 'batch_pending', 'processing_results', 'submitting', 'pending', 'running', 'collecting'].includes(current.state);
    const ready = current && (current.state === 'output_ready' || current.state === 'succeeded');
    if (inFlight || (ready && !again)) return;
    setUrdu1BusyId(id);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/urdu1-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(again ? { model: urdu1Model, again: true } : { model: urdu1Model })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage({ show: true, text: data.message || 'UrOcr batch was not submitted', type: 'error' });
      } else {
        setUrdu1BatchMap(prev => {
          const m = new Map(prev);
          m.set(id, { state: data.state || 'queued', model: data.model || '', error: '', succeeded_count: 0, failed_count: 0, total_count: 0 });
          return m;
        });
        setMessage({ show: true, text: data.message || 'UrOcr batch submitted', type: 'success' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'UrOcr batch failed: ' + err.message, type: 'error' });
    } finally {
      setUrdu1BusyId(null);
    }
  };

  // Sync global auto-sort daemon Start/Stop state from the backend. Polled
  // so another browser's toggle is reflected here without a reload.
  useEffect(() => {
    let cancelled = false;
    const fetchDaemonStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/analyze/auto-sort/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        setAutoSortEnabled(!!data.active);
        setAutoSortDaemonBusy(!!data.running);
      } catch (_) { /* ignore — button stays at last known state */ }
    };
    fetchDaemonStatus();
    const id = setInterval(fetchDaemonStatus, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [API_BASE_URL]);

  // Mirror aiStatus.sort.busy when source is auto so the status dot updates
  // without waiting for the 10s daemon-status poll.
  useEffect(() => {
    if (aiStatus?.sort?.busy && aiStatus.sort.source === 'auto') {
      setAutoSortDaemonBusy(true);
    } else if (!aiStatus?.sort?.busy) {
      setAutoSortDaemonBusy(false);
    }
  }, [aiStatus]);

  const handleToggleAutoSort = async () => {
    if (autoSortToggleBusy) return;
    setAutoSortToggleBusy(true);
    const next = !autoSortEnabled;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${API_BASE_URL}/analyze/auto-sort/${next ? 'start' : 'stop'}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage({ show: true, text: data.message || `Failed to ${next ? 'start' : 'stop'} auto-sort`, type: 'error' });
        return;
      }
      setAutoSortEnabled(!!data.active);
      setAutoSortDaemonBusy(!!data.running);
      setMessage({
        show: true,
        text: data.message || (data.active ? 'Auto-sort started' : 'Auto-sort stopped'),
        type: 'success'
      });
    } catch (err) {
      setMessage({ show: true, text: `Failed to ${next ? 'start' : 'stop'} auto-sort: ${err.message}`, type: 'error' });
    } finally {
      setAutoSortToggleBusy(false);
    }
  };

  // Examiner Start on a folder card — queues THIS folder for auto-sort.
  const handleStartAutoSort = async (folderId, folderTitle) => {
    if (startAutoSortBusyId) return;
    if (uploadMismatchIds.has(String(folderId))) {
      const err = autoSortStatusMap.get(String(folderId))?.last_error;
      setMessage({
        show: true,
        text: err
          || `Cannot start auto-sort on "${folderTitle || `folder ${folderId}`}": local folder and website image counts differ. Click Upload to sync them first.`,
        type: 'error'
      });
      return;
    }
    setStartAutoSortBusyId(String(folderId));
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/start-auto-sort`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAutoSortEnabled(true);
        setAutoSortStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            ...(prev.get(String(folderId)) || {}),
            attempts: 0,
            last_error: null,
            requested_at: new Date().toISOString()
          });
          return next;
        });
        setMessage({ show: true, text: data.message || 'Auto-sort started for this folder', type: 'success' });
      } else {
        setMessage({ show: true, text: data.message || 'Failed to start auto-sort', type: 'error' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'Failed to start auto-sort: ' + err.message, type: 'error' });
    } finally {
      setStartAutoSortBusyId(null);
    }
  };

  // Reset auto-sort attempts on a rubric so the backend daemon picks it
  // up again on the next 5-minute tick. Used by the per-card "Retry
  // auto-sort" button shown when attempts >= 3.
  const handleRetryAutoSort = async (folderId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/retry-auto-sort`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ show: true, text: data.message || 'Auto-sort retry queued', type: 'success' });
        setAutoSortStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            ...(prev.get(String(folderId)) || {}),
            attempts: 0,
            last_error: null,
            requested_at: prev.get(String(folderId))?.requested_at || new Date().toISOString()
          });
          return next;
        });
      } else {
        setMessage({ show: true, text: data.message || 'Failed to reset auto-sort', type: 'error' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'Failed to reset auto-sort: ' + err.message, type: 'error' });
    }
  };

  // Queue a Review / Reviewed folder for another auto-sort pass. The daemon
  // clears the old snapshot when it starts, re-sorts, then restores the
  // prior Review / Reviewed status on the new snapshot.
  const handleReSort = async (folderId, folderTitle) => {
    // Refuse re-sort while local and website image counts disagree — same
    // gate as Start Sorting. The red card stays until Upload syncs them.
    if (uploadMismatchIds.has(String(folderId))) {
      const err = autoSortStatusMap.get(String(folderId))?.last_error;
      setMessage({
        show: true,
        text: err
          || `Cannot re-sort "${folderTitle || `folder ${folderId}`}": local folder and website image counts differ. Click Upload to sync them first.`,
        type: 'error'
      });
      return;
    }
    const snapSt = snapshotFolderStatusMap.get(String(folderId)) || 'saved';
    const label = snapSt === 'reviewed' ? 'Reviewed' : 'Review';
    const daemonHint = autoSortEnabled
      ? ''
      : '\n\nAuto-sort is currently OFF — click Start Auto-Sort (or Start on this folder) so the daemon picks it up.';
    const ok = window.confirm(
      `Re-sort "${folderTitle || `folder ${folderId}`}"?\n\n` +
      `This queues the folder for auto-sort again. Ready to Print is cleared so it can be checked again after sorting. The current sorted list will be replaced, then the "${label}" status will be restored when sorting finishes.` +
      daemonHint
    );
    if (!ok) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/re-sort`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({
          show: true,
          text: autoSortEnabled
            ? (data.message || 'Re-sort queued')
            : `${data.message || 'Re-sort queued'} — Auto-sort is OFF; Start on this folder will turn it back on.`,
          type: 'success'
        });
        setAutoSortStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            ...(prev.get(String(folderId)) || {}),
            attempts: 0,
            last_error: null,
            restore_status: data.restore_status || snapSt,
            requested_at: prev.get(String(folderId))?.requested_at || new Date().toISOString()
          });
          return next;
        });
        // Drop Ready to Print immediately — backend cleared completed_at.
        setScansFolders(prev => prev.map(f =>
          String(f.id) === String(folderId) ? { ...f, completed_at: null } : f
        ));
        setAutoCheckStepsMap(prev => {
          const next = new Map(prev);
          next.delete(String(folderId));
          return next;
        });
      } else {
        setMessage({ show: true, text: data.message || 'Failed to queue re-sort', type: 'error' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'Failed to queue re-sort: ' + err.message, type: 'error' });
    }
  };

  // Re-arm ONE folder for auto-check after a failed / stuck run, or to re-grade
  // a Copy-to-Edupage folder. Clears the AI step boxes + claim, sets snapshot
  // back to 'reviewed', so an ai1/ai2/ai3 browser will pick it up again. Only
  // this folder is touched.
  const handleRetryAutoCheck = async (folderId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/retry-auto-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ show: true, text: data.message || 'Auto-check retry queued', type: 'success' });
        // Clear the step boxes on the card immediately.
        setAutoCheckStepsMap(prev => {
          const next = new Map(prev);
          next.delete(String(folderId));
          return next;
        });
        // Show Reviewed again (was Check Failed / stuck).
        setSnapshotFolderStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), 'reviewed');
          return next;
        });
        // Drop Ready to Print / Printed / Edupaged badges immediately —
        // backend cleared completed_at and printed_at so auto-check can
        // pick this folder up again.
        setScansFolders(prev => prev.map(f =>
          String(f.id) === String(folderId) ? { ...f, completed_at: null, printed_at: null } : f
        ));
      } else {
        setMessage({ show: true, text: data.message || 'Failed to reset auto-check', type: 'error' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'Failed to reset auto-check: ' + err.message, type: 'error' });
    }
  };

  const handleQueueRegrade = async (folderId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folderId}/queue-regrade`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ show: true, text: data.message || 'Re-grade queued', type: 'success' });
        setAutoCheckStepsMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), {
            by: null,
            run_at: null,
            regrade_at: prev.get(String(folderId))?.regrade_at || new Date().toISOString(),
            steps: {}
          });
          return next;
        });
        setSnapshotFolderStatusMap(prev => {
          const next = new Map(prev);
          next.set(String(folderId), 'reviewed');
          return next;
        });
        setScansFolders(prev => prev.map(f =>
          String(f.id) === String(folderId) ? { ...f, completed_at: null, printed_at: null } : f
        ));
      } else {
        setMessage({ show: true, text: data.message || 'Failed to queue re-grade', type: 'error' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'Failed to queue re-grade: ' + err.message, type: 'error' });
    }
  };

  // Probe whether the selected folder has a saved snapshot. Returns the
  // snapshot meta (saved_at, record_count) or null. Used to enable the
  // Import button + display "Last saved …" hint to the teacher.
  const fetchSnapshotMeta = async (path) => {
    if (!path) { setSnapshotMeta(null); return; }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/snapshots/meta?folder_path=${encodeURIComponent(path)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSnapshotMeta(data && data.meta ? data.meta : null);
      } else {
        setSnapshotMeta(null);
      }
    } catch (err) {
      console.error('[AnalyzeTab] Snapshot meta fetch failed:', err);
      setSnapshotMeta(null);
    }
  };

  // Save the current sorted list as a snapshot for the selected folder and
  // stamp it with a workflow `status`. The "Reviewed" button calls this with
  // 'reviewed' — it writes the examiner's current (possibly hand-corrected)
  // list and flips the folder's badge from "Review" → "Reviewed". Warns +
  // confirms first so the examiner doesn't accidentally overwrite the list.
  const handleSaveSnapshot = async (status = 'reviewed') => {
    if (!folderPath) {
      setMessage({ show: true, text: 'Select a folder first', type: 'error' });
      return;
    }
    if (!results || results.length === 0) {
      setMessage({ show: true, text: 'No sorted list to mark reviewed — click "Review" to load the list first', type: 'error' });
      return;
    }
    const folderTitle = selectedFolderId && scansFolders.find(f => String(f.id) === String(selectedFolderId))
      ? scansFolders.find(f => String(f.id) === String(selectedFolderId)).title
      : folderPath;
    const confirmMsg = status === 'reviewed'
      ? `Mark the sorted list for "${folderTitle}" as REVIEWED?\n\nThis saves your current ${results.length}-student list and flips the folder's status from "Review" → "Reviewed".`
      : `Save the current ${results.length}-student list to "${folderTitle}"?`;
    if (!window.confirm(confirmMsg)) return;

    setSnapshotBusy(true);
    try {
      const token = localStorage.getItem('authToken');
      const rows = results.map(r => ({
        roll_name: r.roll_name,
        image_no: r.image_no,
        unique_code: r.unique_code,
        sheet_count: r.sheet_count,
        batch_size_final: r.batch_size_final,
        model_type: r.model_type || null,
        limit_type: r.limit_type || null
      }));
      const response = await fetch(`${API_BASE_URL}/analyze/snapshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ folder_path: folderPath, rows, status })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const okMsg = status === 'reviewed'
          ? `Reviewed — saved ${rows.length} student${rows.length !== 1 ? 's' : ''} and marked "${folderTitle}" as Reviewed`
          : (data.message || `Saved ${rows.length} students to "${folderTitle}"`);
        setMessage({ show: true, text: okMsg, type: 'success' });
        await fetchSnapshotMeta(folderPath);
        await fetchSnapshotFolderSet();
      } else {
        setMessage({ show: true, text: data.message || 'Failed to save snapshot', type: 'error' });
      }
    } catch (err) {
      setMessage({ show: true, text: 'Failed to save snapshot: ' + err.message, type: 'error' });
    } finally {
      setSnapshotBusy(false);
    }
  };

  // Promote the saved snapshot to be this user's live working copy for the
  // folder. The backend wipes the user's current analyzed_papers rows for
  // this folder and inserts the snapshot rows in their place — atomic
  // transaction. After this returns, AssessmentTab's /analyze/results fetch
  // also sees the imported list, so "Pick Students" / "Start Checking" both
  // work against the imported snapshot.
  const handleImportSnapshot = async (pathArg, opts = {}) => {
    // Accept an explicit folder path — the per-card "Review" button passes one
    // so it doesn't have to wait for setFolderPath to flush through state.
    // When used as a click handler, pathArg is the event object, so we fall
    // back to the selected folderPath.
    const path = (typeof pathArg === 'string' && pathArg) ? pathArg : folderPath;
    if (!path) {
      setMessage({ show: true, text: 'Select a folder first', type: 'error' });
      return;
    }
    if (!opts.silent && !window.confirm(
      'Import the saved list?\n\nThis will REPLACE your current working list for this folder. If this folder also has a saved Check table, that will be restored into the Assessment tab too.'
    )) return;
    setSnapshotBusy(true);
    try {
      const token = localStorage.getItem('authToken');

      // Always wipe previous Sort/Check working tables (server + localStorage)
      // BEFORE importing. Otherwise Retest of folder B can still show folder
      // A's Check table when B has no assessment snapshot (404 used to skip
      // the wipe that only ran inside a successful assess import).
      await clearAllWorkingTables(API_BASE_URL, token, path);
      setResults([]);
      setSelectedImages([]);
      setSummary(null);

      const response = await fetch(`${API_BASE_URL}/analyze/snapshots/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ folder_path: path })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage({ show: true, text: data.message || 'Failed to import snapshot', type: 'error' });
        notifyWorkingTablesReloaded({ folder_path: path, analyze: false, assess: false });
        return;
      }
      viewingSavedSortRef.current = true;
      setResults(await loadSavedSort(path));
      setCalendarCollapsed(true);

      // Auto-import the matching assessment_results snapshot (if any) for
      // the same folder so the Check tab is restored in lockstep with the
      // sorted list. Missing snapshot (HTTP 404) is normal for sort-only
      // folders — previous Check rows were already wiped above.
      let assessNote = '';
      let assessImported = false;
      try {
        const assessRes = await fetch(`${API_BASE_URL}/assess/snapshots/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ folder_path: path, source: 'check_more' })
        });
        if (assessRes.ok) {
          const ad = await assessRes.json().catch(() => ({}));
          if (ad && ad.imported) {
            assessImported = true;
            assessNote = ` Also restored ${ad.imported} assessment row${ad.imported !== 1 ? 's' : ''} into the Check tab.`;
          }
        }
      } catch (assessErr) {
        console.warn('[AnalyzeTab] Assessment snapshot import skipped:', assessErr);
      }

      notifyWorkingTablesReloaded({
        folder_path: path,
        analyze: true,
        assess: assessImported
      });

      setMessage({
        show: true,
        text: (data.message || `Imported ${data.imported || 0} students`) + assessNote,
        type: 'success'
      });
    } catch (err) {
      setMessage({ show: true, text: 'Failed to import snapshot: ' + err.message, type: 'error' });
    } finally {
      setSnapshotBusy(false);
    }
  };

  const toggleFolderRubricPreview = async (e, folder) => {
    e.stopPropagation();
    const id = String(folder.id);
    const willOpen = !rubricPreviewOpenIds.has(id);
    setRubricPreviewOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (!willOpen) {
      if (String(editingRubricId) === id) {
        setEditingRubricId(null);
        setRubricEditDraft('');
        setRubricEditNote('');
      }
      return;
    }
    const cached = rubricPreviewCache[id];
    if (cached && (cached.status === 'ok' || cached.status === 'loading')) return;
    setRubricPreviewCache((prev) => ({ ...prev, [id]: { status: 'loading' } }));
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/rubrics/${folder.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Could not load rubric (${res.status})`);
      const rubric = data.rubric || {};
      let pdfText = '';
      if (rubric.issues_json) {
        try {
          const issues = typeof rubric.issues_json === 'string'
            ? JSON.parse(rubric.issues_json)
            : rubric.issues_json;
          if (issues && Array.isArray(issues.pages) && issues.pages.length > 0) {
            pdfText = rubric.content || '';
          }
        } catch (_) { /* ignore a bad issues blob */ }
      }
      setRubricPreviewCache((prev) => ({
        ...prev,
        [id]: { status: 'ok', text: rubric.content || '', pdfText }
      }));
    } catch (err) {
      setRubricPreviewCache((prev) => ({
        ...prev,
        [id]: { status: 'error', message: err.message || 'Could not load rubric' }
      }));
    }
  };

  const beginFolderRubricEdit = (e, folder, text) => {
    e.stopPropagation();
    const id = String(folder.id);
    if (editingRubricId && editingRubricId !== id) {
      const openText = (rubricPreviewCache[editingRubricId] && rubricPreviewCache[editingRubricId].text) || '';
      if (rubricEditDraft !== openText) {
        setRubricEditNote('Save or cancel the open rubric first.');
        return;
      }
    }
    setEditingRubricId(id);
    setRubricEditDraft(text || '');
    setRubricEditNote('');
  };

  const cancelFolderRubricEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingRubricId(null);
    setRubricEditDraft('');
    setRubricEditNote('');
  };

  const saveFolderRubricEdit = async (e, folder) => {
    e.stopPropagation();
    const id = String(folder.id);
    const cached = rubricPreviewCache[id] || {};
    if (rubricEditSaving || rubricEditDraft === (cached.text || '')) return;
    setRubricEditSaving(true);
    setRubricEditNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/rubrics/${folder.id}/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: rubricEditDraft })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      setRubricPreviewCache((prev) => {
        const cur = prev[id] || {};
        const pdfSame = (cur.pdfText || '').trim() === (cur.text || '').trim();
        return {
          ...prev,
          [id]: {
            ...cur,
            status: 'ok',
            text: rubricEditDraft,
            pdfText: pdfSame ? rubricEditDraft : (cur.pdfText || '')
          }
        };
      });
      setEditingRubricId(null);
      setRubricEditDraft('');
      setMessage({ show: true, text: 'Rubric saved for this folder', type: 'success' });
    } catch (err) {
      setRubricEditNote(err.message || 'Save failed');
    } finally {
      setRubricEditSaving(false);
    }
  };

  const fetchScansFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/scans/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Exclude MCQs folders — the whole Evaluate tab is for Subjective
        // tests; MCQ rubrics live under the MCQs > OMR sub-tab.
        const moreSheetOnly = (data.folders || []).filter(f => f.type !== 'mcq');
        // Sort folders by date (newest first)
        const sortedFolders = moreSheetOnly.sort((a, b) => {
          const dateA = new Date(a.test_date || a.created_at || 0);
          const dateB = new Date(b.test_date || b.created_at || 0);
          return dateB - dateA;
        });
        if (data.card_meta) {
          const ids = new Set();
          const statusMap = new Map();
          const metaMap = new Map();
          const stepMap = new Map();
          const medMap = new Map();
          for (const folder of sortedFolders) {
            const id = String(folder.id);
            if (folder.sort_snapshot) {
              ids.add(id);
              statusMap.set(id, folder.sort_snapshot.status || 'saved');
            }
            if (folder.check_snapshot) {
              metaMap.set(id, {
                saved_at: folder.check_snapshot.saved_at || null,
                record_count: folder.check_snapshot.record_count || 0
              });
            }
            if (folder.auto_check_medium) medMap.set(id, folder.auto_check_medium);
            if (folder.auto_check_steps || folder.auto_check_by || folder.auto_check_regrade_at || folder.auto_check_run_at) {
              let steps = folder.auto_check_steps || {};
              if (typeof steps === 'string') {
                try { steps = JSON.parse(steps); } catch { steps = {}; }
              }
              stepMap.set(id, {
                by: folder.auto_check_by || null,
                run_at: folder.auto_check_run_at || null,
                regrade_at: folder.auto_check_regrade_at || null,
                steps: steps && typeof steps === 'object' ? steps : {}
              });
            }
          }
          setSnapshotFolderIdSet(ids);
          setSnapshotFolderStatusMap(statusMap);
          setAssessSnapshotMetaMap(metaMap);
          setAutoCheckStepsMap(stepMap);
          setAutoCheckMediumMap(medMap);
        }
        setScansFolders(sortedFolders);
      }
    } catch (error) {
      console.error('Error fetching scans folders:', error);
    }
  };

  // ── Calendar helpers ─────────────────────────────────────────────────────
  const dateToKey = (d) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return `${nd.getFullYear()}-${String(nd.getMonth()+1).padStart(2,'0')}-${String(nd.getDate()).padStart(2,'0')}`;
  };

  // Folders narrowed by the teacher/class filter chips above the calendar.
  // The calendar, the date counts, and the folder list at the bottom all
  // read from this so a single filter change consistently updates everything.
  const visibleFolders = React.useMemo(() => {
    return (scansFolders || []).filter(f => {
      if (filterTeacher && (f.created_by || '') !== filterTeacher) return false;
      if (filterClass.length && !filterClass.some((item) => item.value === (f.class || ''))) return false;
      if (filterSubject.length && !filterSubject.some((item) => item.value === (f.subject || ''))) return false;
      if (filterSorted !== 'all') {
        const hasSnap = snapshotFolderIdSet.has(String(f.id));
        if (filterSorted === 'sorted'   && !hasSnap) return false;
        if (filterSorted === 'unsorted' &&  hasSnap) return false;
      }
      if (filterChecked !== 'all') {
        const isChecked = !!f.completed_at || !!f.printed_at;
        if (filterChecked === 'checked'   && !isChecked) return false;
        if (filterChecked === 'unchecked' &&  isChecked) return false;
      }
      if (filterErrors === 'errors') {
        const id = String(f.id);
        const listed = (map) => {
          const row = map.get(id);
          return !!(row && Array.isArray(row.students) && row.students.length);
        };
        const hasIssue = listed(unmatchedLabelMap)
          || listed(ocrNoiseMap)
          || listed(unexpectedBlankMap)
          || listed(latePaperMap)
          || listed(overMaxMarksMap)
          || listed(thinkingMarksMap)
          || listed(checkErrorMap)
          || snapshotFolderStatusMap.get(id) === 'error';
        if (!hasIssue) return false;
      }
      if (filterErrors === 'late') {
        const late = latePaperMap.get(String(f.id));
        if (!(late && Array.isArray(late.students) && late.students.length)) return false;
      }
      if (filterErrors === 'regrade') {
        const requests = (folderReviews.requests && folderReviews.requests[String(f.id)]) || [];
        if (!requests.length) return false;
      }
      if (filterImageChanges) {
        const delta = imageDeltaMap.get(String(f.id));
        const hasChange = !!(delta && ((delta.added && delta.added.length) || (delta.deleted && delta.deleted.length)));
        if (!hasChange) return false;
      }
      return true;
    });
  }, [scansFolders, filterTeacher, filterClass, filterSubject, filterSorted, filterChecked, filterErrors, filterImageChanges, imageDeltaMap, snapshotFolderIdSet, unmatchedLabelMap, ocrNoiseMap, unexpectedBlankMap, latePaperMap, overMaxMarksMap, thinkingMarksMap, checkErrorMap, snapshotFolderStatusMap, folderReviews]);

  // Count of folders dated tomorrow onwards (respecting the active filter
  // chips) — shown as a badge on the "Future Cards" button.
  const futureFolderCount = React.useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return (visibleFolders || []).filter(f => {
      if (!f.test_date) return false;
      const d = new Date(f.test_date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() >= tomorrow.getTime();
    }).length;
  }, [visibleFolders]);

  // Dropdown option lists are always sourced from the unfiltered set so
  // the user can switch between filters without one option hiding another.
  const teacherOptions = React.useMemo(() => {
    const set = new Set();
    (scansFolders || []).forEach(f => { if (f.created_by) set.add(f.created_by); });
    return [...set].sort();
  }, [scansFolders]);

  const classOptions = React.useMemo(() => {
    const set = new Set();
    (scansFolders || []).forEach(f => { if (f.class) set.add(f.class); });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }, [scansFolders]);

  const subjectOptions = React.useMemo(() => {
    const set = new Set();
    (scansFolders || []).forEach(f => { if (f.subject) set.add(f.subject); });
    return [...set].sort();
  }, [scansFolders]);

  const getDatesWithFolders = () => {
    const s = new Set();
    visibleFolders.forEach(f => { if (f.test_date) s.add(dateToKey(f.test_date)); });
    return s;
  };

  const getFolderCountForDate = (date) => {
    const key = dateToKey(date);
    return visibleFolders.filter(f => f.test_date && dateToKey(f.test_date) === key).length;
  };

  // Rolling 30-day window — today − 29 … today, or (in Future Cards mode) the
  // next 30 days tomorrow … tomorrow+29. The `year`/`month` args are ignored
  // (kept for call-site compatibility) since the strip is anchored to today.
  const CALENDAR_WINDOW_DAYS = 30;
  // eslint-disable-next-line no-unused-vars
  const getCalendarDays = (_year, _month) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Future Cards (or a selected future date) flips the strip to the NEXT 15
    // days so upcoming folders become pickable.
    const selMidnight = selectedCalendarDate ? new Date(selectedCalendarDate).setHours(0, 0, 0, 0) : null;
    const futureWindow = showFuture || (selMidnight != null && selMidnight > today.getTime());
    const days = [];
    if (futureWindow) {
      for (let i = 1; i <= CALENDAR_WINDOW_DAYS; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        days.push({ date: d, current: true });
      }
    } else {
      for (let i = CALENDAR_WINDOW_DAYS - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        days.push({ date: d, current: true });
      }
    }
    return days;
  };

  const clearDateRange = () => {
    setSelectedCalendarDate(null);
    setRangeEndDate(null);
    setShowAllFolders(false);
  };

  // First click sets the start day. Second click sets the end day (order
  // does not matter). A click after a finished range starts a new one.
  const handleDateClick = (date) => {
    setGradeFilter(null);
    setSectionFilter(null);
    setSubjectPick(null);
    setShowAllFolders(false);
    const clicked = new Date(date);
    clicked.setHours(0, 0, 0, 0);
    if (!selectedCalendarDate || rangeEndDate) {
      setSelectedCalendarDate(clicked);
      setRangeEndDate(null);
    } else {
      const start = new Date(selectedCalendarDate);
      start.setHours(0, 0, 0, 0);
      if (clicked.getTime() === start.getTime()) {
        setRangeEndDate(null);
      } else if (clicked < start) {
        setRangeEndDate(start);
        setSelectedCalendarDate(clicked);
      } else {
        setRangeEndDate(clicked);
      }
    }
    setSelectedFolderId('');
    setFolderPath('');
  };

  const handleFolderClickFromCalendar = (folder) => {
    setSelectedFolderId(String(folder.id));
    setFolderPath(`rubrics/${folder.id}`);
  };

  // Hand this folder card to the Login Edupage extension to auto-login.
  // We send the card's rendered innerText (title on line 0, assignment on
  // line 1, creator email within it) — exactly what the extension's manual
  // picker reads — so the extension's matching/course/assignment logic is
  // unchanged. See the Login Edupage bridge useEffect above.
  const loginToEduPageForCard = (e, folder) => {
    e.stopPropagation(); // don't also trigger the card's folder-select click
    const cardEl = e.currentTarget.closest('[data-edu-card]');
    const text = cardEl ? cardEl.innerText : '';
    if (!text.trim()) return;
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_LOGIN_CARD', text }, '*');
    showMessage(
      eduBridgeReady
        ? `Logging into EduPage for ${folder.created_by || 'this folder'}…`
        : 'Sent to Login Edupage — if nothing opens, install/update the extension and reload.',
      'info'
    );
  };

  // Shared result of a per-folder upload: flip the red mismatch tag and
  // show the same counts the Upload button has always shown.
  const finishFolderUpload = (folder, data) => {
    const id = folder.id;
    const title = folderDisplayTitle(folder);
    setUploadMismatchIds((prev) => {
      const n = new Set(prev);
      if (data.blocked) n.add(String(id)); else n.delete(String(id));
      return n;
    });
    if (data.blocked) {
      const bits = [];
      if (data.failed)   bits.push(`${data.failed} failed to upload`);
      if (data.badNames) bits.push(`${data.badNames} have bad names (must be "Image_<number>.jpg")`);
      if (data.missing)  bits.push(`${data.missing} still missing`);
      showMessage(
        `⚠️ ${title}: local folder has ${data.localImages} image${data.localImages === 1 ? '' : 's'} but the website has ${data.backendCount}`
        + (bits.length ? ` — ${bits.join(', ')}` : '')
        + `. Sorting and checking are PAUSED for this folder until they match — click Upload again to retry (rename/remove any non "Image_<n>.jpg" files).`,
        'error'
      );
    } else {
      showMessage(
        data.uploaded > 0
          ? `Uploaded ${data.uploaded} image${data.uploaded === 1 ? '' : 's'} for ${title}. Local & website match (${data.backendCount != null ? data.backendCount : data.total}).`
          : `Nothing new to upload for ${title} — all ${data.total || 0} image${data.total === 1 ? '' : 's'} already on the server.`,
        'success'
      );
      setUploadedFolderIds((prev) => new Set(prev).add(id));
    }
    fetchScansFolders();
  };

  // Upload THIS folder's local images to the backend, on demand. Asks the
  // local folder-server (port 3099) to scan just this folder on disk and
  // upload any images that aren't already on the server — nothing else is
  // touched. Lets the user push one folder's scans up instantly instead of
  // waiting for (or relying on) the background watcher.
  const uploadFolderImages = async (e, folder) => {
    e.stopPropagation(); // don't also trigger the card's folder-select click
    const id = folder.id;
    if (uploadingFolderIds.has(id)) return; // already in flight
    setUploadingFolderIds((prev) => new Set(prev).add(id));
    showMessage(`Uploading images for ${folderDisplayTitle(folder)}…`, 'info');
    try {
      const LOCAL_SERVER_URL = `http://${window.location.hostname}:3099`;
      const res = await fetch(`${LOCAL_SERVER_URL}/watcher/upload-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: folder.id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Upload failed (HTTP ${res.status})`);
      }
      finishFolderUpload(folder, data);
    } catch (err) {
      showMessage(
        `Upload failed: ${err.message}. Is the local folder-server running on port 3099?`,
        'error'
      );
    } finally {
      setUploadingFolderIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  // Red "LOCAL ≠ WEB" tag. The website backend starts the folder-server on
  // this PC when port 3099 is down, then uploads this folder. When the
  // folder-server is already running, only the upload happens.
  const uploadFromMismatchTag = async (e, folder) => {
    e.stopPropagation();
    const id = folder.id;
    if (uploadingFolderIds.has(id)) return;
    setUploadingFolderIds((prev) => new Set(prev).add(id));
    showMessage(`Uploading images for ${folderDisplayTitle(folder)}…`, 'info');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${id}/local-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `Upload failed (HTTP ${res.status})`);
      }
      finishFolderUpload(folder, data);
    } catch (err) {
      showMessage(`Upload failed: ${err.message}`, 'error');
    } finally {
      setUploadingFolderIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  // Shown only while LOCAL ≠ WEB is on. Deletes website images that are not
  // in this folder on disk. The local folder is not changed. An empty local
  // folder is refused by the folder-server.
  const removeWebsiteExtras = async (e, folder) => {
    e.stopPropagation();
    const id = String(folder.id);
    if (removingExtrasIds.has(id)) return;
    const title = folderDisplayTitle(folder);
    if (!window.confirm(
      `Remove extra images from the website for "${title}"?\n\nOnly images that are on the website and not in the local folder are deleted. The local folder stays as it is.`
    )) return;
    setRemovingExtrasIds((prev) => new Set(prev).add(id));
    showMessage(`Removing extra website images for ${title}…`, 'info');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folder.id}/remove-website-extras`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || `Remove extras failed (HTTP ${res.status})`);
      }
      setUploadMismatchIds((prev) => {
        const n = new Set(prev);
        if (data.blocked) n.add(id); else n.delete(id);
        return n;
      });
      if (!data.blocked) {
        setAutoSortStatusMap((prev) => {
          const next = new Map(prev);
          const cur = next.get(id);
          if (cur) next.set(id, { ...cur, last_error: null, locked_until: null });
          return next;
        });
      }
      const removed = Number(data.removed) || 0;
      showMessage(
        removed > 0
          ? `Removed ${removed} extra image${removed === 1 ? '' : 's'} from the website for ${title}. Local ${data.localImages}, website ${data.backendCount}.`
          : (data.message || `No extra website images for ${title}.`),
        data.blocked ? 'error' : 'success'
      );
      fetchScansFolders();
    } catch (err) {
      showMessage(`Could not remove extras: ${err.message}`, 'error');
    } finally {
      setRemovingExtrasIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  // Per-card "Review" button — selects the folder and loads its saved sorted
  // list into the Analysis Results table, exactly like the "Review" button in
  // the results action bar (handleImportSnapshot). Only shown for folders that
  // actually have a saved snapshot.
  const reviewFolderFromCard = (e, folder) => {
    e.stopPropagation(); // don't double-fire the card's folder-select click
    const path = `rubrics/${folder.id}`;
    setSelectedFolderId(String(folder.id));
    setFolderPath(path);
    handleImportSnapshot(path, { silent: true }); // explicit path + no confirm for the per-card Review
  };

  // Per-card ReCheck — open Check more sheets with this folder's saved
  // assessment rows and its rubric, ready to OCR or recheck. Does not start
  // either action.
  const recheckFolderFromCard = (e, folder) => {
    e.stopPropagation();
    navigate('/evaluate/check', {
      state: {
        autoOpenFolder: {
          folder_path: `rubrics/${folder.id}`,
          title: folderDisplayTitle(folder),
          class: folder.class || '',
          subject: folder.subject || '',
          intent: 'recheck'
        }
      }
    });
  };

  // Re-Grade chip: open Check on the first request. The dropdown there
  // holds the rest. The first request's students and questions are ticked.
  const openRegradeFromCard = (e, folder) => {
    e.stopPropagation();
    const requests = ((folderReviews.requests && folderReviews.requests[String(folder.id)]) || [])
      .slice()
      .sort((a, b) => Number(a.slot) - Number(b.slot));
    if (!requests.length) return;
    const queue = requests.map((request) => ({
      slot: request.slot,
      students: request.students || [],
      questions: request.questions || [],
      note: request.note || '',
      createdBy: request.created_by || '',
      audioUrl: ''
    }));
    setRegradeHoverId(null);
    navigate('/evaluate/check', {
      state: {
        autoOpenFolder: {
          folder_path: `rubrics/${folder.id}`,
          title: folderDisplayTitle(folder),
          class: folder.class || '',
          subject: folder.subject || '',
          intent: 'regrade-request',
          regradeRequest: queue[0],
          regradeQueue: queue
        }
      }
    });
  };

  // Get target date based on offset
  const getTargetDate = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + currentDateOffset);
    return date;
  };

  // Format folder display label
  const formatFolderLabel = (folder) => {
    const dateStr = new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return `${folderDisplayTitle(folder)} (${folder.scan_count} PDFs) ${dateStr}`.replace(' - ', ' ');
  };

  // Folders for the chosen day, range, Future Cards, or All. Opening the
  // tab leaves this empty so only the calendar is on screen.
  const getFilteredFolders = () => {
    if (!visibleFolders || visibleFolders.length === 0) return [];
    const byWord = (list) => (assignmentWord
      ? list.filter((folder) => itemMatchesAssignmentWord(folder, assignmentWord))
      : list);
    if (gradeFilter) {
      return byWord(visibleFolders
        .filter((folder) => itemMatchesClass(folder, gradeFilter, sectionFilter, subjectPick)))
        .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' }));
    }
    if (!showFuture && !selectedCalendarDate && !showAllFolders) return [];
    // "Future Cards": everything dated tomorrow (00:00) or later, across all
    // dates — takes precedence over the single-date calendar filter.
    if (showFuture) {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const future = visibleFolders.filter(f => {
        if (!f.test_date) return false;
        const d = new Date(f.test_date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() >= tomorrow.getTime();
      });
      // Soonest date first, then by title within a date.
      return byWord(future).sort((a, b) => {
        const byDate = new Date(a.test_date) - new Date(b.test_date);
        return byDate !== 0 ? byDate : (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    const startKey = selectedCalendarDate ? dateToKey(selectedCalendarDate) : null;
    const endKey = rangeEndDate ? dateToKey(rangeEndDate) : startKey;
    const base = startKey
      ? visibleFolders.filter(f => {
          if (!f.test_date) return false;
          const key = dateToKey(f.test_date);
          return key >= startKey && key <= endKey;
        })
      : visibleFolders;
    return byWord([...base]).sort((a, b) => {
      const byDate = startKey ? (new Date(a.test_date) - new Date(b.test_date)) : 0;
      return byDate !== 0
        ? byDate
        : (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Check if there are folders available for previous/next dates
  const hasPreviousDate = () => {
    if (!scansFolders || scansFolders.length === 0) return false;
    const currentTime = getTargetDate().getTime();
    return scansFolders.some(f => {
      const folderDate = new Date(f.test_date);
      folderDate.setHours(0, 0, 0, 0);
      return folderDate.getTime() < currentTime;
    });
  };

  const hasNextDate = () => {
    if (!scansFolders || scansFolders.length === 0) return false;
    const currentTime = getTargetDate().getTime();
    return scansFolders.some(f => {
      const folderDate = new Date(f.test_date);
      folderDate.setHours(0, 0, 0, 0);
      return folderDate.getTime() > currentTime;
    });
  };

  // Navigate to previous date with folders
  const goToPreviousDate = () => {
    if (!scansFolders || scansFolders.length === 0) return;
    const currentTime = getTargetDate().getTime();
    
    const previousFolders = scansFolders
      .filter(f => {
        const folderDate = new Date(f.test_date);
        folderDate.setHours(0, 0, 0, 0);
        return folderDate.getTime() < currentTime;
      })
      .sort((a, b) => new Date(b.test_date) - new Date(a.test_date));
    
    if (previousFolders.length > 0) {
      const targetFolder = previousFolders[0];
      const targetDate = new Date(targetFolder.test_date);
      targetDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const diffTime = targetDate.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
      setCurrentDateOffset(diffDays);
      setSelectedFolderId('');
    }
  };

  // Navigate to next date with folders
  const goToNextDate = () => {
    if (!scansFolders || scansFolders.length === 0) return;
    const currentTime = getTargetDate().getTime();
    
    const nextFolders = scansFolders
      .filter(f => {
        const folderDate = new Date(f.test_date);
        folderDate.setHours(0, 0, 0, 0);
        return folderDate.getTime() > currentTime;
      })
      .sort((a, b) => new Date(a.test_date) - new Date(b.test_date));
    
    if (nextFolders.length > 0) {
      const targetFolder = nextFolders[0];
      const targetDate = new Date(targetFolder.test_date);
      targetDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const diffTime = targetDate.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
      setCurrentDateOffset(diffDays);
      setSelectedFolderId('');
    }
  };

  const fetchResults = async (pathOverride) => {
    const path = typeof pathOverride === 'string' ? pathOverride : folderPath;
    if (!path) {
      viewingSavedSortRef.current = false;
      setResults([]);
      return [];
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(path)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const rows = data.data || [];
        viewingSavedSortRef.current = false;
        setResults(rows);
        return rows;
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
    return [];
  };

  // The saved sort is one list per folder. Every account reads this, not the
  // rows that account happened to sort itself.
  const loadSavedSort = async (path) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(
      `${API_BASE_URL}/analyze/snapshots?folder_path=${encodeURIComponent(path)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = response.ok ? await response.json().catch(() => ({})) : {};
    const rows = Array.isArray(data.data) ? data.data : [];
    return rows.map(r => ({ ...r, created_at: r.created_at || r.saved_at || null }));
  };

  const rowWriteUrl = (id, { withFolder = false } = {}) => {
    if (viewingSavedSortRef.current) {
      return `${API_BASE_URL}/analyze/snapshots/rows/${id}`;
    }
    if (withFolder && folderPath) {
      return `${API_BASE_URL}/analyze/results/${id}?folder_path=${encodeURIComponent(folderPath)}`;
    }
    return `${API_BASE_URL}/analyze/results/${id}`;
  };

  const refreshOpenList = async () => {
    if (viewingSavedSortRef.current && folderPath) {
      const rows = await loadSavedSort(folderPath);
      setResults(rows);
      return rows;
    }
    return fetchResults();
  };

  // Opening a folder shows that folder's latest saved sort. A sort that was
  // run and not saved stays off this screen.
  useEffect(() => {
    let cancelled = false;
    const loadFolderRows = async () => {
      if (!folderPath) {
        viewingSavedSortRef.current = false;
        setResults([]);
        setSelectedImages([]);
        return;
      }
      setResults([]);
      setSelectedImages([]);
      try {
        const rows = await loadSavedSort(folderPath);
        if (cancelled) return;
        viewingSavedSortRef.current = true;
        setResults(rows);
      } catch (error) {
        console.error('Error fetching saved sort:', error);
      }
    };
    loadFolderRows();
    return () => { cancelled = true; };
  // loadSavedSort is recreated each render; the folder path is the trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath]);

  const handleFolderChange = (e) => {
    const folderId = e.target.value;
    setSelectedFolderId(folderId);
    
    if (folderId) {
      const folder = scansFolders.find(f => f.id === parseInt(folderId));
      if (folder) {
        // Build S3 folder path (bucket prefix)
        // Format: rubrics/{rubricId}/
        const folderPath = `rubrics/${folder.id}`;
        setFolderPath(folderPath);
      }
    } else {
      setFolderPath('');
    }
  };

  // Format date for display
  const formatDisplayDate = () => {
    const date = getTargetDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) {
      return 'Today';
    } else if (date.getTime() === today.getTime() - 86400000) {
      return 'Yesterday';
    } else if (date.getTime() === today.getTime() + 86400000) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Check for missing image numbers in a folder
  const checkForMissingImages = async () => {
    if (!selectedFolderId) return null;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/scans/${selectedFolderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return null;
      const data = await response.json();
      const scans = data.scans || [];
      if (scans.length === 0) return null;

      // Extract numbers from filenames like "Image_25576.jpg" or "image_001.jpg"
      const numbers = scans
        .map(s => {
          const match = (s.file_name || '').match(/(\d+)/g);
          return match ? parseInt(match[match.length - 1]) : null;
        })
        .filter(n => n !== null)
        .sort((a, b) => a - b);

      if (numbers.length < 2) return null;

      // Find gaps
      const missing = [];
      for (let i = 1; i < numbers.length; i++) {
        for (let n = numbers[i - 1] + 1; n < numbers[i]; n++) {
          missing.push(n);
        }
      }

      if (missing.length === 0) return null;

      return {
        missing,
        total: scans.length,
        rangeStart: numbers[0],
        rangeEnd: numbers[numbers.length - 1]
      };
    } catch (err) {
      console.error('Error checking image gaps:', err);
      return null;
    }
  };

  // Inspect the selected folder's scan list. Returns:
  //   { total: <int>, duplicates: [{ name, count }, ...] }
  // Used for two pre-flight blocks:
  //   - total === 0           → can't sort, no images
  //   - duplicates.length > 0 → can't sort, would corrupt OCR/grading
  const inspectScansForFolder = async () => {
    if (!selectedFolderId) return { total: 0, duplicates: [] };
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/scans/${selectedFolderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return { total: 0, duplicates: [] };
      const data = await response.json();
      const scans = data.scans || [];
      const counts = new Map();
      for (const s of scans) {
        const name = String(s.file_name || '').trim();
        if (!name) continue;
        counts.set(name, (counts.get(name) || 0) + 1);
      }
      const duplicates = [];
      for (const [name, count] of counts) {
        if (count > 1) duplicates.push({ name, count });
      }
      return { total: scans.length, duplicates };
    } catch (err) {
      console.error('Error inspecting scans folder:', err);
      return { total: 0, duplicates: [] };
    }
  };

  // ── Get PDF — import online students' handwritten PDFs as sorted batches ──
  // For folders whose students submitted PDFs (e.g. "(A01)Saher Riaz.pdf") to a
  // local folder, this REPLACES Start Sorting: identity is already in each
  // filename, so there's nothing for the AI to read. We resolve the same local
  // folder /get-text uses, fetch each PDF, rasterize its pages in the browser,
  // upload them as normal Image_<n>.jpg scans, then register one batch per
  // student (all pages, tagged model_type='pdf'). After this the folder is a
  // normal sorted list → Review → Check (vision OCR→grade) → Approve.
  const handleGetPdf = async () => {
    if (isAnalyzing || gettingPdf) return;
    if (!selectedFolderId) { showMessage('Select a folder first', 'error'); return; }
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    if (!folder) { showMessage('Selected folder not found.', 'error'); return; }
    if (!folder.test_date) { showMessage('This folder has no test date — cannot locate its PDF folder.', 'error'); return; }
    if (!folder.subject) { showMessage('This folder is missing a subject — cannot resolve the local PDF folder.', 'error'); return; }

    const fp = folderPath || `rubrics/${selectedFolderId}`;
    const LOADER_SERVER_URL = `http://${window.location.hostname}:3101`;
    const token = localStorage.getItem('authToken');

    setGettingPdf(true);
    setProgress('Asking local server for PDFs…');
    try {
      // 1) Resolve the local folder + list its PDFs.
      const listResp = await fetch(`${LOADER_SERVER_URL}/get-pdfs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          class: folder.class || '',
          section: folder.section || '',
          subject: folder.subject || '',
          test_date: folder.test_date,
          folder_name: folder.title || ''
        })
      });
      const listData = await listResp.json().catch(() => ({}));
      if (!listResp.ok || !listData.success) {
        const detail = (listData && listData.error) ? listData.error : `HTTP ${listResp.status}`;
        const avail = (listData && Array.isArray(listData.available) && listData.available.length)
          ? ` Folders present that day: ${listData.available.slice(0, 10).join(', ')}${listData.available.length > 10 ? ', …' : ''}` : '';
        throw new Error(`${detail}.${avail} Is the loader-server running on port 3101? (npm run loader-server)`);
      }
      const pdfFiles = Array.isArray(listData.files) ? listData.files : [];
      if (pdfFiles.length === 0) {
        showMessage(`No PDF files found in ${listData.folder || 'the day folder'}${listData.base_path ? ` (${listData.base_path})` : ''}.`, 'error');
        return;
      }
      const folderAbsPath = listData.base_path; // for /get-pdf-file

      // 2) Per PDF: parse roll/name, rasterize pages, upload each as a scan.
      const students = [];
      const skipped = [];
      // Pure-digit, monotonically increasing image ids so every page gets a
      // unique Image_<n>.jpg name the backend's strict name policy accepts.
      let imgSeq = Date.now();
      for (let i = 0; i < pdfFiles.length; i++) {
        const f = pdfFiles[i];
        setProgress(`Importing PDF ${i + 1}/${pdfFiles.length}: ${f.name}…`);
        // "(A01)Saher Riaz.pdf" / "(A01)Saher_Riaz.pdf" → code A01, name "Saher Riaz"
        const m = String(f.name).match(/^\s*\(([^)]+)\)\s*(.*?)\s*\.pdf$/i);
        if (!m) { skipped.push(`${f.name} (no "(roll)" prefix)`); continue; }
        const uniqueCode = m[1].trim().toUpperCase();
        const namePart = (m[2] || '').replace(/[_-]+/g, ' ').trim();
        const rollName = `(${uniqueCode})${namePart}`;

        let buf;
        try {
          const fileResp = await fetch(`${LOADER_SERVER_URL}/get-pdf-file?path=${encodeURIComponent(folderAbsPath)}&name=${encodeURIComponent(f.name)}`);
          if (!fileResp.ok) throw new Error(`HTTP ${fileResp.status}`);
          buf = await fileResp.arrayBuffer();
        } catch (e) { skipped.push(`${f.name} (read failed: ${e.message})`); continue; }

        let blobs;
        try { blobs = await renderPdfToJpegBlobs(buf); }
        catch (e) { skipped.push(`${f.name} (render failed: ${e.message})`); continue; }
        if (!blobs.length) { skipped.push(`${f.name} (0 pages)`); continue; }

        const imageNames = [];
        for (let p = 0; p < blobs.length; p++) {
          const imageName = `Image_${imgSeq++}.jpg`;
          try {
            const fd = new FormData();
            fd.append('file', blobs[p], imageName);
            const up = await fetch(`${API_BASE_URL}/scans/${selectedFolderId}/upload`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
            });
            const upData = await up.json().catch(() => ({}));
            if (up.ok && upData.scan && upData.scan.file_name) imageNames.push(upData.scan.file_name);
          } catch (_) { /* one page failed — keep the rest */ }
        }
        if (imageNames.length > 0) students.push({ unique_code: uniqueCode, roll_name: rollName, image_names: imageNames });
        else skipped.push(`${f.name} (no pages uploaded)`);
      }

      if (students.length === 0) {
        throw new Error(`Could not import any students.${skipped.length ? ' Issues: ' + skipped.slice(0, 5).join('; ') : ''}`);
      }

      // 3) Register the batches (replaces this user's rows for the folder).
      setProgress(`Registering ${students.length} student batch(es)…`);
      const batchResp = await fetch(`${API_BASE_URL}/analyze/pdf-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder_path: fp, students })
      });
      const batchData = await batchResp.json().catch(() => ({}));
      if (!batchResp.ok || !batchData.success) {
        throw new Error((batchData && batchData.message) || `Batch registration failed (HTTP ${batchResp.status})`);
      }

      await fetchResults();
      const msg = `Imported ${students.length} student(s) from PDF (${batchData.pages} page${batchData.pages !== 1 ? 's' : ''}).`
        + (skipped.length ? ` Skipped ${skipped.length}: ${skipped.slice(0, 5).join('; ')}${skipped.length > 5 ? ', …' : ''}` : '');
      showMessage(msg, skipped.length ? 'info' : 'success');
      setProgress('');
    } catch (err) {
      showMessage('Get PDF failed: ' + (err && err.message ? err.message : String(err)), 'error');
      setProgress('');
    } finally {
      setGettingPdf(false);
    }
  };

  // ── Get Sorting — import from ocr_results.json (odd pages only) ──
  // Fresh folders: wipe Sort table, then import the full list.
  // Already reviewed / checked / EduPage folders: sync against ocr_results —
  //   • DROP sorted rows whose image_no is no longer in ocr_results, then
  //   • recount late rolls that are still stored as sheet 1 / batch 2 on
  //     every front, then
  //   • APPEND only brand-new front pages. A roll that gained a page is
  //     recounted again so its sheet count and batch cover every front.
  // Existing rows that still match ocr_results stay untouched.
  // OCR names are auto-corrected against the imported student list when the
  // roll matches and the name is ≥80% similar (e.g. "Muhamnad Ale" → "Muhammad Ali").

  // Correct an OCR `(roll)name` against the imported student list when the
  // roll matches and the name is ≥80% similar. Returns { roll_name, unique_code, corrected }.
  const correctOcrNameAgainstImported = (rollName, uniqueCode, expectedByRoll) => {
    const fromName = String(rollName || '').match(/^\s*\(([^)]+)\)/);
    const roll = String(uniqueCode || (fromName && fromName[1]) || '').trim().toUpperCase();
    if (!roll || roll === '000' || roll.startsWith('000-') || !expectedByRoll || expectedByRoll.size === 0) {
      return { roll_name: rollName, unique_code: uniqueCode, corrected: false };
    }
    const expectedFull = expectedByRoll.get(roll);
    if (!expectedFull) {
      return { roll_name: rollName, unique_code: uniqueCode, corrected: false };
    }
    const ocrName = String(rollName || '').replace(/^\s*\([^)]*\)\s*/, '').trim();
    const expectedName = String(expectedFull).replace(/^\s*\([^)]*\)\s*/, '').trim();
    if (!ocrName || !expectedName) {
      return { roll_name: rollName, unique_code: uniqueCode, corrected: false };
    }
    if (String(rollName || '').trim() === expectedFull) {
      return { roll_name: expectedFull, unique_code: roll, corrected: false };
    }
    // Local Levenshtein similarity (0–1) so this can run before the shared helpers.
    const s1 = ocrName.toLowerCase();
    const s2 = expectedName.toLowerCase();
    let similarity = 1;
    if (s1 !== s2) {
      const longer = s1.length > s2.length ? s1 : s2;
      const costs = [];
      for (let i = 0; i <= s1.length; i++) {
        let lastValue = i;
        for (let j = 0; j <= s2.length; j++) {
          if (i === 0) {
            costs[j] = j;
          } else if (j > 0) {
            let newValue = costs[j - 1];
            if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
              newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
            }
            costs[j - 1] = lastValue;
            lastValue = newValue;
          }
        }
        if (i > 0) costs[s2.length] = lastValue;
      }
      const dist = costs[s2.length];
      similarity = longer.length === 0 ? 1 : (longer.length - dist) / longer.length;
    }
    if (similarity >= 0.80) {
      return { roll_name: expectedFull, unique_code: roll, corrected: true };
    }
    return { roll_name: rollName, unique_code: uniqueCode, corrected: false };
  };

  // Always fetch the Students-tab list for this folder (don't rely on React
  // state — Get Sorting can run before importedStudents has finished loading).
  const fetchExpectedByRollForFolder = async (folder, token) => {
    const expectedByRoll = new Map();
    const folderClass = (folder.class || '').trim();
    const folderSubject = (folder.subject || '').trim();
    if (!folderClass || !folderSubject) return expectedByRoll;
    try {
      const listsRes = await fetch(`${API_BASE_URL}/students/lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!listsRes.ok) return expectedByRoll;
      const allLists = await listsRes.json().catch(() => []);
      const matchedLists = (Array.isArray(allLists) ? allLists : []).filter(l =>
        (l.class || '').trim().toLowerCase() === folderClass.toLowerCase() &&
        (l.subject || '').trim().toLowerCase() === folderSubject.toLowerCase()
      );
      if (matchedLists.length === 0) return expectedByRoll;

      const studentResponses = await Promise.all(matchedLists.map(l =>
        fetch(
          `${API_BASE_URL}/students?class=${encodeURIComponent(l.class)}&section=${encodeURIComponent(l.section || '')}&subject=${encodeURIComponent(l.subject)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(r => r.ok ? r.json() : []).catch(() => [])
      ));
      for (const arr of studentResponses) {
        for (const s of (arr || [])) {
          const full = String(s.student_name || '').trim();
          if (!full) continue;
          const m = full.match(/^\s*\(([^)]+)\)/);
          if (!m) continue;
          const code = m[1].trim().toUpperCase();
          if (code && !expectedByRoll.has(code)) expectedByRoll.set(code, full);
        }
      }
    } catch (err) {
      console.warn('[AnalyzeTab] fetchExpectedByRollForFolder failed:', err);
    }
    return expectedByRoll;
  };

  // After Get Sorting loads rows, fix any still-mismatched names on the live
  // table (same roll + ≥80% name match → imported list spelling).
  const autoCorrectLiveResultsAgainstImported = async (rows, token, folderPathForUpdate, expectedByRollArg = null) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return { corrected: 0, rows };
    }
    let expectedByRoll = expectedByRollArg;
    if (!expectedByRoll || expectedByRoll.size === 0) {
      expectedByRoll = new Map();
      for (const stu of (importedStudents || [])) {
        const full = String(stu.student_name || '').trim();
        if (!full) continue;
        const m = full.match(/^\s*\(([^)]+)\)/);
        if (!m) continue;
        const roll = m[1].trim().toUpperCase();
        if (roll && !expectedByRoll.has(roll)) expectedByRoll.set(roll, full);
      }
    }
    if (expectedByRoll.size === 0) return { corrected: 0, rows };

    let corrected = 0;
    const next = [];
    for (const r of rows) {
      const fix = correctOcrNameAgainstImported(r.roll_name, r.unique_code, expectedByRoll);
      if (!fix.corrected || !r.id) {
        next.push(r);
        continue;
      }
      try {
        const resp = await fetch(`${API_BASE_URL}/analyze/results/${r.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            roll_name: fix.roll_name,
            unique_code: fix.unique_code,
            folder_path: folderPathForUpdate || r.folder_path || folderPath
          })
        });
        if (resp.ok) {
          corrected += 1;
          next.push({ ...r, roll_name: fix.roll_name, unique_code: fix.unique_code });
        } else {
          next.push(r);
        }
      } catch (_) {
        next.push(r);
      }
    }
    if (corrected > 0) setResults(next);
    return { corrected, rows: next };
  };

  // Existing rows that still match ocr_results stay untouched.
  const handleLoadOcrClick = () => {
    if (isAnalyzing || gettingPdf || gettingOcr) return;
    if (!selectedFolderId) { showMessage('Select a folder first', 'error'); return; }
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    if (!folder) { showMessage('Selected folder not found.', 'error'); return; }
    if (!folder.test_date) { showMessage('This folder has no test date — cannot locate its OCR file.', 'error'); return; }
    if (!folder.class || !folder.subject) {
      showMessage('Selected folder is missing class or subject.', 'error');
      return;
    }

    const snapStatus = (snapshotMeta && snapshotMeta.status)
      || snapshotFolderStatusMap.get(String(selectedFolderId))
      || null;
    const hasExistingList = !!snapshotMeta
      || ['saved', 'reviewed', 'auto-checked', 'copy_to_edupage', 'completed'].includes(snapStatus);

    setOcrSortDialog({
      folderTitle: folder.title || folderPath || 'this folder',
      hasExistingList
    });
  };

  const runLoadOcr = async ({ overwrite = false } = {}) => {
    if (isAnalyzing || gettingPdf || gettingOcr) return;
    if (!selectedFolderId) { showMessage('Select a folder first', 'error'); return; }
    const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
    if (!folder) { showMessage('Selected folder not found.', 'error'); return; }
    if (!folder.test_date) { showMessage('This folder has no test date — cannot locate its OCR file.', 'error'); return; }
    if (!folder.class || !folder.subject) {
      showMessage('Selected folder is missing class or subject.', 'error');
      return;
    }

    const fp = folderPath || `rubrics/${selectedFolderId}`;
    const LOADER_SERVER_URL = `http://${window.location.hostname}:3101`;
    const token = localStorage.getItem('authToken');
    const snapStatus = (snapshotMeta && snapshotMeta.status)
      || snapshotFolderStatusMap.get(String(selectedFolderId))
      || null;
    const isIncremental = !overwrite && (
      !!snapshotMeta
      || ['saved', 'reviewed', 'auto-checked', 'copy_to_edupage', 'completed'].includes(snapStatus)
    );

    setGettingOcr(true);
    setProgress(isIncremental
      ? 'Checking existing sorted images…'
      : 'Clearing previous sorted table…');
    try {
      // Already-processed folders: load saved sorted rows so we can prune
      // stale image_nos and append only new ones.
      const existingImageNos = new Set();
      let snapRows = [];
      if (isIncremental) {
        setProgress('Loading existing sorted list…');
        const snapResp = await fetch(
          `${API_BASE_URL}/analyze/snapshots?folder_path=${encodeURIComponent(fp)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const snapData = await snapResp.json().catch(() => ({}));
        if (!snapResp.ok || !snapData.success) {
          throw new Error((snapData && snapData.message) || 'Failed to load existing sorted list');
        }
        snapRows = Array.isArray(snapData.data) ? snapData.data : [];
        for (const row of snapRows) {
          const raw = String((row && row.image_no) || '').trim();
          if (!raw) continue;
          for (const part of raw.split(',')) {
            const name = part.trim().toLowerCase();
            if (name) existingImageNos.add(name);
          }
        }
      } else {
        // Fresh sort of THIS folder only. Other folders keep their own rows.
        const clearAnalyzeResponse = await fetch(
          `${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(fp)}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
        if (!clearAnalyzeResponse.ok) {
          const clearData = await clearAnalyzeResponse.json().catch(() => ({}));
          throw new Error(clearData.message || 'Failed to clear previous Sort data');
        }
        setResults([]);
        setSummary(null);
        setSelectedImages([]);
      }

      setProgress('Reading ocr_results from local folder…');
      const ocrAbort = new AbortController();
      const ocrTimeout = setTimeout(() => ocrAbort.abort(), 15000);
      let resp;
      try {
        resp = await fetch(`${LOADER_SERVER_URL}/ocr-results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: ocrAbort.signal,
          body: JSON.stringify({
            class: folder.class || '',
            section: folder.section || '',
            subject: folder.subject || '',
            test_date: folder.test_date,
            folder_name: folder.title || ''
          })
        });
      } catch (fetchErr) {
        if (fetchErr && fetchErr.name === 'AbortError') {
          throw new Error(
            'loader-server on port 3101 did not respond within 15s (likely frozen). ' +
            'Restart it: npm run loader-server'
          );
        }
        throw fetchErr;
      } finally {
        clearTimeout(ocrTimeout);
      }
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        const detail = (data && data.error) ? data.error : `HTTP ${resp.status}`;
        const avail = (data && Array.isArray(data.available) && data.available.length)
          ? ` Folders present that day: ${data.available.slice(0, 8).join(', ')}${data.available.length > 8 ? ', …' : ''}`
          : '';
        const hint = /Failed to fetch|NetworkError/i.test(detail)
          ? ' Is the loader-server running on port 3101? (npm run loader-server)'
          : '';
        throw new Error(`${detail}.${avail}${hint}`);
      }

      const pages = Array.isArray(data.data) ? data.data : [];
      if (pages.length === 0) {
        showMessage(`The ocr_results file in "${data.folder || ''}" was empty.`, 'error');
        return;
      }

      // Odd pages only (0-based indices 0,2,4… = 1st, 3rd, 5th images).
      const frontPages = pages.filter((_, idx) => idx % 2 === 0);
      const hvVal = (hv, key) => {
        const node = hv && hv[key];
        return node && node.value != null ? String(node.value).trim() : '';
      };

      // Every OCR front filename currently on disk — source of truth for
      // which sorted image_nos should remain.
      const ocrFrontSet = new Set();
      for (const page of frontPages) {
        if (page && page.missing_file) continue;
        const imageNo = page.filename || page.image_name || '';
        if (imageNo) ocrFrontSet.add(String(imageNo).trim().toLowerCase());
      }

      const students = [];
      let skippedNoImage = 0;
      let emptyRollCount = 0;
      let skippedMissingFronts = 0;
      let skippedAlreadyPresent = 0;
      let autoCorrectedCount = 0;

      // Fresh fetch — don't rely on importedStudents state (often still empty
      // when Get Sorting is clicked right after selecting a folder).
      setProgress('Loading student list for name auto-correct…');
      const expectedByRoll = await fetchExpectedByRollForFolder(folder, token);

      for (const page of frontPages) {
        if (page && page.missing_file) { skippedMissingFronts += 1; continue; }
        const hv = page.header_verification || {};
        const letter = hvVal(hv, 'roll_number_letter');
        const d1 = hvVal(hv, 'roll_number_1st_digit');
        const d2 = hvVal(hv, 'roll_number_2nd_digit');
        const rollCombined = (letter || d1 || d2) ? `${letter}${d1}${d2}` : '';
        const roll = (rollCombined || hvVal(hv, 'roll_number')).toUpperCase();
        const name = hvVal(hv, 'student_name');
        const imageNo = page.filename || page.image_name || '';
        if (!imageNo) { skippedNoImage += 1; continue; }
        if (isIncremental && existingImageNos.has(String(imageNo).trim().toLowerCase())) {
          skippedAlreadyPresent += 1;
          continue;
        }
        const displayRoll = roll || '000';
        const displayName = name || 'unknown';
        if (!roll) emptyRollCount += 1;
        let uniqueCode = roll || `000-${String(emptyRollCount).padStart(2, '0')}`;
        let rollName = `(${displayRoll})${displayName}`;
        const fix = correctOcrNameAgainstImported(rollName, uniqueCode, expectedByRoll);
        if (fix.corrected) {
          rollName = fix.roll_name;
          uniqueCode = fix.unique_code;
          autoCorrectedCount += 1;
        }
        students.push({
          unique_code: uniqueCode,
          roll_name: rollName,
          image_no: imageNo
        });
      }

      if (isIncremental) {
        // Images deleted chip: those numbers were removed from the website.
        // Take just those rows off the saved sort, including when the folder
        // is already checked, Edupaged, or printed. Every other row stays.
        let removedChipCount = 0;
        const deletedSet = new Set();
        try {
          const deltaRes = await fetch(`${API_BASE_URL}/analyze/folders/image-deltas`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const deltaData = await deltaRes.json().catch(() => ({}));
          const rubricMatch = String(fp).match(/rubrics\/(\d+)/);
          const deltaRow = (deltaData.data || []).find((item) => (
            rubricMatch && String(item.id) === rubricMatch[1]
          ));
          for (const n of (deltaRow && Array.isArray(deltaRow.deleted) ? deltaRow.deleted : [])) {
            deletedSet.add(String(n));
          }
        } catch (_) { /* the chip is optional; OCR sync still runs */ }
        const imageNumberOf = (value) => {
          const text = String(value || '');
          const named = text.match(/Image_(\d+)/i);
          if (named) return named[1];
          const bare = text.trim();
          return /^\d+$/.test(bare) ? bare : '';
        };
        const chipImageNos = [];
        if (deletedSet.size) {
          for (const row of snapRows) {
            const raw = String((row && row.image_no) || '').trim();
            if (!raw) continue;
            const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
            const numbers = parts.map(imageNumberOf).filter(Boolean);
            if (numbers.length && numbers.every((n) => deletedSet.has(n))) chipImageNos.push(raw);
          }
        }
        if (chipImageNos.length) {
          setProgress(`Removing ${chipImageNos.length} image(s) deleted from the website…`);
          const chipResp = await fetch(`${API_BASE_URL}/analyze/snapshots/remove-images`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ folder_path: fp, image_nos: chipImageNos, deletedChip: true })
          });
          const chipData = await chipResp.json().catch(() => ({}));
          if (!chipResp.ok || !chipData.success) {
            throw new Error((chipData && chipData.message) || 'Failed to remove images deleted from the website');
          }
          if (!chipData.skipped) {
            removedChipCount = chipData.snapshot_deleted != null
              ? chipData.snapshot_deleted
              : chipImageNos.length;
          }
        }

        // Drop sorted rows whose image is gone from ocr_results (retest /
        // deleted scans). Keep rows that still match an OCR front.
        // An already checked / Edupaged / printed folder skips this sweep.
        // The chip removal above is the one that still runs for those folders.
        const staleImageNos = [];
        for (const row of snapRows) {
          const raw = String((row && row.image_no) || '').trim();
          if (!raw) continue;
          const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
          const anyStillPresent = parts.some(p => ocrFrontSet.has(p.toLowerCase()));
          if (!anyStillPresent) staleImageNos.push(raw);
        }

        let removedCount = 0;
        const namedSnapRows = snapRows.filter(row => String((row && row.image_no) || '').trim());
        const wouldWipeAll = namedSnapRows.length > 0 && (
          ocrFrontSet.size === 0 || staleImageNos.length >= namedSnapRows.length
        );
        const approvedList = ['auto-checked', 'copy_to_edupage', 'completed'].includes(snapStatus);
        if (staleImageNos.length > 0 && !wouldWipeAll && !approvedList) {
          setProgress(`Removing ${staleImageNos.length} sorted row(s) missing from ocr_results…`);
          const removeResp = await fetch(`${API_BASE_URL}/analyze/snapshots/remove-images`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ folder_path: fp, image_nos: staleImageNos })
          });
          const removeData = await removeResp.json().catch(() => ({}));
          if (!removeResp.ok || !removeData.success) {
            throw new Error((removeData && removeData.message) || 'Failed to remove stale sorted images');
          }
          removedCount = removeData.snapshot_deleted != null
            ? removeData.snapshot_deleted
            : staleImageNos.length;
          if (removedCount > 0 && !removeData.skipped) {
            for (const raw of staleImageNos) {
              for (const part of String(raw).split(',')) {
                existingImageNos.delete(part.trim().toLowerCase());
              }
            }
          }
        } else if (staleImageNos.length > 0) {
          setProgress('OCR file did not match the saved list — sorted list kept');
        }

        // Late fronts were each stored as sheet 1, batch 2. Recount those
        // rolls on the saved list before it is copied into the working table.
        // Rolls that already share one total are left alone.
        setProgress('Recounting late rolls…');
        const repairResp = await fetch(`${API_BASE_URL}/analyze/snapshots/recount-late`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ folder_path: fp })
        });
        const repairData = await repairResp.json().catch(() => ({}));
        if (!repairResp.ok || !repairData.success) {
          throw new Error((repairData && repairData.message) || 'Failed to recount late rolls');
        }
        const repairedCount = Array.isArray(repairData.recounted) ? repairData.recounted.length : 0;

        // Restore the (now pruned) saved list into the working table, then
        // append any brand-new OCR fronts.
        setProgress('Restoring saved sorted list…');
        const importResp = await fetch(`${API_BASE_URL}/analyze/snapshots/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ folder_path: fp })
        });
        const importData = await importResp.json().catch(() => ({}));
        if (!importResp.ok || !importData.success) {
          throw new Error((importData && importData.message) || 'Failed to restore saved sorted list');
        }

        if (students.length === 0) {
          const restored = Array.isArray(importData.data) ? importData.data : [];
          setResults(restored);
          setSelectedImages([]);
          const liveFix = await autoCorrectLiveResultsAgainstImported(restored, token, fp, expectedByRoll);
          await fetchSnapshotMeta(fp);
          await fetchSnapshotFolderSet();
          const parts = [];
          if (removedChipCount > 0) parts.push(`removed ${removedChipCount} image(s) deleted from the website`);
          if (removedCount > 0) parts.push(`removed ${removedCount} stale image(s) not in ocr_results`);
          if (skippedAlreadyPresent > 0) {
            parts.push(`kept ${liveFix.rows.length} row(s); all ${skippedAlreadyPresent} OCR front(s) already listed`);
          } else {
            parts.push(`restored ${liveFix.rows.length} sorted row(s); no new OCR fronts`);
          }
          if (liveFix.corrected > 0) parts.push(`auto-corrected ${liveFix.corrected} name(s) from student list`);
          if (repairedCount > 0) parts.push(`recounted ${repairedCount} late roll${repairedCount !== 1 ? 's' : ''}`);
          showMessage(parts.join(' — '), (removedChipCount > 0 || removedCount > 0 || liveFix.corrected > 0 || repairedCount > 0) ? 'success' : 'info');
          setProgress('');
          return;
        }

        setProgress(`Appending ${students.length} new sheet(s)…`);
        // Placeholders. The append call recounts each roll that gained a
        // page and overwrites these with the full sheet count and batch.
        const rows = students.map(s => ({
          roll_name: s.roll_name,
          image_no: s.image_no,
          unique_code: s.unique_code,
          sheet_count: 1,
          batch_size_final: 2,
          model_type: 'ocr',
          limit_type: 'OCR'
        }));
        const appendResp = await fetch(`${API_BASE_URL}/analyze/snapshots/append`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ folder_path: fp, rows, from_get_sorting: true })
        });
        const appendData = await appendResp.json().catch(() => ({}));
        if (!appendResp.ok || !appendData.success) {
          throw new Error((appendData && appendData.message) || `Get Sorting failed (HTTP ${appendResp.status})`);
        }

        const liveRows = await fetchResults();
        const liveFix = await autoCorrectLiveResultsAgainstImported(liveRows, token, fp, expectedByRoll);
        await fetchSnapshotMeta(fp);
        await fetchSnapshotFolderSet();
        const totalCorrected = autoCorrectedCount + liveFix.corrected;
        const lateRolls = new Set();
        for (const roll of (repairData.recounted || [])) {
          if (roll && roll.uniqueCode) lateRolls.add(String(roll.uniqueCode));
        }
        for (const roll of (appendData.recounted || [])) {
          if (roll && roll.uniqueCode) lateRolls.add(String(roll.uniqueCode));
        }
        const msg = [
          removedChipCount > 0 ? `Removed ${removedChipCount} image(s) deleted from the website` : null,
          removedCount > 0 ? `Removed ${removedCount} stale image(s)` : null,
          `Appended ${students.length} new sheet(s)`,
          lateRolls.size > 0
            ? `recounted ${lateRolls.size} late roll${lateRolls.size !== 1 ? 's' : ''}`
            : null,
          totalCorrected > 0 ? `auto-corrected ${totalCorrected} name(s)` : null,
          expectedByRoll.size === 0 ? 'no student list found for name auto-correct' : null,
          `(${data.folder || ''}${data.file ? '/' + data.file : ''})`
        ].filter(Boolean).join(' · ')
          + (emptyRollCount ? ` ${emptyRollCount} with missing roll → (000).` : '')
          + (skippedNoImage ? ` Skipped ${skippedNoImage} page(s) with no filename.` : '')
          + (skippedMissingFronts ? ` ${skippedMissingFronts} front(s) skipped (local image missing).` : '');
        showMessage(msg, (emptyRollCount || skippedNoImage || skippedMissingFronts || expectedByRoll.size === 0) ? 'info' : 'success');
        setProgress('');
        return;
      }

      if (students.length === 0) {
        throw new Error(`No usable front-page OCR headers found (${frontPages.length} odd page(s) checked).`);
      }

      setProgress(`Clearing sorted table & importing ${students.length} student(s)…`);
      const batchResp = await fetch(`${API_BASE_URL}/analyze/ocr-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder_path: fp, students })
      });
      const batchData = await batchResp.json().catch(() => ({}));
      if (!batchResp.ok || !batchData.success) {
        throw new Error((batchData && batchData.message) || `Get Sorting failed (HTTP ${batchResp.status})`);
      }

      const liveRows = await fetchResults();
      const liveFix = await autoCorrectLiveResultsAgainstImported(liveRows, token, fp, expectedByRoll);
      const totalCorrected = autoCorrectedCount + liveFix.corrected;
      const msg = `Imported ${batchData.students || students.length} student(s)`
        + ` (${data.folder || ''}${data.file ? '/' + data.file : ''}).`
        + ` Used ${students.length} front(s) of ${pages.length} page(s).`
        + (totalCorrected > 0 ? ` Auto-corrected ${totalCorrected} name(s) from student list.` : '')
        + (expectedByRoll.size === 0 ? ' No student list found for name auto-correct.' : '')
        + (emptyRollCount ? ` ${emptyRollCount} with missing roll → (000).` : '')
        + (skippedNoImage ? ` Skipped ${skippedNoImage} page(s) with no filename.` : '')
        + (skippedMissingFronts ? ` ${skippedMissingFronts} front(s) skipped (local image missing).` : '');
      showMessage(msg, (emptyRollCount || skippedNoImage || skippedMissingFronts || expectedByRoll.size === 0) ? 'info' : 'success');
      setProgress('');
    } catch (err) {
      const msg = String(err && err.message ? err.message : err);
      const hint = /Failed to fetch|NetworkError/i.test(msg)
        ? ' — is the loader-server running on port 3101? (npm run loader-server)'
        : '';
      showMessage('Get Sorting failed: ' + msg + hint, 'error');
      setProgress('');
    } finally {
      setGettingOcr(false);
    }
  };

  const handleStartAnalyzing = async () => {
    if (!folderPath) {
      // Block until the user dismisses the popup so the message doesn't
      // disappear after the toast's 2-second timeout (which was easy to miss).
      setBlockedDialog({
        title: 'No folder selected',
        message: 'Please select a folder before starting the sort.'
      });
      return;
    }

    // Local folder vs website/scans image counts disagree — refuse to sort
    // until Upload brings them back in sync (red "LOCAL ≠ WEB" card).
    if (selectedFolderId && uploadMismatchIds.has(String(selectedFolderId))) {
      const err = autoSortStatusMap.get(String(selectedFolderId))?.last_error;
      setBlockedDialog({
        title: 'Local ≠ website image counts',
        message: (err || 'The local folder and the website/scans folder do not have the same number of images.')
          + ' Click Upload on the folder card to sync them, then try sorting again.'
      });
      return;
    }

    // Pre-flight inspection: block on empty folder OR duplicate file names.
    setProgress('Inspecting scans folder...');
    const { total, duplicates } = await inspectScansForFolder();

    if (total === 0) {
      setProgress('');
      setBlockedDialog({
        title: 'No images to sort',
        message: 'There are 0 images in this folder, so sorting cannot be done. Please upload scans first and try again.'
      });
      return;
    }

    if (duplicates && duplicates.length > 0) {
      const list = duplicates.slice(0, 10).map(d => `${d.name} (×${d.count})`).join(', ');
      const more = duplicates.length > 10 ? ` …and ${duplicates.length - 10} more` : '';
      setProgress('');
      setBlockedDialog({
        title: 'Duplicate images detected',
        message: `There are duplicate images in this folder, so sorting cannot be done. Found ${duplicates.length} duplicate filename(s): ${list}${more}. Remove the extras and try again.`
      });
      return;
    }

    // Check for missing image numbers before proceeding
    setProgress('Checking for missing images...');
    const gapInfo = await checkForMissingImages();
    if (gapInfo && gapInfo.missing.length > 0) {
      const missingList = gapInfo.missing.length <= 20
        ? gapInfo.missing.join(', ')
        : gapInfo.missing.slice(0, 20).join(', ') + `... and ${gapInfo.missing.length - 20} more`;
      
      const proceed = window.confirm(
        `⚠️ Missing Image Numbers Detected!\n\n` +
        `Range: ${gapInfo.rangeStart} to ${gapInfo.rangeEnd} (${gapInfo.total} images uploaded)\n` +
        `Missing ${gapInfo.missing.length} image(s): ${missingList}\n\n` +
        `Some student answer sheets may not have been uploaded.\n\n` +
        `Do you want to proceed with sorting anyway?`
      );
      
      if (!proceed) {
        setProgress('');
        return;
      }
    }

    setIsAnalyzing(true);
    setShowFolderSection(false); // Hide calendar + folder section when sorting starts
    setProgress('Starting analysis...');
    setMessage({ show: false, text: '', type: '' });
    setProgressDetails(null);

    try {
      const token = localStorage.getItem('authToken');
      // Clear this folder's sort rows only. Another class keeps its own list.
      const clearAnalyzeResponse = await fetch(
        `${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(folderPath)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!clearAnalyzeResponse.ok) {
        const data = await clearAnalyzeResponse.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to clear previous Sort data');
      }

      const clearAssessmentResponse = await fetch(`${API_BASE_URL}/assess/results`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!clearAssessmentResponse.ok) {
        const data = await clearAssessmentResponse.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to clear previous Check data');
      }

      setResults([]);
      setSummary(null);
      setSelectedImages([]);
      const currentProvider = sortProvider;
      const response = await fetch(`${API_BASE_URL}/analyze/extract-roll-numbers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: folderPath,
          batch_size: parseInt(batchSize),
          limit: limit,
          model: model,
          provider: currentProvider,
          ...(isLocalProvider && model ? { local_model: model } : {})
        })
      });

      const data = await response.json();

      if (response.status === 202 && data.session_id) {
        // Analysis started, now poll for progress
        setSessionId(data.session_id);
        setProgress('Analysis started. Processing scans...');
      } else if (response.ok) {
        // Legacy response - immediate completion
        showMessage(data.message || 'Analysis completed successfully', 'success');
        setResults(data.data || []);
        setSummary(data.summary);
        setProgress('Analysis completed!');
        setIsAnalyzing(false);
        const finalRows = await fetchResults(); // Refresh results
        await autoSendInvalidRollsToMistakes(finalRows);
      } else {
        showMessage(data.message || 'Analysis failed', 'error');
        setProgress('');
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('Error analyzing:', error);
      showMessage('Failed to analyze: ' + error.message, 'error');
      setProgress('');
      setIsAnalyzing(false);
    }
  };

  const handleStopAnalyzing = async () => {
    if (!sessionId) {
      setIsAnalyzing(false);
      showMessage('Sorting stopped', 'info');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/cancel/${sessionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showMessage('Sorting stopped successfully', 'info');
      } else {
        const data = await response.json();
        showMessage(data.message || 'Failed to stop sorting', 'error');
      }
    } catch (error) {
      console.error('Error stopping sorting:', error);
      showMessage('Failed to stop sorting: ' + error.message, 'error');
    } finally {
      // Clear frontend state regardless of backend response
      setIsAnalyzing(false);
      setSessionId(null);
      setProgress('');
      setProgressDetails(null);
      // Refresh results to show any partial results that were saved
      await fetchResults();
    }
  };

  const handleReanalyzeSpecific = async () => {
    if (!folderPath) {
      setBlockedDialog({
        title: 'No folder selected',
        message: 'Please select a folder before re-analysing specific images.'
      });
      return;
    }

    if (!specificImageRange.trim()) {
      showMessage('Please enter image file names', 'error');
      return;
    }

    // Parse the input to get specific image names
    // Supports: "image001.jpg", "image001.jpg, image005.jpg", "image001.jpg,image005.jpg"
    const imageNames = specificImageRange
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);
    
    if (imageNames.length === 0) {
      showMessage('No valid image names found', 'error');
      return;
    }

    // Remove duplicates
    const uniqueNames = [...new Set(imageNames)];
    
    if (!window.confirm(`Re-analyze ${uniqueNames.length} image(s)?\n\n${uniqueNames.join('\n')}`)) {
      return;
    }

    setIsAnalyzing(true);
    setProgress(`Starting re-analysis of ${uniqueNames.length} image(s)...`);
    setMessage({ show: false, text: '', type: '' });
    setProgressDetails(null);

    try {
      const token = localStorage.getItem('authToken');
      const currentProvider = sortProvider;
      const response = await fetch(`${API_BASE_URL}/analyze/extract-roll-numbers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: folderPath,
          batch_size: parseInt(batchSize),
          limit: limit,
          model: model,
          specific_image_names: uniqueNames,
          provider: currentProvider,
          ...(isLocalProvider && model ? { local_model: model } : {})
        })
      });

      const data = await response.json();

      if (response.status === 202 && data.session_id) {
        // Analysis started, now poll for progress
        setSessionId(data.session_id);
        setProgress(`Re-analysis started. Processing ${uniqueNames.length} specific image(s)...`);
        setSpecificImageRange(''); // Clear input
        setShowReanalyzeOptions(false); // Hide options
      } else if (response.ok) {
        // Legacy response - immediate completion
        showMessage(data.message || 'Re-analysis completed successfully', 'success');
        setResults(data.data || []);
        setSummary(data.summary);
        setProgress('Re-analysis completed!');
        setIsAnalyzing(false);
        const finalRows = await fetchResults();
        await autoSendInvalidRollsToMistakes(finalRows);
        setSpecificImageRange('');
        setShowReanalyzeOptions(false);
      } else {
        showMessage(data.message || 'Re-analysis failed', 'error');
        setProgress('');
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('Error re-analyzing:', error);
      showMessage('Failed to re-analyze: ' + error.message, 'error');
      setProgress('');
      setIsAnalyzing(false);
    }
  };

  const handleSelectImage = (imageName) => {
    setSelectedImages(prev => {
      if (prev.includes(imageName)) {
        return prev.filter(name => name !== imageName);
      } else {
        return [...prev, imageName];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedImages.length === results.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(results.map(r => r.image_no));
    }
  };

  const handleReanalyzeSelected = async () => {
    if (selectedImages.length === 0) {
      showMessage('Please select at least one image', 'error');
      return;
    }

    if (!folderPath) {
      setBlockedDialog({
        title: 'No folder selected',
        message: 'Please select a folder before re-analysing the selected images.'
      });
      return;
    }

    if (!window.confirm(`Re-analyze ${selectedImages.length} selected image(s)?\n\n${selectedImages.join('\n')}`)) {
      return;
    }

    setIsAnalyzing(true);
    setProgress(`Starting re-analysis of ${selectedImages.length} image(s)...`);
    setMessage({ show: false, text: '', type: '' });
    setProgressDetails(null);

    try {
      const token = localStorage.getItem('authToken');
      const currentProvider = sortProvider;
      const response = await fetch(`${API_BASE_URL}/analyze/extract-roll-numbers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: folderPath,
          batch_size: parseInt(batchSize),
          limit: limit,
          model: model,
          specific_image_names: selectedImages,
          provider: currentProvider,
          ...(isLocalProvider && model ? { local_model: model } : {})
        })
      });

      const data = await response.json();

      if (response.status === 202 && data.session_id) {
        setSessionId(data.session_id);
        setProgress(`Re-analysis started. Processing ${selectedImages.length} selected image(s)...`);
        setSelectedImages([]); // Clear selection
      } else if (response.ok) {
        showMessage(data.message || 'Re-analysis completed successfully', 'success');
        setResults(data.data || []);
        setSummary(data.summary);
        setProgress('Re-analysis completed!');
        setIsAnalyzing(false);
        const finalRows = await fetchResults();
        await autoSendInvalidRollsToMistakes(finalRows);
        setSelectedImages([]);
      } else {
        showMessage(data.message || 'Re-analysis failed', 'error');
        setProgress('');
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('Error re-analyzing:', error);
      showMessage('Failed to re-analyze: ' + error.message, 'error');
      setProgress('');
      setIsAnalyzing(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      // Send folder_path to trigger batch size recalculation
      const url = rowWriteUrl(recordId, { withFolder: true });
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        showMessage('Record deleted successfully', 'success');
        await refreshOpenList();
      } else {
        showMessage(data.message || 'Failed to delete', 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showMessage('Failed to delete: ' + error.message, 'error');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedImages.length === 0) {
      showMessage('Please select at least one record to delete', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedImages.length} selected record(s)?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      
      // Get IDs of selected records
      const selectedRecords = results.filter(r => selectedImages.includes(r.image_no));
      const selectedIds = selectedRecords.map(r => r.id);

      // Delete each selected record
      for (const id of selectedIds) {
        const url = rowWriteUrl(id, { withFolder: true });
        
        await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      showMessage(`Successfully deleted ${selectedIds.length} record(s)`, 'success');
      setSelectedImages([]);
      await refreshOpenList();
    } catch (error) {
      console.error('Error deleting selected:', error);
      showMessage('Failed to delete selected records: ' + error.message, 'error');
    }
  };

  const handleDeleteAll = async () => {
    if (!folderPath) {
      showMessage('Select a folder first', 'error');
      return;
    }
    if (!window.confirm('Delete all sorted rows for this folder?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        viewingSavedSortRef.current
          ? `${API_BASE_URL}/analyze/snapshots?folder_path=${encodeURIComponent(folderPath)}`
          : `${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(folderPath)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        const removed = data.deleted_count != null ? data.deleted_count : data.deleted;
        showMessage(`Deleted ${removed} records`, 'success');
        setResults([]);
        setSummary(null);
        setCalendarCollapsed(false);
        if (viewingSavedSortRef.current) {
          fetchSnapshotMeta(folderPath);
          fetchSnapshotFolderSet();
        }
      } else {
        showMessage(data.message || 'Failed to delete', 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showMessage('Failed to delete: ' + error.message, 'error');
    }
  };

  const handleMergeSelected = async () => {
    if (selectedImages.length < 2) {
      showMessage('Please select at least 2 records to merge', 'error');
      return;
    }

    if (!window.confirm(`Merge ${selectedImages.length} selected records into one?`)) {
      return;
    }

    try {
      // Get selected records - selectedImages contains image_no values, not IDs
      const selectedData = results.filter(r => selectedImages.includes(r.image_no));
      
      if (selectedData.length < 2) {
        showMessage('Could not find selected records', 'error');
        return;
      }
      
      // Sort by ID to keep the first one as the base
      selectedData.sort((a, b) => a.id - b.id);
      const baseRecord = selectedData[0];
      const recordsToMerge = selectedData.slice(1);

      // Combine image numbers
      const combinedImageNos = selectedData
        .map(r => r.image_no)
        .filter((name, idx, arr) => arr.indexOf(name) === idx) // Remove duplicates
        .join(', ');

      // Update sheet_count to combined total
      const totalSheetCount = selectedData.reduce((sum, r) => sum + (parseInt(r.sheet_count) || 0), 0);
      
      // Calculate batch_size_final (sheet_count * 2)
      const batchSizeFinal = totalSheetCount * 2;

      const token = localStorage.getItem('authToken');
      
      // Update the base record with merged data
      const updateResponse = await fetch(rowWriteUrl(baseRecord.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_no: combinedImageNos,
          sheet_count: totalSheetCount,
          batch_size_final: batchSizeFinal,
          folder_path: folderPath // Send folder_path for batch size recalculation
        })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update base record');
      }

      // Delete the other records
      const idsToDelete = recordsToMerge.map(r => r.id);
      for (const id of idsToDelete) {
        const deleteUrl = rowWriteUrl(id, { withFolder: true });
        
        await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      showMessage(`Successfully merged ${selectedImages.length} records`, 'success');
      setSelectedImages([]);
      await refreshOpenList();
    } catch (error) {
      console.error('Error merging records:', error);
      showMessage('Failed to merge records: ' + error.message, 'error');
    }
  };

  // MessageBox is a modal popup — user must click OK to dismiss. No auto-timer.
  const showMessage = (text, type) => {
    setMessage({ show: true, text, type });
  };

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig.column === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...results].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      // Numeric sorting for sheet_count and batch_size_final
      if (field === 'sheet_count' || field === 'batch_size_final') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Prefer numeric image order when sorting by image filename
      if (field === 'image_no') {
        const numKey = (v) => {
          const nums = String(v || '').match(/\d+/g);
          return nums && nums.length ? parseInt(nums[nums.length - 1], 10) : NaN;
        };
        const aNum = numKey(aVal);
        const bNum = numKey(bVal);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aNum !== bNum) {
          return direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
      }

      // String comparison for other fields
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setResults(sorted);
    setSortConfig({ column: field, direction });
    setHighlightSort(null); // clear highlight sort when column sort applied
  };

  // Reconciliation view: sort display order only (does not mutate `results`).
  // Supports Expected name (`expected_name`) and Image (`image_no`).
  const handleReconciliationSort = (field) => {
    let direction = 'asc';
    if (sortConfig.column === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ column: field, direction });
    setHighlightSort(null);
  };

  const sortIndicator = (field) => (
    <span style={{ marginLeft: 4, opacity: sortConfig.column === field ? 1 : 0.45, fontSize: '0.7rem' }}>
      {sortConfig.column === field ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  const compareImageNames = (aName, bName, direction) => {
    const numKey = (v) => {
      const nums = String(v || '').match(/\d+/g);
      return nums && nums.length ? parseInt(nums[nums.length - 1], 10) : NaN;
    };
    const aNum = numKey(aName);
    const bNum = numKey(bName);
    const aEmpty = !String(aName || '').trim();
    const bEmpty = !String(bName || '').trim();
    // Missing images always sink to the bottom (regardless of direction)
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aNum !== bNum) {
      return direction === 'asc' ? aNum - bNum : bNum - aNum;
    }
    const aVal = String(aName || '').toLowerCase();
    const bVal = String(bName || '').toLowerCase();
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  // One-click "accept the imported list's name for this row". Used by the
  // green tick button next to "⚠ Name mismatch" in the matched table —
  // takes the expected `(A01) Ahmad` text from the student list and
  // writes it verbatim to the AI row's roll_name, recomputing
  // unique_code + sheet_count just like the manual paste flow. After the
  // update succeeds, also writes a record to the MistakesTab so the
  // examiner has a durable log of every AI name typo that was corrected,
  // with the matching image reference for evidence.
  const acceptExpectedName = async (id, expectedName) => {
    if (!expectedName) return;
    const trimmed = String(expectedName).trim();
    if (!trimmed) return;
    // Snapshot the row BEFORE we PUT — we need the original AI roll_name
    // and the image reference for the Mistakes record.
    const beforeRow = results.find(r => r.id === id) || null;
    const rollMatch = trimmed.match(/\(([^)]+)\)/);
    const newUniqueCode = rollMatch ? rollMatch[1].substring(0, 3).toUpperCase() : null;
    const updateData = { roll_name: trimmed, folder_path: folderPath };
    if (newUniqueCode) updateData.unique_code = newUniqueCode;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(rowWriteUrl(id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updateData)
      });
      if (response.ok) {
        showMessage(`Name updated to "${trimmed}"`, 'success');
        setResults(prev => {
          const updated = prev.map(r => {
            if (r.id !== id) return r;
            const u = { ...r, roll_name: trimmed };
            if (newUniqueCode) u.unique_code = newUniqueCode;
            return u;
          });
          // Re-derive sheet_count / batch_size_final because changing the
          // unique_code can move this row into a different student's group.
          const counts = {};
          updated.forEach(r => {
            if (r.unique_code && r.unique_code !== '000') {
              counts[r.unique_code] = (counts[r.unique_code] || 0) + 1;
            }
          });
          return updated.map(r => {
            if (!r.unique_code || r.unique_code === '000') return r;
            const sc = counts[r.unique_code] || 0;
            return { ...r, sheet_count: sc, batch_size_final: sc * 2 };
          });
        });

        // Also log to MistakesTab. Folder-scoped so the record carries
        // class + subject; the image filename is embedded in [brackets]
        // inside `comments` because MistakesTab's image preview button
        // extracts it from there. Marked PERMANENT so the cleanup cron
        // doesn't sweep it.
        try {
          const folder = scansFolders.find(f => String(f.id) === String(selectedFolderId));
          const folderClass = (folder && folder.class) || '';
          const folderSubject = (folder && folder.subject) || '';
          const firstImage = beforeRow && beforeRow.image_no
            ? String(beforeRow.image_no).split(',')[0].trim()
            : '';
          const aiOriginal = beforeRow && beforeRow.roll_name ? beforeRow.roll_name : '(unknown)';
          const commentParts = [
            `AI read: "${aiOriginal}"`,
            `Expected: "${trimmed}"`,
            firstImage ? `[${firstImage}]` : ''
          ].filter(Boolean);
          const reason = 'Name mismatch corrected from Sort tab — ' + commentParts.join(' · ');
          await fetch(`${API_BASE_URL}/students/unmatched`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              class: folderClass,
              section: '',
              subject: folderSubject,
              name: aiOriginal,
              marks: '',
              comments: 'PERMANENT:' + reason,
              teacher_name: (folder && folder.created_by) || '',
              test_date: (folder && folder.test_date) || ''
            })
          });
        } catch (mistakeErr) {
          // Logging is non-critical — the actual correction already
          // persisted. Warn so we know if the mistakes log fell behind.
          console.warn('[AnalyzeTab] Mistakes log POST failed:', mistakeErr);
        }
      } else {
        const data = await response.json();
        showMessage(data.message || 'Failed to accept name', 'error');
      }
    } catch (err) {
      showMessage('Failed to accept name', 'error');
    }
  };

  const handlePasteRollName = async (id, currentRollName) => {
    if (!copiedRollName || copiedRollName === currentRollName) return;
    const rollMatch = copiedRollName.match(/\(([^)]+)\)/);
    const newUniqueCode = rollMatch ? rollMatch[1].substring(0, 3).toUpperCase() : null;
    const updateData = { roll_name: copiedRollName, folder_path: folderPath };
    if (newUniqueCode) updateData.unique_code = newUniqueCode;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(rowWriteUrl(id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updateData)
      });
      if (response.ok) {
        showMessage('Roll name pasted', 'success');
        setResults(prev => {
          const updated = prev.map(r => {
            if (r.id !== id) return r;
            const u = { ...r, roll_name: copiedRollName };
            if (newUniqueCode) u.unique_code = newUniqueCode;
            return u;
          });
          const counts = {};
          updated.forEach(r => {
            if (r.unique_code && r.unique_code !== '000') {
              counts[r.unique_code] = (counts[r.unique_code] || 0) + 1;
            }
          });
          return updated.map(r => {
            if (!r.unique_code || r.unique_code === '000') return r;
            const sc = counts[r.unique_code] || 0;
            return { ...r, sheet_count: sc, batch_size_final: sc * 2 };
          });
        });
      } else {
        const data = await response.json();
        showMessage(data.message || 'Failed to paste', 'error');
      }
    } catch (err) {
      showMessage('Failed to paste roll name', 'error');
    }
  };

  const handlePreviewImage = async (imageName, rowFolderPath) => {
    const sourcePath = rowFolderPath || folderPath;
    const rubricIdMatch = sourcePath.match(/rubrics\/(\d+)/);
    if (!rubricIdMatch) return;
    const rubricId = rubricIdMatch[1];
    const firstName = imageName.split(',')[0].trim();
    setPreviewLoading(firstName);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/scans/${rubricId}/preview/${encodeURIComponent(firstName)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!data.success) return;

      // Pre-fetch the image bytes through the backend's same-origin
      // /raw streaming endpoint and turn them into a blob: URL before
      // handing them to AnnotatableImage. Using the presigned S3 URL
      // directly as <img src> taints the html2canvas capture so the
      // annotator's Save/Copy buttons would output only the annotation
      // layer over a black background. A blob: URL is treated as same-
      // origin and keeps the canvas clean. Falls back to the presigned
      // URL if the proxy fails so the preview itself still loads.
      let imageUrl = data.downloadUrl;
      try {
        const rawResp = await fetch(
          `${API_BASE_URL}/scans/${rubricId}/preview/${encodeURIComponent(firstName)}/raw`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (rawResp.ok) {
          const blob = await rawResp.blob();
          imageUrl = URL.createObjectURL(blob);
        } else {
          console.warn('[AnalyzeTab] Raw stream returned non-ok, falling back to presigned URL:', rawResp.status);
        }
      } catch (e) {
        console.warn('[AnalyzeTab] Raw stream failed, falling back to presigned URL:', e);
      }

      setPreviewImage({ url: imageUrl, name: firstName });
    } catch (err) {
      console.error('Preview error:', err);
    } finally {
      setPreviewLoading(null);
    }
  };

  // Revoke blob: URLs when the preview closes or swaps to another image,
  // so navigating image-to-image doesn't leak a buffer each time.
  // Direct presigned S3 URLs (non-blob) need no cleanup.
  useEffect(() => {
    const url = previewImage?.url;
    return () => {
      if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [previewImage?.url]);

  const handleCellEdit = (recordId, field, currentValue, options) => {
    editTokenRef.current += 1;
    setEditingCell({
      id: recordId,
      field,
      value: currentValue,
      selectInsideParens: !!(options && options.selectInsideParens),
      token: editTokenRef.current
    });
  };

  const handleCellChange = (value) => {
    setEditingCell(prev => ({ ...prev, value }));
  };

  // Drop the editor only if this same edit is still the open one. A click on
  // another row can open its box while this save is still in flight.
  const closeEditIfCurrent = (token) => {
    setEditingCell(prev => (prev && prev.token === token ? null : prev));
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const { id, field, value, token: editToken } = editingCell;
    const originalRecord = results.find(r => r.id === id);
    
    // If value hasn't changed, just exit editing mode
    if (value === originalRecord[field]) {
      closeEditIfCurrent(editToken);
      return;
    }

    // Auto-derive unique_code when roll_name is edited
    const updateData = { [field]: value, folder_path: folderPath };
    if (field === 'roll_name') {
      const rollMatch = value.match(/\(([^)]+)\)/);
      if (rollMatch && rollMatch[1]) {
        updateData.unique_code = rollMatch[1].substring(0, 3).toUpperCase();
      }
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(rowWriteUrl(id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        showMessage('Cell updated successfully', 'success');
        // Update the record in-place to preserve current sort order
        setResults(prev => {
          // First, apply the cell change
          const updated = prev.map(r => {
            if (r.id !== id) return r;
            const u = { ...r, [field]: value };
            if (field === 'roll_name' && updateData.unique_code) {
              u.unique_code = updateData.unique_code;
            }
            return u;
          });

          // Recalculate sheet_count and batch_size_final for all rows
          // by counting how many rows share each unique_code (excluding '000')
          const counts = {};
          updated.forEach(r => {
            if (r.unique_code && r.unique_code !== '000') {
              counts[r.unique_code] = (counts[r.unique_code] || 0) + 1;
            }
          });

          return updated.map(r => {
            if (!r.unique_code || r.unique_code === '000') return r;
            const newSheetCount = counts[r.unique_code] || 0;
            return { ...r, sheet_count: newSheetCount, batch_size_final: newSheetCount * 2 };
          });
        });
      } else {
        const data = await response.json();
        showMessage(data.message || 'Failed to update', 'error');
      }
    } catch (error) {
      console.error('Error updating cell:', error);
      showMessage('Failed to update: ' + error.message, 'error');
    }

    closeEditIfCurrent(editToken);
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // Calculate string similarity (returns a value between 0 and 1)
  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 1;
    
    // Levenshtein distance for similarity
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = getEditDistance(s1, s2);
    return (longer.length - editDistance) / longer.length;
  };

  // Levenshtein distance calculation
  const getEditDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  // Extract name from roll_name format: (A01)Ahmed Ali -> Ahmed Ali
  const extractName = (rollName) => {
    if (!rollName) return '';
    const match = rollName.match(/\)(.+)$/);
    return match ? match[1].trim() : rollName;
  };

  // Extract roll number from roll_name format: (A01)Ahmed Ali -> A01
  const extractRollNumber = (rollName) => {
    if (!rollName) return '';
    const match = rollName.match(/\(([^)]+)\)/);
    return match ? match[1].trim() : '';
  };

  // Check if a record has AI reading errors
  const hasAIError = (record) => {
    const currentName = extractName(record.roll_name);
    const currentRoll = extractRollNumber(record.roll_name);
    
    if (!currentName || !currentRoll || currentRoll === '000') return false;

    for (const otherRecord of results) {
      if (otherRecord.id === record.id) continue;
      
      const otherName = extractName(otherRecord.roll_name);
      const otherRoll = extractRollNumber(otherRecord.roll_name);
      
      if (!otherName || !otherRoll || otherRoll === '000') continue;

      const nameSimilarity = calculateSimilarity(currentName, otherName);
      
      // Scenario 1: Same name (90%+ similar) but different roll numbers
      // This suggests AI read the same student with different roll numbers
      if (nameSimilarity >= 0.9 && currentRoll !== otherRoll) {
        return true;
      }
      
      // Scenario 2: Same roll number but different names (less than 90% similar)
      // This suggests AI read different students with the same roll number
      if (currentRoll === otherRoll && nameSimilarity < 0.9) {
        return true;
      }
      
      // Note: Same roll + same name (>90% similar) is NORMAL - same student, multiple pages
      // Don't flag this as an error
    }
    
    return false;
  };

  // Strip the "(A01) " roll prefix from a name so "(A01)Ahmad" and "Ahmad"
  // compare equal. Folds case AND removes every character that isn't a
  // letter so spacing / punctuation differences don't split a student in
  // two. After normalisation, all of these collapse to the same string:
  //   "Abdul Rehman" / "Abdul-Rehman" / "Abdulrehman"  →  "abdulrehman"
  //   "M. Ahmad"     / "M Ahmad"      / "M-Ahmad"      →  "mahmad"
  const normalizeNameForMatch = (raw) => {
    if (!raw) return '';
    let s = String(raw);
    // Drop the leading "(A01)" roll prefix before stripping punctuation so
    // we don't accidentally fold the roll into the name (a01 + ahmad would
    // become "a01ahmad" otherwise).
    s = s.replace(/^\s*\([^)]*\)\s*/, '');
    // Keep only letters — eats periods, hyphens, spaces, digits, anything.
    return s.toLowerCase().replace(/[^a-z]/g, '');
  };

  // Extract the roll code (e.g. "A01") from a "(A01) Ahmad" string. Returns
  // empty string if no parenthesised prefix is present.
  const extractRollCode = (raw) => {
    if (!raw) return '';
    const m = String(raw).match(/^\s*\(([^)]+)\)/);
    return m ? m[1].trim().toUpperCase() : '';
  };

  // Build the buckets the reconciliation view renders. Matching is now by
  // **roll number**, not name:
  //   - matched: expected student + every AI row whose unique_code matches
  //     the roll embedded in the student's `(A01) Ahmad` prefix
  //   - missingStudents: imported students whose roll didn't appear in any
  //     AI row
  //   - unmatchedResults: AI rows whose unique_code doesn't appear in any
  //     imported student
  //   - duplicateRolls: Set of roll codes that show up on more than one AI
  //     row (so the table can flag them like StudentsTab does)
  // Each matched aiRow is annotated with `nameMismatch: true` when the AI's
  // name doesn't match the expected one — used to tint that row yellow.
  const buildReconciliation = () => {
    // Bucket AI rows by their unique_code (the roll).
    const aiByRoll = new Map();
    for (const r of results) {
      const roll = (r.unique_code || '').trim().toUpperCase();
      if (!roll) continue;
      if (!aiByRoll.has(roll)) aiByRoll.set(roll, []);
      aiByRoll.get(roll).push(r);
    }

    // Any roll that appears on 2+ AI rows is a duplicate. The table tints
    // these red and adds a "DUPLICATE" badge.
    const duplicateRolls = new Set();
    for (const [roll, rows] of aiByRoll.entries()) {
      if (rows.length > 1) duplicateRolls.add(roll);
    }

    const matched = [];
    const missingStudents = [];
    const usedRolls = new Set();

    for (const stu of importedStudents) {
      const expectedRoll = extractRollCode(stu.student_name);
      const expectedName = normalizeNameForMatch(stu.student_name);
      const hits = expectedRoll ? aiByRoll.get(expectedRoll) : null;

      if (!hits || hits.length === 0) {
        missingStudents.push({ expected: stu });
        continue;
      }

      // Always claim the rows for this student — a single student often
      // writes on multiple sheets and the AI legitimately returns one row
      // per sheet. The "DUPLICATE roll" flag only fires when the multiple
      // rows DISAGREE on the name: two sheets both reading "Abdullah
      // Rafiq" for roll T04 is just multi-sheet (no flag), but one sheet
      // saying "Abdullah Rafiq" and another saying "Bilal" for the same
      // T04 IS a real data error worth surfacing.
      const uniqueAiNames = new Set(hits.map(r => normalizeNameForMatch(r.roll_name)).filter(Boolean));
      const ambiguousDuplicate = hits.length > 1 && uniqueAiNames.size > 1;
      const annotated = hits.map(r => ({
        ...r,
        _nameMismatch: expectedName && normalizeNameForMatch(r.roll_name) !== expectedName,
        _isDuplicateRoll: ambiguousDuplicate
      }));
      matched.push({ expected: stu, aiRows: annotated });
      usedRolls.add(expectedRoll);
    }

    // Unmatched: every AI row whose roll didn't end up claimed by exactly
    // one imported student. That includes (a) rolls that don't appear in
    // the imported list at all and (b) every row that shares its roll with
    // another AI row (duplicates).
    const unmatchedResults = [];
    for (const [roll, rows] of aiByRoll.entries()) {
      if (usedRolls.has(roll)) continue;
      for (const r of rows) {
        unmatchedResults.push({
          ...r,
          _isDuplicateRoll: duplicateRolls.has(roll)
        });
      }
    }

    return { matched, missingStudents, unmatchedResults, duplicateRolls };
  };

  // Only the unexpected-blank names. Dates, filters, cards and scroll stay as they are.
  const refreshOcrNoise = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const noiseRes = await fetch(`${API_BASE_URL}/assess/ocr-noise`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!noiseRes.ok) return;
      const noiseData = await noiseRes.json();
      const noiseMap = new Map();
      for (const row of (noiseData.data || [])) {
        if (row && row.rubric_id != null) noiseMap.set(String(row.rubric_id), row);
      }
      setOcrNoiseMap(noiseMap);
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to refresh OCR noise:', err);
    }
  };

  const refreshUnexpectedBlanks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const blankRes = await fetch(`${API_BASE_URL}/assess/unexpected-blank-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!blankRes.ok) return;
      const blankData = await blankRes.json();
      const blankMap = new Map();
      for (const row of (blankData.data || [])) {
        if (row && row.rubric_id != null) blankMap.set(String(row.rubric_id), row);
      }
      setUnexpectedBlankMap(blankMap);
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to refresh unexpected blank pages:', err);
    }
  };

  const refreshLatePapers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/assess/late-papers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const map = new Map();
      for (const row of (data.data || [])) {
        if (row && row.rubric_id != null) map.set(String(row.rubric_id), row);
      }
      setLatePaperMap(map);
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to refresh late papers:', err);
    }
  };

  // Marks over max and unmatched label.
  // These read saved columns, so they start with the cards.
  const refreshMarkCardFlags = () => {
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };
    const take = async (url, apply) => {
      try {
        const res = await fetch(url, { headers, cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const map = new Map();
        for (const row of (data.data || [])) {
          if (row && row.rubric_id != null) map.set(String(row.rubric_id), row);
        }
        apply(map);
      } catch (err) {
        console.warn('[AnalyzeTab] Failed to fetch card flags:', err);
      }
    };
    take(`${API_BASE_URL}/assess/over-max-marks`, setOverMaxMarksMap);
    take(`${API_BASE_URL}/assess/thinking-in-marks`, setThinkingMarksMap);
    take(`${API_BASE_URL}/assess/unmatched-labels`, setUnmatchedLabelMap);
  };

  const refreshThinkingMarks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/assess/thinking-in-marks`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store'
      });
      if (!res.ok) return;
      const data = await res.json();
      const map = new Map();
      for (const row of (data.data || [])) {
        if (row && row.rubric_id != null) map.set(String(row.rubric_id), row);
      }
      setThinkingMarksMap(map);
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to refresh thinking text:', err);
    }
  };

  // Only the unmatched-label names. Dates, filters, cards and scroll stay as they are.
  const refreshUnmatchedLabels = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/assess/unmatched-labels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const map = new Map();
      for (const row of (data.data || [])) {
        if (row && row.rubric_id != null) map.set(String(row.rubric_id), row);
      }
      setUnmatchedLabelMap(map);
    } catch (err) {
      console.warn('[AnalyzeTab] Failed to refresh unmatched labels:', err);
    }
  };

  return (
    <div className="analyze-tab" style={calendarCollapsed ? { height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' } : undefined}>
      {/* AI-pipeline mutex banner — surfaces the backend's aiMutex state
          so the teacher sees why the Start Sorting button is disabled.
          Yellow tint, persistent until the daemon (or whoever's holding
          the slot) finishes. Source label says "Auto-sort daemon" for
          background runs and "Another teacher's sort" for manual ones. */}
      {sortBusy && (
        <div
          role="status"
          style={{
            marginBottom: '6px',
            padding: '5px 10px',
            background: 'linear-gradient(135deg, rgba(250,204,21,0.15), rgba(245,158,11,0.12))',
            border: '1px solid #facc15',
            borderRadius: 6,
            color: '#92400e',
            fontSize: 12,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <i className="fa-solid fa-triangle-exclamation" style={{ color: '#d97706', fontSize: 14 }} />
          <span style={{ flex: 1 }}>
            {aiStatus.sort.source === 'auto' ? 'Auto-sort daemon is running' : 'Another sort request is in progress'}
            {aiStatus.sort.label ? ` on ${aiStatus.sort.label}` : ''}. Start Sorting is disabled until it finishes.
          </span>
        </div>
      )}
      {/* Page header — matches SavedRubrics / ScansTab: 40×40 icon chip on
          the left, title/subtitle next to it, and the filter chips inline
          on the right of the same row. No more "Scans Folder" sub-label;
          the calendar below makes the context clear. */}
      <div style={{ marginBottom: '6px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '4px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '7px',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)'
          }}>
            <i className="fa-solid fa-list-check" style={{ color: 'white', fontSize: '13px' }}></i>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <h1 style={{
              color: 'var(--text-primary)',
              margin: 0,
              fontSize: '0.95rem',
              fontWeight: '700',
              lineHeight: 1.2
            }}>Sort Papers</h1>
            <p style={{
              margin: 0,
              color: 'var(--text-muted)',
              fontSize: '0.68rem',
              lineHeight: 1.2
            }}>Extract roll numbers and student names from odd-numbered answer sheets</p>
          </div>
          {/* Future Cards — top-row toggle: shows every folder dated tomorrow
              onwards, regardless of the calendar's selected day. */}
          <button
            onClick={() => { const next = !showFuture; setShowFuture(next); if (next) { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setSelectedCalendarDate(null); setRangeEndDate(null); setShowAllFolders(false); } }}
            title="Show only folders dated tomorrow onwards"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 8px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
              background: showFuture ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'var(--surface)',
              color: showFuture ? 'white' : 'var(--text-primary)',
              transition: 'all 0.2s'
            }}
          >
            <i className="fa-solid fa-forward"></i>
            {showFuture ? 'Future Cards · On' : 'Future Cards'}
            {futureFolderCount > 0 && (
              <span style={{
                minWidth: 14, height: 14, padding: '0 4px', borderRadius: 7,
                fontSize: 9, fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: showFuture ? 'rgba(255,255,255,0.25)' : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                color: 'white'
              }}>{futureFolderCount}</span>
            )}
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Teacher Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="fa-solid fa-user-tie" style={{ color: '#8B5CF6', fontSize: '0.8rem' }}></i>
              <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)' }}>Teacher:</label>
              <select
                value={filterTeacher}
                onChange={(e) => setFilterTeacher(e.target.value)}
                style={{
                  padding: '3px 6px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 12,
                  cursor: 'pointer',
                  minWidth: 120,
                  background: 'var(--surface)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="">All Teachers</option>
                {teacherOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Class Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="fa-solid fa-graduation-cap" style={{ color: '#3B82F6', fontSize: '0.8rem' }}></i>
              <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)' }}>Class:</label>
              <MultiCheckSelect
                value={filterClass}
                onChange={setFilterClass}
                options={classOptions}
                allLabel="All Classes"
                minWidth={130}
              />
            </div>

            {/* Subject Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="fa-solid fa-book" style={{ color: '#10b981', fontSize: '0.8rem' }}></i>
              <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)' }}>Subject:</label>
              <MultiCheckSelect
                value={filterSubject}
                onChange={setFilterSubject}
                options={subjectOptions}
                allLabel="All Subjects"
                minWidth={150}
              />
            </div>

            {/* All / Today shortcut — mirrors the same toggle on ScansTab.
                "All" clears the calendar selection; "Today" jumps to today
                (works on the existing selectedCalendarDate state). */}
            {(() => {
              const todayDate = new Date();
              todayDate.setHours(0, 0, 0, 0);
              const isToday = selectedCalendarDate && !rangeEndDate
                && new Date(selectedCalendarDate).setHours(0, 0, 0, 0) === todayDate.getTime();
              const isAll = showAllFolders && !selectedCalendarDate && !rangeEndDate && !showFuture;
              return (
                <div style={{ display: 'inline-flex', gap: 2, alignItems: 'center', background: 'var(--tab-bg, rgba(0,0,0,0.04))', padding: 2, borderRadius: 6 }}>
                  <button
                    onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setSelectedCalendarDate(null); setRangeEndDate(null); setShowFuture(false); setShowAllFolders(true); }}
                    style={{
                      padding: '3px 8px',
                      border: 'none',
                      borderRadius: 5,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 11,
                      background: isAll ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                      color: isAll ? 'white' : 'var(--text-primary)',
                      transition: 'all 0.2s'
                    }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setSelectedCalendarDate(todayDate); setRangeEndDate(null); setShowFuture(false); setShowAllFolders(false); }}
                    style={{
                      padding: '3px 8px',
                      border: 'none',
                      borderRadius: 5,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 11,
                      background: isToday ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                      color: isToday ? 'white' : 'var(--text-primary)',
                      transition: 'all 0.2s'
                    }}
                  >
                    Today
                  </button>
                </div>
              );
            })()}

            {/* Sorted + Status filters moved to the Batch/Model action row
                below the calendar — see the block right after the Model
                selector. The Clear button below still resets them. */}

            {(filterTeacher || filterClass.length > 0 || filterSubject.length > 0 || filterSorted !== 'all' || filterChecked !== 'all' || filterErrors !== 'all' || filterImageChanges || gradeFilter || assignmentWord) && (
              <button
                onClick={() => {
                  setFilterTeacher('');
                  setFilterClass([]);
                  setFilterSubject([]);
                  setFilterSorted('all');
                  setFilterChecked('all');
                  setFilterErrors('all');
                  setFilterImageChanges(false);
                  setGradeFilter(null);
                  setSectionFilter(null);
                  setSubjectPick(null);
                  setAssignmentWord(null);
                }}
                style={{
                  padding: '3px 8px',
                  border: 'none',
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  boxShadow: '0 1px 4px rgba(245, 158, 11, 0.3)'
                }}
              >
                <i className="fa-solid fa-filter-circle-xmark"></i>
                Clear
              </button>
            )}

            {/* Hide/Show folders toggle — pinned to the extreme RIGHT of
                the header row. Reuses calendarCollapsed which is also
                flipped by Import Sorted List + Change folder; this button
                just lets the user do it manually. */}
            <button
              onClick={() => setCalendarCollapsed(c => !c)}
              title={calendarCollapsed ? 'Show the calendar + folder picker + sort controls' : 'Hide the calendar, folder picker, and sort controls to give the table more room'}
              style={{
                marginLeft: 'auto',
                padding: '3px 8px',
                border: '1px solid var(--border-color, #d1d5db)',
                borderRadius: 5,
                background: 'var(--panel-bg, white)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '0.7rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                whiteSpace: 'nowrap'
              }}
            >
              <i className={`fa-solid fa-${calendarCollapsed ? 'eye' : 'eye-slash'}`}></i>
              {calendarCollapsed ? 'Show folders' : 'Hide folders'}
            </button>
          </div>
        </div>
      </div>

      {message.show && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="analyze-controls">
        {showFolderSection && (
        <div className="control-row">
          <div className="control-group">

            {/* ── Two-column layout: compact 30-day calendar (or its
                  collapsed summary bar) on the LEFT in a narrow rail;
                  folder list for the selected date on the RIGHT. The
                  collapsed-summary case takes the full row (width:100%)
                  so it doesn't get squished into the 290px rail. ───── */}
            <div className="analyze-cal-folders" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: '2px' }}>
            {/* Collapsed-folder summary bar removed — its content (folder
                name + date + path) is now folded into the action row below,
                and the redundant "Change folder" button is gone since the
                "Show folders" toggle in the page header re-expands. */}
            {!calendarCollapsed && (
            <div
              className="analyze-cal-rail"
              style={{
              background: 'var(--card-bg, white)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '8px',
              padding: '4px',
              marginTop: '2px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              width: 260,
              flexShrink: 0,
              boxSizing: 'border-box'
            }}>
              {/* Range header — "Select Date" title on the left, green
                  range pill on the right (matches the shared
                  DateFilterCalendar so ScansTab / SavedRubrics /
                  AnalyzeTab all look identical). */}
              {(() => {
                const winDays = getCalendarDays();
                const formatDay = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const first = winDays[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const last  = winDays[winDays.length - 1].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const pill = selectedCalendarDate && rangeEndDate
                  ? `${formatDay(selectedCalendarDate)} – ${formatDay(rangeEndDate)}`
                  : selectedCalendarDate
                    ? formatDay(selectedCalendarDate)
                    : `${first} – ${last}`;
                return (
                  <div style={{ marginBottom: '3px', padding: '0 2px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-primary)' }}>Date range</span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '1px 6px',
                          borderRadius: 8,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          background: selectedCalendarDate ? 'rgba(37,99,235,0.12)' : 'rgba(16,185,129,0.12)',
                          color: selectedCalendarDate ? '#1d4ed8' : '#15803d',
                          border: selectedCalendarDate ? '1px solid rgba(37,99,235,0.45)' : '1px solid rgba(16,185,129,0.45)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {pill}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.58rem', color: 'var(--text-secondary)', marginTop: 1 }}>
                      Click a start day, then an end day.
                    </div>
                  </div>
                );
              })()}

              {/* Day cells — 5 wide × 6 rows for the 30-day window
                  (matches the shared DateFilterCalendar). */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '3px' }}>
                {getCalendarDays().map((cell, idx) => {
                  const key = dateToKey(cell.date);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const isToday = dateToKey(today) === key;
                  const startKey = selectedCalendarDate ? dateToKey(selectedCalendarDate) : null;
                  const endKey = rangeEndDate ? dateToKey(rangeEndDate) : null;
                  const isEndpoint = !!(startKey && (key === startKey || key === endKey));
                  const isInRange = !!(startKey && endKey && key > startKey && key < endKey);
                  const count = getFolderCountForDate(cell.date);
                  const hasFolder = count > 0;
                  return (
                    <div
                      key={idx}
                      onClick={() => handleDateClick(cell.date)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px 0',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        background: isEndpoint
                          ? 'linear-gradient(135deg,#3b82f6,#2563eb)'
                          : isInRange
                          ? 'rgba(59,130,246,0.22)'
                          : isToday
                          ? 'rgba(59,130,246,0.07)'
                          : hasFolder
                          ? 'rgba(16,185,129,0.06)'
                          : 'transparent',
                        border: isEndpoint
                          ? '1.5px solid #2563eb'
                          : isInRange
                          ? '1px solid #93c5fd'
                          : isToday
                          ? '1.5px solid #93c5fd'
                          : hasFolder
                          ? '1px solid rgba(16,185,129,0.25)'
                          : '1px solid transparent',
                        opacity: cell.current ? 1 : 0.3,
                        transition: 'all 0.15s ease',
                        gap: '1px'
                      }}
                    >
                      {/* Day-of-week label — the dedicated header strip is
                          gone with the 30-day layout, so each cell carries
                          its own. */}
                      <span style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        color: isEndpoint ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary, #6b7280)'
                      }}>
                        {cell.date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>

                      {/* Date number only — month name dropped per user
                          request; the green range pill above already
                          communicates the month. */}
                      <span style={{
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        lineHeight: 1,
                        color: isEndpoint ? 'white' : 'var(--text-primary, #111827)'
                      }}>
                        {cell.date.getDate()}
                      </span>

                      {/* Folder count text */}
                      {hasFolder && (
                        <span style={{
                          fontSize: '0.68rem',
                          fontWeight: '600',
                          lineHeight: 1,
                          color: isEndpoint ? 'rgba(255,255,255,0.95)' : '#2563eb',
                          whiteSpace: 'nowrap'
                        }}>
                          {count}-F
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend + Clear Date button. The Clear Date pill appears
                  only when a calendar date is currently selected, matching
                  the ScansTab / shared DateFilterCalendar pattern. */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '3px', fontSize: '0.6rem', color: 'var(--text-secondary)', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}></span>
                  Has folders
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', background: 'rgba(59,130,246,0.07)', border: '1.5px solid #93c5fd' }}></span>
                  Today
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)' }}></span>
                  Start / end
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', background: 'rgba(59,130,246,0.22)', border: '1px solid #93c5fd' }}></span>
                  In range
                </span>
                {selectedCalendarDate && (
                  <button
                    type="button"
                    onClick={clearDateRange}
                    style={{
                      marginLeft: 'auto',
                      padding: '2px 6px',
                      border: 'none',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '0.58rem',
                      fontWeight: 700,
                      boxShadow: '0 1px 2px rgba(239, 68, 68, 0.3)'
                    }}
                    title="Clear date filter"
                  >
                    <i className="fas fa-times" style={{ marginRight: 3 }}></i>Clear Date
                  </button>
                )}
              </div>

              {/* Sorted + Status segmented filters — each on its own row under
                  the calendar. First option is labelled "None" (value 'all'). */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {(() => {
                  const rowStyle = {
                    display: 'flex',
                    alignItems: 'stretch',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    overflow: 'hidden',
                    background: 'var(--surface)',
                    width: '100%',
                    height: 32,
                    boxSizing: 'border-box'
                  };
                  const noneStyle = (active) => ({
                    flex: '0 0 72px',
                    width: 72,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    borderRight: '1px solid var(--border)',
                    background: active ? 'rgba(16,185,129,0.12)' : 'transparent',
                    boxShadow: active ? 'inset 0 0 0 1px rgba(16,185,129,0.45)' : 'none',
                    color: active ? '#15803d' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 700
                  });
                  const choiceStyle = (active) => ({
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    border: 'none',
                    background: active ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'transparent',
                    color: active ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  });
                  return (
                    <>
                      <div style={rowStyle}>
                        <button type="button" onClick={() => setFilterSorted('all')} style={noneStyle(filterSorted === 'all')}>None</button>
                        {[['sorted', 'Sorted'], ['unsorted', 'Unsorted']].map(([v, label]) => (
                          <button key={v} type="button" onClick={() => setFilterSorted(v)} style={choiceStyle(filterSorted === v)}>{label}</button>
                        ))}
                      </div>
                      <div style={rowStyle}>
                        <button type="button" onClick={() => setFilterChecked('all')} style={noneStyle(filterChecked === 'all')}>None</button>
                        {[['checked', 'Checked'], ['unchecked', 'Unchecked']].map(([v, label]) => (
                          <button key={v} type="button" onClick={() => setFilterChecked(v)} style={choiceStyle(filterChecked === v)}>{label}</button>
                        ))}
                      </div>
                      <div style={rowStyle}>
                        <button type="button" onClick={() => setFilterErrors('all')} title="Show every folder" style={noneStyle(filterErrors === 'all')}>None</button>
                        <button type="button" onClick={() => setFilterErrors('errors')} title="Show only folders with Unmatched label, Bad OCR, Unexpected blank page, Late papers, Marks over max, Thinking in marks, or Check Failed" style={choiceStyle(filterErrors === 'errors')}>Errors</button>
                        <button type="button" onClick={() => setFilterErrors('regrade')} title="Show only folders with a Re-Grade request" style={choiceStyle(filterErrors === 'regrade')}>Re-Grade</button>
                      </div>
                      <div style={rowStyle}>
                        <button
                          type="button"
                          onClick={() => { if (filterErrors === 'late') setFilterErrors('all'); }}
                          title="Show every folder"
                          style={noneStyle(filterErrors !== 'late')}
                        >
                          None
                        </button>
                        <button
                          type="button"
                          onClick={() => setFilterErrors('late')}
                          title="Show only folders with a Late papers chip."
                          style={choiceStyle(filterErrors === 'late')}
                        >
                          Late papers
                        </button>
                      </div>
                      <div style={rowStyle}>
                        <button
                          type="button"
                          onClick={() => setFilterImageChanges(false)}
                          title="Show every folder"
                          style={noneStyle(!filterImageChanges)}
                        >
                          None
                        </button>
                        <button
                          type="button"
                          onClick={() => setFilterImageChanges(true)}
                          title="Show only folders with an Images added or Images deleted chip."
                          style={choiceStyle(filterImageChanges)}
                        >
                          Images +/-
                        </button>
                        <button
                          type="button"
                          onClick={openReLateLog}
                          title="Dated log of every image added or deleted from now on."
                          style={choiceStyle(false)}
                        >
                          Recheck logs
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div style={{ marginTop: 4, width: '100%', height: 32, display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  UrOcr model
                </label>
                <select
                  value={urdu1Model}
                  onChange={(e) => setUrdu1Model(e.target.value)}
                  title="Gemini model used when UrOcr is clicked on a folder"
                  style={{ flex: 1, minWidth: 0, height: 32, boxSizing: 'border-box', padding: '0 6px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: '1px solid var(--border-color, #e0e0e0)', background: 'var(--card-bg, #fff)', color: 'var(--text-primary)' }}
                >
                  {URDU1_GEMINI_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Auto-sort master switch sits under the UrOcr model row.
                  Per-folder Start on each card is what queues a folder. */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap',
                  height: 32, boxSizing: 'border-box',
                  padding: '0 6px', marginTop: 4, borderRadius: 6,
                  border: `1px solid ${autoSortEnabled ? '#2563eb' : 'var(--border-color, #e0e0e0)'}`,
                  background: autoSortEnabled ? 'rgba(37,99,235,0.06)' : 'var(--card-bg, #fafafa)'
                }}
              >
                <button
                  type="button"
                  onClick={handleToggleAutoSort}
                  disabled={autoSortToggleBusy}
                  title={autoSortEnabled
                    ? 'Auto-sort daemon is ON. It only sorts folders where you clicked Start. Turn off to stop picking new folders.'
                    : 'Turn ON the auto-sort daemon. Then click Start on a folder when you want that folder sorted.'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    height: 26, boxSizing: 'border-box',
                    padding: '0 8px', borderRadius: 5, border: 'none',
                    cursor: autoSortToggleBusy ? 'wait' : 'pointer',
                    fontWeight: 700, fontSize: '0.72rem', color: 'white',
                    opacity: autoSortToggleBusy ? 0.7 : 1,
                    background: autoSortEnabled
                      ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                      : 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                  }}
                >
                  <i className={`fa-solid ${autoSortEnabled ? 'fa-stop' : 'fa-robot'}`}></i>
                  {autoSortEnabled ? 'Stop Auto-Sort' : 'Start Auto-Sort'}
                </button>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, flex: 1 }}>
                  {autoSortEnabled ? (
                    <>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                        background: autoSortDaemonBusy ? '#f59e0b' : '#2563eb',
                        boxShadow: autoSortDaemonBusy ? '0 0 4px #f59e0b' : '0 0 4px #2563eb'
                      }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {autoSortDaemonBusy
                          ? (aiStatus?.sort?.label
                            ? `Sorting ${aiStatus.sort.label}…`
                            : 'Sorting a folder…')
                          : 'Click Start on a folder when you are ready.'}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontStyle: 'italic' }}>Auto-sort daemon is off. Click Start on a folder to turn it on.</span>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Class search sits beside the calendar. Cards appear after a
                class button or a calendar day. Hide folders still hides both. */}
            {!calendarCollapsed && (
            <div style={{ flex: 1, minWidth: 280, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <ClassSectionFilters
              items={visibleFolders}
              itemNoun="folders"
              gradeFilter={gradeFilter}
              sectionFilter={sectionFilter}
              subjectFilter={subjectPick}
              assignmentWord={assignmentWord}
              onAssignmentWord={setAssignmentWord}
              onGrade={(grade) => {
                setSubjectPick(null);
                setSectionFilter(null);
                if (gradeFilter === grade) {
                  setGradeFilter(null);
                } else {
                  setGradeFilter(grade);
                  setSelectedCalendarDate(null);
                  setRangeEndDate(null);
                  setShowAllFolders(false);
                  setShowFuture(false);
                }
              }}
              onSection={(section) => {
                const next = sectionFilter === section ? null : section;
                setSectionFilter(next);
                if (subjectPick && !itemsInClass(visibleFolders, gradeFilter, next).some((folder) => (folder.subject || '').trim() === subjectPick)) {
                  setSubjectPick(null);
                }
              }}
            />
            {(showFuture || selectedCalendarDate || showAllFolders || gradeFilter) && (
            <div className="analyze-folders-pane" style={{ minWidth: 0 }}>
              <div style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'var(--text-secondary)',
                marginBottom: '6px'
              }}>
                <i className="fa-solid fa-folder-open" style={{ color: '#f59e0b', marginRight: '6px' }}></i>
                {getFilteredFolders().length > 0
                  ? `${getFilteredFolders().length} folder${getFilteredFolders().length !== 1 ? 's' : ''}`
                  : 'No folders'}
                {gradeFilter ? (
                  <span style={{ marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted, #94a3b8)' }}>
                    · {sectionFilter || `Class ${gradeFilter}`}{subjectPick ? ` · ${subjectPick}` : ''}{assignmentWord ? ` · ${assignmentWord.charAt(0).toUpperCase()}${assignmentWord.slice(1)}` : ''}
                  </span>
                ) : showFuture ? (
                  <span style={{ marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted, #94a3b8)' }}>
                    · From tomorrow onwards
                  </span>
                ) : selectedCalendarDate && rangeEndDate ? (
                  <span style={{ marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted, #94a3b8)' }}>
                    · {new Date(selectedCalendarDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(rangeEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                ) : selectedCalendarDate ? (
                  <span style={{ marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted, #94a3b8)' }}>
                    · {new Date(selectedCalendarDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                ) : (
                  <span style={{ marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted, #94a3b8)' }}>
                    · All dates
                  </span>
                )}
              </div>
                {getFilteredFolders().length === 0 ? (
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{gradeFilter ? 'No folders for this class.' : showFuture ? 'No upcoming folders (tomorrow onwards).' : rangeEndDate ? 'No folders in this date range.' : selectedCalendarDate ? 'No folders for this date.' : 'No folders.'}</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                    {getFilteredFolders().map(folder => {
                      const isSel = selectedFolderId === String(folder.id);
                      // Status priority mirrors ScansTab so a folder reads the
                      // same way wherever it appears:
                      //   selected   → blue (existing behavior)
                      //   empty      → red (no images uploaded yet — the user
                      //                asked for this explicit flag)
                      //   printed    → blue tint, blue border
                      //   done       → green tint, green border
                      //   default    → neutral card
                      const isEmpty = !folder.scan_count || folder.scan_count === 0;
                      // Local folder vs website image counts disagree — the
                      // upload didn't fully land (some sheets missing/failed/
                      // bad-named). Flagged RED; auto-sort is paused until fixed.
                      const isUploadMismatch = uploadMismatchIds.has(String(folder.id));
                      // Odd image count → a page is likely missing (answer
                      // sheets come in pairs), so flag the count in red.
                      const isOddCount = !isEmpty && (folder.scan_count % 2 !== 0);
                      const isPrinted = !!folder.printed_at;
                      const isDone = !isPrinted && !!folder.completed_at;
                      // Snapshot indicator — green bold title + a 📌 badge
                      // when this folder has a saved sorted list pinned to
                      // it. Tells the teacher at a glance which folders are
                      // ready for instant import (no AI re-sort needed).
                      const hasSnapshot = snapshotFolderIdSet.has(String(folder.id));
                      // Selected folder shows in amber/yellow so it stands
                      // out from the green "done" and blue "printed" status
                      // tints (user wanted a visually distinct selected
                      // state). Empty / printed / done colors are unchanged.
                      // A count mismatch outranks every state except the
                      // user's own selection, so the red flag is never hidden
                      // by a printed/done tint.
                      const borderColor = isSel
                        ? '#F59E0B'
                        : isUploadMismatch
                          ? '#DC2626'
                          : isEmpty
                            ? '#EF4444'
                            : isPrinted
                              ? '#2196F3'
                              : isDone
                                ? '#28a745'
                                : null;
                      const bgColor = isSel
                        ? 'rgba(245, 158, 11, 0.14)'
                        : isUploadMismatch
                          ? 'rgba(220, 38, 38, 0.12)'
                          : isEmpty
                            ? 'rgba(239, 68, 68, 0.08)'
                            : isPrinted
                              ? 'rgba(33, 150, 243, 0.08)'
                              : isDone
                                ? 'rgba(40, 167, 69, 0.08)'
                                : 'var(--card-bg, white)';
                      const cardBorder = borderColor
                        ? `3px solid ${borderColor}`
                        : '1px solid var(--border-color, #e0e0e0)';
                      // The five auto-check step boxes (Run, Save, Scans,
                      // Approve, Absent) only render once a folder is past
                      // "saved". Result sits on the English / Urdu / UrOG row.
                      const stepInfoForBoxes = autoCheckStepsMap.get(String(folder.id)) || {};
                      const stepValsForBoxes = Object.values(stepInfoForBoxes.steps || {});
                      const snapStForBoxes = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                      const showReAutoForBoxes = isPrinted
                        || snapStForBoxes === 'error'
                        || snapStForBoxes === 'auto-checked'
                        || snapStForBoxes === 'copy_to_edupage'
                        || snapStForBoxes === 'completed'
                        || stepValsForBoxes.some(s => s && s.status === 'error')
                        || !!(stepInfoForBoxes.by && stepValsForBoxes.some(s => s && s.status === 'running'))
                        || !!(stepInfoForBoxes.regrade_at && !stepInfoForBoxes.by);
                      const stepBoxesVisible = hasSnapshot && !(snapStForBoxes === 'saved' && !showReAutoForBoxes);
                      const renderResultButton = () => {
                        const verified = !!(folderReviews.verified && folderReviews.verified[String(folder.id)]);
                        const sheetMeta = assessSnapshotMetaMap.get(String(folder.id));
                        const sheetStudents = sheetMeta ? Number(sheetMeta.record_count) || 0 : null;
                        const sheetCount = sheetStudents == null
                          ? 'No saved result sheet.'
                          : `${sheetStudents} student${sheetStudents === 1 ? '' : 's'} in the result sheet.`;
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setReportFolder(folder);
                            }}
                            title={verified
                              ? `${sheetCount} Result is marked OK. Open the result sheet.`
                              : `${sheetCount} Open the result sheet. The label stays orange until Result is OK is clicked.`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '3px 6px',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              border: '1px solid var(--border-color, #d1d5db)',
                              borderRadius: 6,
                              background: 'transparent',
                              color: verified ? '#15803d' : '#ea580c',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            Result
                          </button>
                        );
                      };
                      return (
                        <div
                          key={folder.id}
                          data-edu-card="1"
                          onClick={() => handleFolderClickFromCalendar(folder)}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '14px',
                            borderRadius: '12px',
                            border: cardBorder,
                            background: bgColor,
                            cursor: 'pointer',
                            boxShadow: isSel ? '0 4px 12px rgba(245,158,11,0.25)' : '0 2px 8px rgba(0,0,0,0.08)',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            zIndex: (overMaxHoverId === String(folder.id) || thinkingMarksHoverId === String(folder.id) || unmatchedHoverId === String(folder.id) || ocrNoiseHoverId === String(folder.id) || ocrNoiseHoverId === `chatter:${folder.id}` || unexpectedBlankHoverId === String(folder.id) || latePaperHoverId === String(folder.id) || checkErrorHoverId === String(folder.id) || (regradeHoverId && String(regradeHoverId).startsWith(`${folder.id}:`))) ? 8 : 'auto'
                          }}
                          onMouseEnter={(e) => { if (!isSel) { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)'; } }}
                          onMouseLeave={(e) => { if (!isSel) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; } }}
                        >
                          {/* Status pills (Printed / Empty) — moved out
                              of the absolute-positioned top-right slot
                              and into the title row alongside Sorted and
                              Checked, so they no longer overlap long
                              folder names. The <span>s render inside the
                              title flex row below. */}
                          {/* Info — flex column so the status tags can be
                              dropped to the bottom (below the medium buttons)
                              with order:99 instead of physically reordering. */}
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div style={{
                              fontWeight: hasSnapshot ? 800 : 700,
                              fontSize: '0.92rem',
                              color: isSel ? '#B45309' : (hasSnapshot ? '#059669' : 'var(--text-primary)'),
                              marginBottom: '6px',
                              wordBreak: 'break-word',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              flexWrap: 'wrap'
                            }}>
                              {folderDisplayTitle(folder)}
                              {isUploadMismatch && (
                                <span
                                  role="button"
                                  onClick={(e) => uploadFromMismatchTag(e, folder)}
                                  title={autoSortStatusMap.get(String(folder.id))?.last_error
                                    || 'Local folder and website image counts differ. Click to start the folder-server if it is stopped, then upload this folder.'}
                                  style={{
                                    fontSize: '0.6rem', fontWeight: 800, color: '#fff',
                                    background: '#DC2626', borderRadius: '5px',
                                    padding: '2px 6px', whiteSpace: 'nowrap', letterSpacing: '0.02em',
                                    cursor: 'pointer'
                                  }}
                                >
                                  ⚠ LOCAL ≠ WEB Click here
                                </span>
                              )}
                            </div>
                            {/* Assignment name — kept directly after the title in
                                DOM order so a manual drag-select reads
                                title → assignment name (the status badges now sit
                                last in the card, so selection follows visual order). */}
                            {folder.assignment_name && (
                              <div
                                title={`Assignment: ${folder.assignment_name}`}
                                style={{ fontSize: '0.76rem', color: '#1D4ED8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px', minWidth: 0 }}
                              >
                                <i className="fa-solid fa-tag" style={{ width: '12px', color: '#2563EB' }}></i>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {folder.assignment_name}
                                </span>
                              </div>
                            )}
                            {folder.test_date && (
                              <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                                <i className="fa-solid fa-calendar-day" style={{ width: '12px', color: '#6b7280' }}></i>
                                <span style={{ whiteSpace: 'nowrap' }}>
                                  {new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            )}
                            <div
                              title={isEmpty
                                ? 'No scanned images on the server yet'
                                : isOddCount
                                  ? `${folder.scan_count} scanned images — odd number, a page may be missing`
                                  : `${folder.scan_count} scanned images on the server`}
                              style={{ fontSize: '0.76rem', color: (isEmpty || isOddCount) ? '#DC2626' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: (isEmpty || isOddCount) ? 700 : 400, width: '100%' }}>
                              <i className="fa-solid fa-file-image" style={{ width: '12px', color: (isEmpty || isOddCount) ? '#DC2626' : '#6b7280' }}></i>
                              {folder.scan_count} image{folder.scan_count !== 1 ? 's' : ''}
                              {Array.isArray(folder.ocr_missing) && folder.ocr_missing.length > 0 && (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const first = folder.ocr_missing.find((item) => item && String(item.student || '').trim());
                                    if (!first) return;
                                    const pages = parseMissingPages(first.pages).map(String);
                                    setQuickTranscript({
                                      folderPath: `rubrics/${folder.id}`,
                                      name: String(first.student).trim(),
                                      page: pages[0] || '',
                                      pages,
                                      list: 'missing',
                                      refresh: 'missing'
                                    });
                                  }}
                                  title={ocrMissingFolderTitle(folder.ocr_missing) || 'Open the transcript for the first student with OCR missing'}
                                  style={{
                                    marginLeft: 4,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '1px 7px',
                                    borderRadius: 10,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    background: '#fff7ed',
                                    color: '#c2410c',
                                    border: '1px solid #fdba74',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  ocr missing
                                </span>
                              )}
                              {/* URDU folders: annotate with the typed-answer .docx count
                                  from the local watcher. Green when every image has its
                                  .docx (scan_count === docx → ready to auto-check), amber
                                  while Google Drive is still syncing them in. */}
                              {autoCheckMediumMap.get(String(folder.id)) === 'urdu' && (() => {
                                const info = docxReadyMap.get(String(folder.id));
                                if (!info || info.error != null) return null;
                                const ready = info.docx === folder.scan_count;
                                return (
                                  <span
                                    style={{ marginLeft: 4, fontWeight: 700, color: ready ? '#16a34a' : '#d97706', whiteSpace: 'nowrap' }}
                                    title={ready
                                      ? 'All typed-answer .docx are present — ready to auto-check.'
                                      : `Waiting for typed-answer .docx: ${info.docx}/${folder.scan_count} synced. Auto-check holds until they match.`}
                                  >
                                    · {ready ? `${info.docx} docx` : `${info.docx}/${folder.scan_count} docx`}
                                  </span>
                                );
                              })()}
                            </div>
                            {folder.created_by && (
                              <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '3px' }}>
                                <i className="fa-solid fa-user" style={{ width: '12px', color: '#6b7280' }}></i>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.created_by}</span>
                              </div>
                            )}
                            {isUploadMismatch && (
                              <button
                                onClick={(e) => removeWebsiteExtras(e, folder)}
                                disabled={removingExtrasIds.has(String(folder.id))}
                                title="Delete website images that are not in this folder on disk. The local folder is not changed."
                                style={{
                                  marginTop: 6,
                                  alignSelf: 'flex-start',
                                  padding: '3px 8px',
                                  fontSize: '0.68rem',
                                  fontWeight: 800,
                                  border: '1px solid #DC2626',
                                  borderRadius: 6,
                                  background: '#FEF2F2',
                                  color: '#991B1B',
                                  cursor: removingExtrasIds.has(String(folder.id)) ? 'wait' : 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {removingExtrasIds.has(String(folder.id)) ? 'Removing…' : 'Remove extras'}
                              </button>
                            )}
                            {(() => {
                              const stepInfo = autoCheckStepsMap.get(String(folder.id)) || {};
                              const steps = stepInfo.steps || {};
                              const assessMeta = assessSnapshotMetaMap.get(String(folder.id));
                              const runAt = stepInfo.run_at || (steps.run && steps.run.at) || null;
                              const saveAt = (steps.save && steps.save.at) || null;
                              const checkedAt = (assessMeta && assessMeta.saved_at) || saveAt || runAt;
                              const snapSt = snapshotFolderStatusMap.get(String(folder.id));
                              const approvedAt = (steps.approve && steps.approve.status === 'done' && steps.approve.at)
                                || ((snapSt === 'copy_to_edupage' || snapSt === 'completed' || isPrinted || isDone)
                                  && (folder.completed_at || folder.printed_at))
                                || null;
                              const gradingRunning = !!(steps.run && steps.run.status === 'running');
                              const checkedLabel = gradingRunning ? 'Grading started' : 'Last checked';
                              const checkedValue = gradingRunning ? (runAt || checkedAt) : checkedAt;
                              const checkedStr = formatFolderPkt(checkedValue);
                              const approvedStr = formatFolderPkt(approvedAt);
                              const startedStr = formatFolderPkt(runAt);
                              if (!checkedStr && !approvedStr) return null;
                              return (
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {checkedStr && (
                                    <div
                                      title={startedStr && startedStr !== checkedStr
                                        ? `Grading started ${startedStr} (same clock as User History)`
                                        : 'Last time this folder was graded — same clock as Last Assessed on the Check tab and Grading Started in User History'}
                                      style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                    >
                                      <i className="fa-solid fa-clock" style={{ width: '12px', color: '#059669' }}></i>
                                      <span style={{ whiteSpace: 'nowrap' }}>{checkedLabel} {checkedStr}</span>
                                    </div>
                                  )}
                                  {approvedStr && (
                                    <div
                                      title="Last time this folder was approved into the student list (Results Finalized in User History)"
                                      style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                    >
                                      <i className="fa-solid fa-circle-check" style={{ width: '12px', color: '#2563eb' }}></i>
                                      <span style={{ whiteSpace: 'nowrap' }}>Last approved {approvedStr}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const unmatched = unmatchedLabelMap.get(String(folder.id));
                              const students = unmatched && Array.isArray(unmatched.students) ? unmatched.students : [];
                              if (!students.length) return null;
                              const oneLabel = students.length === 1
                                && Array.isArray(students[0].labels)
                                && students[0].labels.length === 1
                                ? students[0].labels[0]
                                : '';
                              const tag = oneLabel
                                ? `Unmatched label · ${oneLabel}`
                                : `Unmatched label${students.length > 1 ? ` (${students.length})` : ''}`;
                              const title = students
                                .map((s) => `${s.name} — ${(Array.isArray(s.labels) ? s.labels : []).join(', ')}`)
                                .join(', ');
                              const openCheck = (e) => {
                                e.stopPropagation();
                                navigate('/evaluate/check', {
                                  state: { autoOpenFolder: {
                                    folder_path: `rubrics/${folder.id}`,
                                    title: folderDisplayTitle(folder),
                                    class: folder.class || '',
                                    subject: folder.subject || ''
                                  } }
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setUnmatchedHoverId(String(folder.id))}
                                  onMouseLeave={() => setUnmatchedHoverId((current) => (
                                    current === String(folder.id) ? null : current
                                  ))}
                                  onClick={openCheck}
                                  title={title}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: 'rgba(220, 38, 38, 0.1)',
                                      border: '1px solid #DC2626',
                                      color: '#991B1B',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {tag}
                                  </div>
                                  {unmatchedHoverId === String(folder.id) && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FECACA',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 180,
                                        maxWidth: 280,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        color: '#7F1D1D',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {students.map((student) => (
                                        <button
                                          key={student.name}
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setQuickTranscript({
                                              mode: 'comment',
                                              folderPath: `rubrics/${folder.id}`,
                                              name: student.name,
                                              refresh: 'unmatched'
                                            });
                                          }}
                                          title={`Open the comments, scan, transcript and rubric for ${student.name}`}
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            width: '100%',
                                            padding: '2px 0',
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'inherit',
                                            font: 'inherit',
                                            fontWeight: 700,
                                            textAlign: 'left',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <span>{student.name}</span>
                                          <span style={{ fontWeight: 600, color: '#B91C1C', whiteSpace: 'nowrap' }}>
                                            {(Array.isArray(student.labels) ? student.labels : []).join(', ')}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const noise = ocrNoiseMap.get(String(folder.id));
                              const students = (noise && Array.isArray(noise.students) ? noise.students : [])
                                .map((student) => ({
                                  ...student,
                                  notes: (Array.isArray(student.notes) ? student.notes : []).filter((note) => !isSuspectedNote(note))
                                }))
                                .filter((student) => student.notes.length);
                              if (!students.length) return null;
                              const tag = students.length > 1 ? `Bad OCR (${students.length})` : 'Bad OCR';
                              const openCheck = (e) => {
                                e.stopPropagation();
                                navigate('/evaluate/check', {
                                  state: { autoOpenFolder: {
                                    folder_path: `rubrics/${folder.id}`,
                                    title: folderDisplayTitle(folder),
                                    class: folder.class || '',
                                    subject: folder.subject || ''
                                  } }
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setOcrNoiseHoverId(String(folder.id))}
                                  onMouseLeave={() => setOcrNoiseHoverId((current) => (
                                    current === String(folder.id) ? null : current
                                  ))}
                                  onClick={openCheck}
                                  title={students.map((s) => `${s.name} — ${(Array.isArray(s.notes) ? s.notes : []).join(', ')}`).join('\n')}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: 'rgba(220, 38, 38, 0.1)',
                                      border: '1px solid #DC2626',
                                      color: '#991B1B',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {tag}
                                  </div>
                                  {ocrNoiseHoverId === String(folder.id) && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FECACA',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 200,
                                        maxWidth: 320,
                                        maxHeight: 220,
                                        overflowY: 'auto',
                                        color: '#7F1D1D',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {students.map((student) => (
                                        <div key={student.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                          <span>{student.name}</span>
                                          <span style={{ fontWeight: 600, color: '#B91C1C', whiteSpace: 'nowrap' }}>
                                            {(Array.isArray(student.notes) ? student.notes : []).join(', ')}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const noise = ocrNoiseMap.get(String(folder.id));
                              const students = noise && Array.isArray(noise.students) ? noise.students : [];
                              const rows = [];
                              for (const student of students) {
                                const pages = suspectedPagesFromNotes(student.notes);
                                for (const page of pages) {
                                  rows.push({ name: student.name, page: page.page, label: page.label, kind: page.kind });
                                }
                              }
                              if (!rows.length) return null;
                              const thinkingOnly = rows.every((row) => row.kind === 'thinking');
                              const names = new Set(rows.map((row) => row.name));
                              const count = names.size;
                              const tag = thinkingOnly
                                ? (count > 1 ? `Thinking Text (${count})` : 'Thinking Text')
                                : (count > 1 ? `Suspected Chatter (${count})` : 'Suspected Chatter');
                              const first = rows[0];
                              const openChatter = (e) => {
                                e.stopPropagation();
                                if (!first || !first.name) return;
                                setOcrNoiseHoverId(null);
                                setQuickTranscript({
                                  folderPath: `rubrics/${folder.id}`,
                                  name: first.name,
                                  page: first.label,
                                  pages: [first.label],
                                  queue: rows.map((item) => ({
                                    name: item.name,
                                    pages: [item.label]
                                  })),
                                  refresh: 'chatter'
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setOcrNoiseHoverId(`chatter:${folder.id}`)}
                                  onMouseLeave={() => setOcrNoiseHoverId((current) => (
                                    current === `chatter:${folder.id}` ? null : current
                                  ))}
                                  onClick={(e) => e.stopPropagation()}
                                  title={rows.map((row) => `${row.name} — ${row.label}`).join('\n')}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    onClick={openChatter}
                                    title={`Open page ${first.page} for ${first.name}`}
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: 'rgba(245, 158, 11, 0.15)',
                                      border: '1px solid #D97706',
                                      color: '#92400E',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {tag}
                                  </div>
                                  {ocrNoiseHoverId === `chatter:${folder.id}` && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FCD34D',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 220,
                                        maxWidth: 340,
                                        maxHeight: 220,
                                        overflowY: 'auto',
                                        color: '#92400E',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {rows.map((row) => (
                                        <div
                                          key={`${row.name}-${row.page}`}
                                          style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
                                        >
                                          <span>{row.name}</span>
                                          <span style={{ fontWeight: 600, color: '#B45309', whiteSpace: 'nowrap' }}>{row.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const blank = unexpectedBlankMap.get(String(folder.id));
                              const students = blank && Array.isArray(blank.students) ? blank.students : [];
                              if (!students.length) return null;
                              const tag = students.length > 1
                                ? `Unexpected blank page (${students.length})`
                                : 'Unexpected blank page';
                              const first = students[0];
                              const openPaper = (e) => {
                                e.stopPropagation();
                                if (!first || !first.name) return;
                                const pages = Array.isArray(first.pages) ? first.pages : [];
                                setUnexpectedBlankHoverId(null);
                                setQuickTranscript({
                                  folderPath: `rubrics/${folder.id}`,
                                  name: first.name,
                                  page: pages[0] || '',
                                  pages,
                                  queue: students.map((item) => ({
                                    name: item.name,
                                    pages: Array.isArray(item.pages) ? item.pages : []
                                  })),
                                  refresh: 'blank',
                                  list: 'blank'
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setUnexpectedBlankHoverId(String(folder.id))}
                                  onMouseLeave={() => setUnexpectedBlankHoverId((current) => (
                                    current === String(folder.id) ? null : current
                                  ))}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    onClick={openPaper}
                                    title={first && first.name ? `Open the transcript for ${first.name}` : 'Open the transcript'}
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: 'rgba(220, 38, 38, 0.1)',
                                      border: '1px solid #DC2626',
                                      color: '#991B1B',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {tag}
                                  </div>
                                  {unexpectedBlankHoverId === String(folder.id) && (
                                    <div
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FECACA',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 200,
                                        maxWidth: 320,
                                        maxHeight: 220,
                                        overflowY: 'auto',
                                        color: '#7F1D1D',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {students.map((student) => (
                                        <button
                                          key={student.name}
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const pages = Array.isArray(student.pages) ? student.pages : [];
                                            setQuickTranscript({
                                              folderPath: `rubrics/${folder.id}`,
                                              name: student.name,
                                              page: pages[0] || '',
                                              pages,
                                              queue: students.map((item) => ({
                                                name: item.name,
                                                pages: Array.isArray(item.pages) ? item.pages : []
                                              })),
                                              refresh: 'blank',
                                              list: 'blank'
                                            });
                                          }}
                                          title={`Open the OCR transcript for ${student.name}`}
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            width: '100%',
                                            padding: '2px 0',
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'inherit',
                                            font: 'inherit',
                                            fontWeight: 700,
                                            textAlign: 'left',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <span>{student.name}</span>
                                          <span style={{ fontWeight: 600, color: '#B91C1C', whiteSpace: 'nowrap' }}>
                                            {(Array.isArray(student.pages) ? student.pages : []).join(', ')}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const late = latePaperMap.get(String(folder.id));
                              const students = late && Array.isArray(late.students) ? late.students : [];
                              if (!students.length) return null;
                              const firstName = students[0] && students[0].name;
                              const openPaper = (e) => {
                                e.stopPropagation();
                                if (!firstName) return;
                                setLatePaperHoverId(null);
                                setQuickTranscript({
                                  mode: 'late',
                                  list: 'late',
                                  refresh: 'late',
                                  folderPath: `rubrics/${folder.id}`,
                                  name: firstName
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setLatePaperHoverId(String(folder.id))}
                                  onMouseLeave={() => setLatePaperHoverId((current) => (
                                    current === String(folder.id) ? null : current
                                  ))}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    onClick={openPaper}
                                    title={firstName ? `Open the check paper for ${firstName}` : 'Open the late paper'}
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: '#7c3aed',
                                      border: 'none',
                                      color: '#fff',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {`Late papers (${students.length})`}
                                  </div>
                                  {latePaperHoverId === String(folder.id) && (
                                    <div
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FDE68A',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 200,
                                        maxWidth: 320,
                                        maxHeight: 220,
                                        overflowY: 'auto',
                                        color: '#92400E',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {students.map((student) => (
                                        <div key={student.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                          <span>{student.name}</span>
                                          <span style={{ fontWeight: 600, color: '#B45309', whiteSpace: 'nowrap' }}>
                                            {student.sheets ? `${student.sheets} sheet${student.sheets === 1 ? '' : 's'}` : ''}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const over = overMaxMarksMap.get(String(folder.id));
                              const students = over && Array.isArray(over.students) ? over.students : [];
                              if (!students.length) return null;
                              const openCheck = (e) => {
                                e.stopPropagation();
                                navigate('/evaluate/check', {
                                  state: { autoOpenFolder: {
                                    folder_path: `rubrics/${folder.id}`,
                                    title: folderDisplayTitle(folder),
                                    class: folder.class || '',
                                    subject: folder.subject || ''
                                  } }
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setOverMaxHoverId(String(folder.id))}
                                  onMouseLeave={() => setOverMaxHoverId((current) => (
                                    current === String(folder.id) ? null : current
                                  ))}
                                  onClick={openCheck}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: 'rgba(220, 38, 38, 0.1)',
                                      border: '1px solid #DC2626',
                                      color: '#991B1B',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Marks &gt; max{students.length > 1 ? ` (${students.length})` : ''}
                                  </div>
                                  {overMaxHoverId === String(folder.id) && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FECACA',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 180,
                                        maxWidth: 280,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        color: '#7F1D1D',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {students.map((student) => (
                                        <div key={student.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                          <span>{student.name}</span>
                                          {Array.isArray(student.scores) && student.scores.length > 0 && (
                                            <span style={{ fontWeight: 600, color: '#B91C1C', whiteSpace: 'nowrap' }}>
                                              {student.scores.join(', ')}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const thinking = thinkingMarksMap.get(String(folder.id));
                              const students = thinking && Array.isArray(thinking.students) ? thinking.students : [];
                              if (!students.length) return null;
                              const tag = students.length > 1 ? `Thinking in marks (${students.length})` : 'Thinking in marks';
                              const first = students[0];
                              const queue = students.map((item) => ({ name: item.name, pages: [] }));
                              const openPaper = (student) => {
                                if (!student || !student.name) return;
                                setThinkingMarksHoverId(null);
                                setQuickTranscript({
                                  folderPath: `rubrics/${folder.id}`,
                                  name: student.name,
                                  page: '',
                                  pages: [],
                                  queue,
                                  refresh: 'thinking',
                                  list: 'thinking'
                                });
                              };
                              return (
                                <div
                                  onMouseEnter={() => setThinkingMarksHoverId(String(folder.id))}
                                  onMouseLeave={() => setThinkingMarksHoverId((current) => (
                                    current === String(folder.id) ? null : current
                                  ))}
                                  style={{ position: 'relative', width: 'fit-content', marginTop: 6 }}
                                >
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPaper(first);
                                    }}
                                    title={first && first.name ? `Open the transcript for ${first.name}` : 'Open the transcript'}
                                    style={{
                                      padding: '3px 7px',
                                      borderRadius: 6,
                                      background: 'rgba(220, 38, 38, 0.1)',
                                      border: '1px solid #DC2626',
                                      color: '#991B1B',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      lineHeight: 1.35,
                                      width: 'fit-content',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {tag}
                                  </div>
                                  {thinkingMarksHoverId === String(folder.id) && (
                                    <div
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        zIndex: 20,
                                        marginTop: 4,
                                        background: '#fff',
                                        border: '1px solid #FECACA',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                        minWidth: 180,
                                        maxWidth: 280,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        color: '#7F1D1D',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {students.map((student) => (
                                        <button
                                          key={student.name}
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openPaper(student);
                                          }}
                                          title={`Open the transcript for ${student.name}`}
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            width: '100%',
                                            padding: '2px 0',
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'inherit',
                                            font: 'inherit',
                                            fontWeight: 700,
                                            textAlign: 'left',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <span>{student.name}</span>
                                          <span style={{ fontWeight: 600, color: '#B91C1C', whiteSpace: 'nowrap' }}>
                                            {(Array.isArray(student.phrases) ? student.phrases : []).join(', ')}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Per-card action row: EduPage Login + Upload.
                                • Login hands the card to the Login Edupage
                                  extension (needs a creator email to match on).
                                • Upload pushes just THIS folder's local images to
                                  the server on demand, via the local
                                  folder-server — no other folder is touched. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 6, flexWrap: 'nowrap' }}>
                              {folder.created_by && (
                                <button
                                  onClick={(e) => loginToEduPageForCard(e, folder)}
                                  title={eduBridgeReady
                                    ? `Log into EduPage as ${folder.created_by} and open this test`
                                    : `Log into EduPage as ${folder.created_by} — needs the Login Edupage extension installed & updated`}
                                  style={{
                                    flex: '1 1 0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 0,
                                    padding: '3px 2px',
                                    fontSize: '0.64rem',
                                    fontWeight: 700,
                                    border: '1px solid #059669',
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: '#059669',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  Login
                                </button>
                              )}
                              {(() => {
                                const isUploading = uploadingFolderIds.has(folder.id);
                                const isUploaded = uploadedFolderIds.has(folder.id);
                                // Same green as Login. Grey only while an upload is in progress.
                                const color = isUploading ? '#6b7280' : '#059669';
                                const label = isUploading ? 'Uploading…' : isUploaded ? 'Uploaded' : 'Upload';
                                return (
                                  <button
                                    onClick={(e) => uploadFolderImages(e, folder)}
                                    disabled={isUploading}
                                    title={isUploaded
                                      ? `Uploaded — click to upload again if you added more images (${folder.scan_count} on the server)`
                                      : `Upload this folder's images to the server now (${folder.scan_count} currently on the server)`}
                                    style={{
                                      flex: '1 1 0',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: 0,
                                      padding: '3px 2px',
                                      fontSize: '0.64rem',
                                      fontWeight: 700,
                                      border: `1px solid ${color}`,
                                      borderRadius: 6,
                                      background: 'transparent',
                                      color,
                                      cursor: isUploading ? 'wait' : 'pointer',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {label}
                                  </button>
                                );
                              })()}
                              {/* Review — same action as the "Review" button in
                                  the Analysis Results bar: loads this folder's
                                  saved sorted list into the results table. Only
                                  shown when the folder has a saved snapshot. */}
                              {hasSnapshot && (
                                <button
                                  onClick={(e) => reviewFolderFromCard(e, folder)}
                                  disabled={snapshotBusy}
                                  title="Load this folder's sorted list into the Analysis Results table to review it"
                                  style={{
                                    flex: '1 1 0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 0,
                                    padding: '3px 2px',
                                    fontSize: '0.64rem',
                                    fontWeight: 700,
                                    border: '1px solid #059669',
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: '#059669',
                                    cursor: snapshotBusy ? 'wait' : 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  Review
                                  </button>
                              )}
                              {assessSnapshotMetaMap.has(String(folder.id)) && (
                                <button
                                  onClick={(e) => recheckFolderFromCard(e, folder)}
                                  title="Open Check more sheets with this folder's saved assessment rows and rubric, ready to OCR or recheck"
                                  style={{
                                    flex: '1 1 0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 0,
                                    padding: '3px 2px',
                                    fontSize: '0.64rem',
                                    fontWeight: 700,
                                    border: '1px solid #059669',
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: '#059669',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  CheckTab
                                </button>
                              )}
                            </div>
                            {/* Auto-check medium picker. Sets which checking
                                pipeline the browser-side auto-check engine
                                runs once this folder is "Reviewed". Clicking
                                the active medium again turns auto-check off.
                                stopPropagation so the card's folder-select
                                click doesn't fire. Hidden on empty folders. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                            {!isEmpty && (() => {
                              const autoMedium = autoCheckMediumMap.get(String(folder.id)) || null;
                              const busy = autoCheckMediumBusyId === String(folder.id);
                              // The button is lit only while that medium is saved. A check
                              // start, a print, and Edupage clear the saved medium, so
                              // the three buttons go grey and nothing is left stored.
                              const mkBtn = (medium, label, activeColor, hint) => {
                                const active = autoMedium === medium;
                                return (
                                  <button
                                    key={medium}
                                    onClick={(e) => { e.stopPropagation(); if (!busy) handleSetAutoCheckMedium(folder.id, medium); }}
                                    disabled={busy}
                                    title={active
                                      ? `Auto-check is ON for this folder (${label}). Click to turn it off.`
                                      : (hint || `Turn on auto-check for this folder using the ${label} pipeline. It runs ~30 sec after the list is marked Reviewed.`)}
                                    style={{
                                      flexShrink: 0,
                                      padding: '3px 6px',
                                      fontSize: '0.68rem',
                                      fontWeight: 700,
                                      border: active ? `1.5px solid ${activeColor}` : '1px solid var(--border-color, #d1d5db)',
                                      borderRadius: 6,
                                      background: active ? activeColor : 'transparent',
                                      color: active ? 'white' : 'var(--text-secondary, #6b7280)',
                                      cursor: busy ? 'wait' : 'pointer',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {label}
                                  </button>
                                );
                              };
                              return (
                                <>
                                  {mkBtn('english', 'English', '#2563eb')}
                                  {mkBtn('urdu', 'Urdu', '#7c3aed')}
                                  {mkBtn('urog', 'UrOG', '#0f766e', 'Turn on auto-check using the Urdu pipeline. OCR runs on Gemini 3.8-flash; grading uses ggml on Spc and the Mac. It runs ~30 sec after the list is marked Reviewed.')}
                                </>
                              );
                            })()}
                              {renderResultButton()}
                              {(() => {
                                const previewOpen = rubricPreviewOpenIds.has(String(folder.id));
                                return (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFolderRubricPreview(e, folder);
                                    }}
                                    title={previewOpen ? 'Hide rubric preview' : 'Show rubric preview'}
                                    aria-label={previewOpen ? 'Hide rubric preview' : 'Show rubric preview'}
                                    style={{
                                      order: 3,
                                      marginLeft: stepBoxesVisible ? 'auto' : undefined,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: previewOpen
                                        ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                                        : 'rgba(255, 255, 255, 0.6)',
                                      color: previewOpen ? 'white' : '#1E40AF',
                                      border: previewOpen ? 'none' : '1px solid #93C5FD',
                                      borderRadius: 999,
                                      width: 26,
                                      height: 26,
                                      padding: 0,
                                      fontSize: 12,
                                      cursor: 'pointer',
                                      flexShrink: 0
                                    }}
                                  >
                                    <i className={`fa-solid ${previewOpen ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                  </button>
                                );
                              })()}
                            </div>
                            {(() => {
                              const requests = (folderReviews.requests && folderReviews.requests[String(folder.id)]) || [];
                              if (!requests.length) return null;
                              return (
                                <div style={{ marginTop: 6 }}>
                                  <RegradeChip
                                    requests={requests}
                                    folderPath={`rubrics/${folder.id}`}
                                    onOpen={(e) => openRegradeFromCard(e, folder)}
                                  />
                                </div>
                              );
                            })()}
                            {rubricPreviewOpenIds.has(String(folder.id)) && (() => {
                              const cached = rubricPreviewCache[String(folder.id)];
                              const text = (cached && cached.text) || '';
                              const pdfText = (cached && cached.pdfText) || '';
                              const showPdf = pdfText.trim() && pdfText.trim() !== text.trim();
                              const renderLines = (value) => value.split('\n').map((line, index) => (
                                <div key={index}>{line ? <MatrixText text={line} /> : '\u00A0'}</div>
                              ));
                              const editingThis = editingRubricId === String(folder.id);
                              return (
                                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 8 }}>
                                  {(!cached || cached.status === 'loading') && (
                                    <div className="rubric-preview" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                      Loading rubric…
                                    </div>
                                  )}
                                  {cached && cached.status === 'error' && (
                                    <div className="rubric-preview" style={{ color: '#b91c1c', fontSize: '0.8rem' }}>
                                      {cached.message || 'Could not load rubric'}
                                    </div>
                                  )}
                                  {cached && cached.status === 'ok' && (
                                    <div className="rubric-preview">
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                                        <strong style={{ marginBottom: 0 }}>Preview:</strong>
                                        {!editingThis && (
                                          <button
                                            type="button"
                                            onClick={(e) => beginFolderRubricEdit(e, folder, text)}
                                            title="Edit this folder's rubric"
                                            style={{
                                              background: 'transparent',
                                              color: '#059669',
                                              border: '1px solid #059669',
                                              borderRadius: 6,
                                              padding: '2px 8px',
                                              fontSize: '0.72rem',
                                              fontWeight: 700,
                                              cursor: 'pointer',
                                              flexShrink: 0
                                            }}
                                          >
                                            Edit
                                          </button>
                                        )}
                                      </div>
                                      {editingThis ? (
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #6b7280)' }}>
                                          This rubric is open in the large edit window.
                                        </div>
                                      ) : (
                                        text
                                          ? <div className="preview-text">{renderLines(text)}</div>
                                          : <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontStyle: 'italic' }}>No rubric text saved for this folder yet.</div>
                                      )}
                                    </div>
                                  )}
                                  {cached && cached.status === 'ok' && showPdf && !editingThis && (
                                    <div className="rubric-preview">
                                      <strong>PDF Text Preview:</strong>
                                      <div className="preview-text">{renderLines(pdfText)}</div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Status tags — placed LAST in DOM order (not just
                                pushed down visually with `order`) so a manual
                                top-to-bottom drag-select reads in visual order:
                                title → assignment → date → images → creator →
                                English/Urdu → status badges last. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                              {(() => {
                                const autoSt = autoSortStatusMap.get(String(folder.id)) || {};
                                const requested = !!(autoSt.requested_at || autoSt.restore_status);
                                const sortFp = String(aiStatus?.sort?.folderPath || '');
                                const sortingThis = !!(aiStatus?.sort?.busy && (
                                  sortFp === `rubrics/${folder.id}` || sortFp.endsWith(`rubrics/${folder.id}`)
                                ));
                                const failed = !hasSnapshot && (autoSt.attempts || 0) >= 3;
                                const startBusy = startAutoSortBusyId === String(folder.id);
                                const inProgress = !hasSnapshot && (requested || sortingThis || startBusy) && !failed;
                                const pill = {
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  letterSpacing: 0.3,
                                  padding: '2px 7px',
                                  borderRadius: 10,
                                  textTransform: 'uppercase',
                                  whiteSpace: 'nowrap',
                                  border: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                };
                                if (hasSnapshot) {
                                  const snapSt = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                                  // Sorted only while awaiting review. After
                                  // the examiner clicks Reviewed, that badge
                                  // replaces this one.
                                  if (snapSt !== 'saved') return null;
                                  return (
                                    <span
                                      title="This folder has been sorted and is awaiting review. Open it, click Review to load the list, then Reviewed to confirm."
                                      style={{
                                        ...pill,
                                        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                        color: 'white',
                                        boxShadow: '0 1px 3px rgba(37,99,235,0.4)'
                                      }}
                                    >
                                      <i className="fa-solid fa-check" aria-hidden="true" />
                                      Sorted
                                    </span>
                                  );
                                }
                                if (isEmpty) return null;
                                if (inProgress) {
                                  return (
                                    <span
                                      title="Auto-sort is running or queued for this folder"
                                      style={{
                                        ...pill,
                                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                        color: 'white',
                                        boxShadow: '0 1px 3px rgba(37,99,235,0.35)',
                                        opacity: 0.9
                                      }}
                                    >
                                      <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                                      Sorting…
                                    </span>
                                  );
                                }
                                if (failed) return null;
                                const blocked = isUploadMismatch || isOddCount;
                                return (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!blocked && !startBusy) {
                                        handleStartAutoSort(folder.id, folderDisplayTitle(folder));
                                      }
                                    }}
                                    disabled={blocked || startBusy}
                                    title={isUploadMismatch
                                      ? 'Local folder and website image counts differ — fix with Upload before starting auto-sort.'
                                      : isOddCount
                                        ? 'Odd image count — a page may be missing. Fix the scan count before starting auto-sort.'
                                        : 'Start auto-sort for this folder when the scans are ready.'}
                                    style={{
                                      ...pill,
                                      background: blocked
                                        ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                                        : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                      color: 'white',
                                      boxShadow: blocked
                                        ? '0 1px 3px rgba(100,116,139,0.35)'
                                        : '0 1px 3px rgba(37,99,235,0.4)',
                                      cursor: (blocked || startBusy) ? (startBusy ? 'wait' : 'default') : 'pointer',
                                      opacity: blocked ? 0.85 : 1
                                    }}
                                  >
                                    <i className="fa-solid fa-play" aria-hidden="true" />
                                    Start
                                  </button>
                                );
                              })()}
                              {hasSnapshot && (() => {
                                // Three-state badge tracking the sorted list's
                                // workflow:
                                //   'saved'           → "Review"  (amber)  — auto-sorted, awaiting an examiner
                                //   'reviewed'        → "Reviewed" (green)  — examiner confirmed
                                //   'auto-checked'    → "Checked" (purple) — auto-checked, awaiting a manual Approve
                                //   'copy_to_edupage' → "Copy to Edupage" (blue) — checked + approved
                                //   'completed'       → "Edupaged" (deep green) — marks pasted into EduPage, batch done
                                //   'error'           → "Check Failed" / "Ungraded rows" (red) — auto-check
                                //                       failed/interrupted OR saved table has ungraded
                                //                       placeholders that need regrade
                                const snapStatus = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                                // 'saved' is shown as Sorted in the Start-slot
                                // above. Don't also render the old Review chip.
                                if (snapStatus === 'saved') return null;
                                const stepInfo = autoCheckStepsMap.get(String(folder.id)) || {};
                                const approveErr = String((stepInfo.steps || {}).approve?.error || '');
                                const isUngradedError = snapStatus === 'error' && /ungraded/i.test(approveErr);
                                const badge = snapStatus === 'error'
                                  ? (isUngradedError
                                      ? {
                                          text: '⚠ Ungraded rows',
                                          bg: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                          shadow: '0 1px 3px rgba(245,158,11,0.45)',
                                          title: 'Auto-check left some rows ungraded (OCR done but grading incomplete / Table N/A). Click to open in the Check tab, regrade those rows, then Approve.'
                                        }
                                      : {
                                          text: '⚠ Check Failed',
                                          bg: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                                          shadow: '0 1px 3px rgba(239,68,68,0.45)',
                                          title: 'Auto-check flagged a problem for this folder — OCR/grading failures, fewer checked than the sorted list, or a failed/interrupted run. Click to open it in the Check tab: the saved table loads so you can recheck the problem rows, then Approve. (A failed/interrupted run may have no saved table — just re-run the check.)'
                                        })
                                  : snapStatus === 'completed'
                                  ? {
                                      text: '✅ Edupaged',
                                      bg: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                      shadow: '0 1px 3px rgba(34,197,94,0.4)',
                                      title: 'Marks have been copied into EduPage — this batch is complete.'
                                    }
                                  : snapStatus === 'copy_to_edupage'
                                  ? {
                                      text: '📋 Copy to Edupage',
                                      bg: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                      shadow: '0 1px 3px rgba(37,99,235,0.4)',
                                      title: 'Checked and approved — results are ready to be copied into EduPage.'
                                    }
                                  : snapStatus === 'auto-checked'
                                    ? {
                                        text: '✅ Auto-Checked',
                                        bg: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                        shadow: '0 1px 3px rgba(139,92,246,0.4)',
                                        title: 'Auto-checked — papers were graded and the result table saved. Open the Check tab and click Approve to finalize (saves to the student list + scans folder).'
                                      }
                                  : snapStatus === 'reviewed'
                                    ? {
                                        text: '✓ Reviewed',
                                        bg: 'linear-gradient(135deg, #10b981, #059669)',
                                        shadow: '0 1px 3px rgba(16,185,129,0.4)',
                                        title: 'This sorted list has been reviewed and confirmed by an examiner.'
                                      }
                                    : {
                                        text: '🔍 Review',
                                        bg: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        shadow: '0 1px 3px rgba(245,158,11,0.4)',
                                        title: 'This folder was sorted automatically and is awaiting review — open it and click "Review" to load the list, then "Reviewed" to confirm.'
                                      };
                                // Two clickable shortcut badges:
                                //  • "Copy to Edupage" → jump to this class's
                                //    student list in the Students tab.
                                //  • "Auto-Checked" → open this folder in the
                                //    Check tab (imports the sorted list + the
                                //    checked table, ready for review + Approve).
                                // stopPropagation so neither also triggers the
                                // card's select.
                                const isCopy = snapStatus === 'copy_to_edupage';
                                // 'completed' (Edupaged) jumps to the Students tab
                                // and opens this class's list too — same as
                                // 'copy_to_edupage' — so a finished batch is still
                                // one click away from its marks.
                                const isCompleted = snapStatus === 'completed';
                                const opensStudents = isCopy || isCompleted;
                                const isAutoChecked = snapStatus === 'auto-checked';
                                const isError = snapStatus === 'error';
                                const failedStudents = (checkErrorMap.get(String(folder.id)) || {}).students || [];
                                const failedDetail = isError ? checkFailedDetail(stepInfo, failedStudents) : { summary: [], people: [] };
                                const failedTitle = [...failedDetail.summary, ...failedDetail.people].join('\n');
                                if (isError && failedStudents.length) {
                                  badge.text = isUngradedError
                                    ? `⚠ Ungraded rows (${failedStudents.length})`
                                    : `⚠ Check Failed (${failedStudents.length})`;
                                }
                                if (isError && failedTitle) badge.title = failedTitle;
                                // 'error' (auto-check hit a problem but saved the
                                // table) opens the Check tab too — same as
                                // 'auto-checked' — so the examiner can load the
                                // saved table and recheck the problem rows.
                                const opensCheckTab = isAutoChecked || isError;
                                const clickable = opensStudents || opensCheckTab;
                                const showFailedList = isError && !!(failedDetail.summary.length || failedDetail.people.length);
                                const chip = (
                                  <span
                                    title={showFailedList ? failedTitle : (isCopy
                                      ? "Checked & approved. Click to open this class's student list in the Students tab."
                                      : isCompleted
                                        ? "Marks are in EduPage — batch complete. Click to open this class's student list in the Students tab."
                                        : isAutoChecked
                                          ? 'Auto-checked. Click to open this folder in the Check tab — loads the sorted list and the checked table for review + Approve.'
                                          : isUngradedError
                                            ? 'Ungraded rows — click to open in the Check tab, regrade those rows, then Approve.'
                                          : badge.title)}
                                    onClick={
                                      opensStudents
                                        ? (e) => {
                                            e.stopPropagation();
                                            navigate('/students', {
                                              state: { autoOpenList: { class: folder.class || '', subject: folder.subject || '' } }
                                            });
                                          }
                                        : opensCheckTab
                                          ? (e) => {
                                              e.stopPropagation();
                                              navigate('/evaluate/check', {
                                                state: { autoOpenFolder: {
                                                  folder_path: `rubrics/${folder.id}`,
                                                  title: folderDisplayTitle(folder),
                                                  class: folder.class || '',
                                                  subject: folder.subject || ''
                                                } }
                                              });
                                            }
                                          : undefined
                                    }
                                    style={{
                                      background: badge.bg,
                                      color: 'white',
                                      fontSize: '0.62rem',
                                      fontWeight: 700,
                                      letterSpacing: 0.3,
                                      padding: '2px 7px',
                                      borderRadius: 10,
                                      textTransform: 'uppercase',
                                      boxShadow: badge.shadow,
                                      whiteSpace: 'nowrap',
                                      cursor: clickable ? 'pointer' : 'default'
                                    }}
                                  >
                                    {badge.text}
                                  </span>
                                );
                                if (!showFailedList) return chip;
                                return (
                                  <span
                                    onMouseEnter={() => setCheckErrorHoverId(String(folder.id))}
                                    onMouseLeave={() => setCheckErrorHoverId((current) => (
                                      current === String(folder.id) ? null : current
                                    ))}
                                    style={{ position: 'relative', display: 'inline-flex' }}
                                  >
                                    {chip}
                                    {checkErrorHoverId === String(folder.id) && (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          left: 0,
                                          top: '100%',
                                          zIndex: 20,
                                          marginTop: 4,
                                          background: '#fff',
                                          border: '1px solid #FECACA',
                                          borderRadius: 8,
                                          padding: '6px 8px',
                                          boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                                          minWidth: 220,
                                          maxWidth: 360,
                                          maxHeight: 220,
                                          overflowY: 'auto',
                                          color: '#7F1D1D',
                                          fontSize: '0.72rem',
                                          fontWeight: 700,
                                          lineHeight: 1.45,
                                          textTransform: 'none',
                                          letterSpacing: 0
                                        }}
                                      >
                                        {failedDetail.summary.map((line) => (
                                          <div key={line} style={{ padding: '2px 0' }}>{line}</div>
                                        ))}
                                        {failedStudents.map((student) => {
                                          const reason = (Array.isArray(student.errors) ? student.errors : []).join(', ');
                                          const pages = problemPagesFromReason(student.errors);
                                          return (
                                          <button
                                            key={student.name}
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setQuickTranscript({
                                                folderPath: `rubrics/${folder.id}`,
                                                name: student.name,
                                                page: pages[0] ? String(pages[0]) : '',
                                                pages: pages.map(String),
                                                refresh: 'ocr'
                                              });
                                            }}
                                            title={pages.length
                                              ? `Open page ${pages[0]} for ${student.name}`
                                              : `Open the transcript for ${student.name}`}
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              gap: 8,
                                              width: '100%',
                                              padding: '2px 0',
                                              border: 'none',
                                              background: 'transparent',
                                              color: 'inherit',
                                              font: 'inherit',
                                              fontWeight: 700,
                                              textAlign: 'left',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            <span>{student.name}</span>
                                            <span style={{ fontWeight: 600, color: '#B91C1C', textAlign: 'right' }}>
                                              {reason}
                                            </span>
                                          </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </span>
                                );
                              })()}
                              {/* Auto-check STEP boxes — "AI1 ☑☑☒☐". Shown on
                                  every reviewed-or-beyond folder (i.e. in the
                                  auto-check pipeline). First the ai user that
                                  ran it, then five boxes in run order: Run,
                                  Save, Scans, Approve, Absent. ✓ done (green), ✕ failed
                                  (red — hover for the reason, click to jump to
                                  the Check tab set up to fix that step), amber …
                                  in progress, empty = not reached yet. */}
                              {hasSnapshot && (() => {
                                const info = autoCheckStepsMap.get(String(folder.id)) || {};
                                const steps = info.steps || {};
                                const by = info.by;
                                const snapSt = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                                const stepVals = Object.values(steps);
                                const hasStepError = stepVals.some(s => s && s.status === 'error');
                                const hasStuckRunning = !!(info.by && stepVals.some(s => s && s.status === 'running'));
                                const regradeQueued = !!(info.regrade_at && !info.by);
                                const showReAuto = isPrinted || snapSt === 'error' || snapSt === 'auto-checked' || snapSt === 'copy_to_edupage' || snapSt === 'completed' || hasStepError || hasStuckRunning || regradeQueued;
                                // Only render the whole block if there's something to show
                                if (snapSt === 'saved' && !showReAuto) return null;
                                const STEP_DEFS = [
                                  { key: 'run', label: 'Run', desc: 'Grade the sheets' },
                                  { key: 'save', label: 'Save', desc: 'Save the result table' },
                                  { key: 'scans', label: 'Scans', desc: 'Save the printable scans-folder table' },
                                  { key: 'approve', label: 'Approve', desc: 'Approve into the student list' },
                                  { key: 'absent', label: 'Absent', desc: 'Mark blank students absent in the student list' },
                                ];
                                return (
                                  <div style={{ order: 100, display: 'flex', alignItems: 'center', gap: '4px', width: '100%', marginTop: '2px' }}>
                                    <span
                                      title={by ? `Auto-checked by ${by}` : 'Not yet picked up by an auto-check account'}
                                      style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.4, minWidth: 26, color: by ? '#6d28d9' : 'var(--text-secondary)' }}
                                    >
                                      {by || '—'}
                                    </span>
                                    {STEP_DEFS.map((sd, i) => {
                                      const st = steps[sd.key] || {};
                                      let status = st.status || 'pending';
                                      // Self-heal a stale box: steps run strictly in order, so a
                                      // LATER step being 'done' proves this one finished. If this box
                                      // is stuck at 'running'/'pending' (a dropped "done" ping, or a
                                      // re-run that re-reported this step) while any later step is
                                      // done, show it as done. An 'error' is left untouched so a real
                                      // failure still shows.
                                      if (status !== 'done' && status !== 'error'
                                          && STEP_DEFS.slice(i + 1).some(later => steps[later.key] && steps[later.key].status === 'done')) {
                                        status = 'done';
                                      }
                                      const isErr = status === 'error';
                                      const isDone = status === 'done';
                                      const isRunning = status === 'running';
                                      const box = isDone
                                        ? { sym: '✓', bg: '#059669', color: '#fff', bd: '#059669' }
                                        : isErr
                                          ? { sym: '✕', bg: '#ef4444', color: '#fff', bd: '#ef4444' }
                                          : isRunning
                                            ? { sym: '…', bg: '#f59e0b', color: '#fff', bd: '#f59e0b' }
                                            : { sym: '', bg: 'transparent', color: 'var(--text-secondary)', bd: 'var(--border-color, #cbd5e1)' };
                                      let stateLine;
                                      if (isDone) stateLine = 'Completed';
                                      else if (isErr) stateLine = `Failed: ${st.error || 'unknown error'} — click to open the Check tab and fix this step`;
                                      else if (isRunning) stateLine = 'In progress…';
                                      else stateLine = 'Not started';
                                      return (
                                        <span
                                          key={sd.key}
                                          title={`${i + 1}. ${sd.label} — ${sd.desc}\n${stateLine}`}
                                          onClick={isErr ? (e) => {
                                            e.stopPropagation();
                                            navigate('/evaluate/check', {
                                              state: { autoOpenFolder: {
                                                folder_path: `rubrics/${folder.id}`,
                                                title: folderDisplayTitle(folder),
                                                class: folder.class || '',
                                                subject: folder.subject || '',
                                                focusStep: sd.key
                                              } }
                                            });
                                          } : undefined}
                                          style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 16, height: 16, borderRadius: 4,
                                            border: `1.5px solid ${box.bd}`, background: box.bg, color: box.color,
                                            fontSize: '0.6rem', fontWeight: 800, lineHeight: 1,
                                            cursor: isErr ? 'pointer' : 'default'
                                          }}
                                        >
                                          {box.sym}
                                        </span>
                                      );
                                    })}
                                    {/* Re-Auto sits after the 5 step boxes (not with status badges). */}
                                    {showReAuto && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleRetryAutoCheck(folder.id); }}
                                        title={isPrinted
                                          ? "Re-check this printed folder — clears printed_at and re-queues auto-check so an ai1–ai4 browser will re-grade all papers again."
                                          : snapSt === 'completed'
                                            ? "Re-check this Edupaged folder — drops status back to Reviewed and an ai1–ai4 browser will re-grade all papers again. printed_at is also cleared."
                                            : snapSt === 'copy_to_edupage'
                                              ? "Re-run auto-check on this approved folder. Status drops back to Reviewed so an ai1–ai4 browser will grade it again. Only this folder is reset."
                                              : snapSt === 'auto-checked'
                                                ? "Re-check this folder from scratch — drops status back to Reviewed and an ai1–ai4 browser will re-grade all papers again. Only this folder is reset."
                                                : "Clear this folder's auto-check error/stuck state and queue it again for ai1–ai4. Only this folder is reset."}
                                        style={{
                                          background: 'transparent',
                                          color: '#7c3aed',
                                          fontSize: '0.58rem',
                                          fontWeight: 700,
                                          letterSpacing: 0.2,
                                          padding: '1px 4px',
                                          borderRadius: 6,
                                          whiteSpace: 'nowrap',
                                          border: '1px solid #7c3aed',
                                          cursor: 'pointer',
                                          marginLeft: 2,
                                          lineHeight: 1.4,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 3
                                        }}
                                      >
                                        <i className="fa-solid fa-rotate-right" style={{ fontSize: '0.55rem' }} aria-hidden="true" />
                                        Re-Auto
                                      </button>
                                    )}
                                    {/* Re-grade — queue this folder for grading-only
                                        auto-check (skip OCR / Text Step 1). Multiple
                                        folders line up; ai1–ai4 then save/approve. */}
                                    {showReAuto && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!regradeQueued) handleQueueRegrade(folder.id);
                                        }}
                                        disabled={regradeQueued}
                                        title={regradeQueued
                                          ? "This folder is already in the re-grade queue. An ai1–ai4 browser will skip OCR / Text Step 1, re-grade the saved table, then run Save → Scans → Approve → Absent."
                                          : "Queue this folder for re-grade. Skips OCR / Text Step 1 and uses the saved transcripts. Click several folders — they run one after another. After grading, Save / Scans / Approve / Absent still run."}
                                        style={{
                                          background: 'transparent',
                                          color: regradeQueued ? '#a78bfa' : '#7c3aed',
                                          fontSize: '0.58rem',
                                          fontWeight: 700,
                                          letterSpacing: 0.2,
                                          padding: '1px 4px',
                                          borderRadius: 6,
                                          whiteSpace: 'nowrap',
                                          border: `1px solid ${regradeQueued ? '#a78bfa' : '#7c3aed'}`,
                                          cursor: regradeQueued ? 'default' : 'pointer',
                                          marginLeft: 2,
                                          lineHeight: 1.4,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 3
                                        }}
                                      >
                                        <i className="fa-solid fa-pen-to-square" style={{ fontSize: '0.55rem' }} aria-hidden="true" />
                                        {regradeQueued ? 'Queued' : 'Re-grade'}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Auto-sort failure → Retry button. Shown
                                  only when the daemon has hit the
                                  3-attempt ceiling AND the folder doesn't
                                  already have a snapshot (no point
                                  retrying something that's already done).
                                  Click resets attempts so the daemon
                                  re-picks the folder on its next tick. */}
                              {!hasSnapshot && (autoSortStatusMap.get(String(folder.id))?.attempts || 0) >= 3 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRetryAutoSort(folder.id); }}
                                  title={`Auto-sort failed ${autoSortStatusMap.get(String(folder.id))?.attempts || 0}× — last error: ${autoSortStatusMap.get(String(folder.id))?.last_error || 'unknown'}. Click to reset and let the background daemon try again.`}
                                  style={{
                                    background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                                    color: 'white',
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '2px 7px',
                                    borderRadius: 10,
                                    textTransform: 'uppercase',
                                    boxShadow: '0 1px 3px rgba(239,68,68,0.4)',
                                    whiteSpace: 'nowrap',
                                    border: 'none',
                                    cursor: 'pointer'
                                  }}
                                >
                                  ↻ Retry auto-sort
                                </button>
                              )}
                              {/* Re-sort — for folders already in Review /
                                  Reviewed. Queues another auto-sort pass;
                                  daemon restores the same badge after.
                                  Also shown as "Re-sorting…" while a
                                  queued re-sort is in flight (snapshot
                                  may already be cleared). */}
                              {(() => {
                                const snapSt = snapshotFolderStatusMap.get(String(folder.id)) || 'saved';
                                const pending = !!(autoSortStatusMap.get(String(folder.id))?.restore_status);
                                const canReSort = hasSnapshot && (snapSt === 'saved' || snapSt === 'reviewed');
                                if (!canReSort && !pending) return null;
                                const mismatch = isUploadMismatch;
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!pending && !mismatch) handleReSort(folder.id, folderDisplayTitle(folder));
                                    }}
                                    disabled={pending || mismatch}
                                    title={mismatch
                                      ? 'Local folder and website image counts differ — fix with Upload before re-sorting.'
                                        : pending
                                        ? 'Re-sort is queued — the background daemon will re-sort this folder and restore its Review status shortly.'
                                        : 'Queue this folder for auto-sort again. Ready to Print is cleared so it can be checked again. The current sorted list is replaced; Review / Reviewed status is restored when sorting finishes.'}
                                    style={{
                                      background: (pending || mismatch)
                                        ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                                        : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                      color: 'white',
                                      fontSize: '0.62rem',
                                      fontWeight: 700,
                                      letterSpacing: 0.3,
                                      padding: '2px 7px',
                                      borderRadius: 10,
                                      textTransform: 'uppercase',
                                      boxShadow: (pending || mismatch)
                                        ? '0 1px 3px rgba(100,116,139,0.35)'
                                        : '0 1px 3px rgba(245,158,11,0.4)',
                                      whiteSpace: 'nowrap',
                                      border: 'none',
                                      cursor: (pending || mismatch) ? 'default' : 'pointer',
                                      opacity: (pending || mismatch) ? 0.85 : 1
                                    }}
                                  >
                                    {pending ? '↻ Re-sorting…' : '↻ Re-sort'}
                                  </button>
                                );
                              })()}
                              {/* Checked pill — renamed from "Done" and
                                  moved here to share the Sorted line per
                                  user request. Same visual weight as the
                                  Sorted chip. */}
                              {isDone && (
                                <span
                                  title="Checked & saved to the scans folder. Click to open this folder in the Scans tab and print it."
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/scans', { state: { autoOpenFolderId: folder.id } });
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                    color: 'white',
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '2px 7px',
                                    borderRadius: 10,
                                    textTransform: 'uppercase',
                                    boxShadow: '0 1px 3px rgba(37,99,235,0.4)',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer'
                                  }}
                                >
                                  🖨 Ready to Print
                                </span>
                              )}
                              {/* Printed pill — moved inline with the
                                  other status chips so it never overlaps
                                  the folder name. */}
                              {isPrinted && (
                                <span
                                  title="Folder has been printed (printed_at is stamped)"
                                  style={{
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: 'white',
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '2px 7px',
                                    borderRadius: 10,
                                    textTransform: 'uppercase',
                                    boxShadow: '0 1px 3px rgba(34,197,94,0.4)',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  🖨 Printed
                                </span>
                              )}
                              {(() => {
                                const delta = imageDeltaMap.get(String(folder.id));
                                if (!delta) return null;
                                const added = delta.added || [];
                                const deleted = delta.deleted || [];
                                const chip = {
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  letterSpacing: 0.3,
                                  padding: '2px 7px',
                                  borderRadius: 10,
                                  textTransform: 'uppercase',
                                  whiteSpace: 'nowrap',
                                  color: 'white'
                                };
                                return (
                                  <>
                                    {added.length > 0 && (
                                      <span
                                        title={`Get Sorting\n${added.join('\n')}`}
                                        style={{
                                          ...chip,
                                          background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                                          boxShadow: '0 1px 3px rgba(3,105,161,0.4)'
                                        }}
                                      >
                                        Images added ({added.length})
                                      </span>
                                    )}
                                    {deleted.length > 0 && (
                                      <span
                                        title={`Get Sorting\n${deleted.join('\n')}`}
                                        style={{
                                          ...chip,
                                          background: 'linear-gradient(135deg, #ea580c, #c2410c)',
                                          boxShadow: '0 1px 3px rgba(194,65,12,0.4)'
                                        }}
                                      >
                                        Images deleted ({deleted.length})
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                              {/* Empty pill — same treatment as the
                                  others. Only shows when the folder has
                                  no images AND no other status. */}
                              {!isPrinted && !isDone && isEmpty && (
                                <span
                                  title="No images uploaded yet"
                                  style={{
                                    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                                    color: 'white',
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '2px 7px',
                                    borderRadius: 10,
                                    textTransform: 'uppercase',
                                    boxShadow: '0 1px 3px rgba(239,68,68,0.4)',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  Empty
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            </div>
            <SubjectColumn
              items={visibleFolders}
              gradeFilter={gradeFilter}
              sectionFilter={sectionFilter}
              subjectFilter={subjectPick}
              onSubject={(subject) => setSubjectPick(subjectPick === subject ? null : subject)}
            />
            </div>
            )}
            </div>{/* /flex row: calendar + folder list */}
          </div>
        </div>
        )}

        {/* Folder Path row was merged into the collapsed-folder summary
            bar above so the path sits inline with the folder name + date,
            saving a full row of vertical space. */}

        {/* Scans-folder action row (Batch / Model / Start Sorting / Review…).
            Hidden with the calendar when folders are collapsed (Review /
            Hide folders) so the tables get the full height. */}
        {!calendarCollapsed && (
        <div className="control-row action-buttons" style={{ alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
          <div className="control-group" style={{ flex: '0 0 auto', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Batch</label>
            <input
              type="number"
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              min="1"
              max="100"
              style={{ width: 64, padding: '3px 6px' }}
            />
          </div>

          <ProviderSelect value={sortProvider} onChange={changeSortProvider} />

          {/* MR1/RC1 are Gemini API-key choices; only relevant for the Gemini provider. */}
          {isGeminiProvider && (
            <div className="control-group" style={{ flex: '0 0 auto', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Limit</label>
              <select value={limit} onChange={(e) => setLimit(e.target.value)} style={{ width: 80, padding: '3px 6px' }}>
                <option value="MR1">MR1</option>
                <option value="RC1">RC1</option>
              </select>
            </div>
          )}

          {isLocalProvider && (
            <div className="control-group" style={{ flex: '0 0 auto', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Model</label>
              <select value={model} onChange={(e) => setModel(e.target.value)} style={{ width: 210, padding: '3px 6px' }}>
                <option value="">Select model</option>
                {LOCAL_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          )}

          {isGeminiProvider && (
            <div className="control-group" style={{ flex: '0 0 auto', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Model</label>
              <select value={model} onChange={(e) => setModel(e.target.value)} style={{ width: 210, padding: '3px 6px' }}>
                <option value="gemini-3.8-flash">3.8-flash</option>
                <option value="gemini-3.6-flash">3.6-flash</option>
                <option value="gemini-3.1-pro-preview">3.1-pro-preview (2-12)</option>
                <option value="gemini-3.1-flash-lite-preview">3.1-flash-lite-preview (0.25-1.5)</option>
                <option value="gemini-3.1-flash-live-preview">3.1-flash-live-preview (1-4.5)</option>
                <option value="gemini-3.1-flash-image-preview">3.1-flash-image-preview (0.5-3)</option>
                <option value="gemini-3-flash-preview">3-flash-preview (0.5-3)</option>
                <option value="gemini-3-pro-image-preview">3-pro-image-preview (2-12)</option>
                <option value="gemini-2.5-pro">2.5-pro (1.25-10)</option>
                <option value="gemini-2.5-flash">2.5-flash (0.3-2.5)</option>
                <option value="gemini-2.5-flash-lite">2.5-flash-lite (0.1-0.4)</option>
              </select>
            </div>
          )}

          {/* Read-only model indicator for OpenAI / DeepSeek (single fixed model). */}
          {(isDeepseekProvider || (!isGeminiProvider && !isLocalProvider)) && (
            <div className="control-group" style={{ flex: '0 0 auto', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Model</label>
              <span style={{ padding: '3px 8px', background: 'var(--surface, #f1f5f9)', borderRadius: 6, fontSize: 12, color: 'var(--text-secondary, #475569)' }}>
                {isDeepseekProvider
                  ? 'deepseek-v4-pro (DSv4)'
                  : isMinimaxProvider
                    ? 'MiniMax (server-configured)'
                    : 'gpt-5.4 (CF.3)'}
              </span>
            </div>
          )}


          <button
            onClick={handleStartAnalyzing}
            disabled={isAnalyzing || !folderPath || sortBusy || !!(selectedFolderId && uploadMismatchIds.has(String(selectedFolderId)))}
            title={
              (selectedFolderId && uploadMismatchIds.has(String(selectedFolderId)))
                ? 'Local folder and website image counts differ — click Upload on the folder card to sync them before sorting.'
                : sortBusy ? sortBusyMsg : ''
            }
            className="start-btn"
          >
            {isAnalyzing
              ? 'Sorting...'
              : (selectedFolderId && uploadMismatchIds.has(String(selectedFolderId)))
                ? 'Local ≠ Web'
                : sortBusy
                  ? (aiStatus.sort.source === 'auto' ? 'Auto-sort running…' : 'Sort busy…')
                  : 'Start Sorting'}
          </button>

          {/* Get PDF — import online students' handwritten PDFs (named
              "(A01)Name.pdf") as sorted batches instead of running the AI sort.
              Rasterizes each PDF's pages → uploads as scans → builds one batch
              per student. Then the folder behaves like a normal sorted list. */}
          <button
            onClick={handleGetPdf}
            disabled={isAnalyzing || gettingPdf || gettingOcr || !folderPath}
            title={!folderPath
              ? 'Select a folder first'
              : 'Import student PDFs from this folder\'s local folder (online submissions). Each "(roll)Name.pdf" becomes one student; pages are converted to images and graded like scans.'}
            style={{
              background: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? '#ccc' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
              color: 'white',
              border: 'none',
              padding: '4px 10px',
              borderRadius: '6px',
              cursor: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? 'not-allowed' : 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              boxShadow: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? 'none' : '0 2px 8px rgba(14,165,233,0.3)'
            }}
          >
            <i className={gettingPdf ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-file-pdf'}></i>
            {gettingPdf ? 'Importing PDFs…' : 'Get PDF'}
          </button>

          {/* OCR — import roll/name from local ocr_results.json (odd pages
              only), same role as the MCQs OMR button. */}
          {results.length === 0 && (
            <button
              onClick={handleLoadOcrClick}
              disabled={isAnalyzing || gettingPdf || gettingOcr || !folderPath}
              title={!folderPath
                ? 'Select a folder first'
                : 'Import from local ocr_results.json — choose Overwrite or Append/Sync'}
              style={{
                background: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? '#ccc' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                color: 'white',
                border: 'none',
                padding: '4px 10px',
                borderRadius: '6px',
                cursor: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? 'none' : '0 2px 8px rgba(14,165,233,0.3)'
              }}
            >
              <i className={gettingOcr ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-language'}></i>
              {gettingOcr ? 'Getting Sorting…' : 'Get Sorting'}
            </button>
          )}

          {(() => {
            const canSave = !isAnalyzing && !!folderPath && results.length > 0;
            const canImport = !isAnalyzing && !!folderPath && !!snapshotMeta;
            // "Review" = pull the (auto-)sorted snapshot into the working
            // table so the examiner can look it over. Same import action as
            // before, just relabelled for the review workflow.
            const reviewTitle = !folderPath
              ? 'Select a folder first'
              : snapshotMeta
                ? `Load the sorted list for this folder to review it (${snapshotMeta.record_count} students, sorted ${new Date(snapshotMeta.saved_at).toLocaleString()}${snapshotMeta.saved_by ? ' by ' + snapshotMeta.saved_by : ''}, status: ${snapshotMeta.status || 'saved'})`
                : 'Nothing to review yet — this folder has not been sorted.';
            // "Reviewed" = save the current working list and flip the
            // folder's status to "reviewed". Requires a loaded list.
            const reviewedTitle = !folderPath
              ? 'Select a folder first'
              : results.length === 0
                ? 'Click "Review" to load the sorted list first, then mark it Reviewed'
                : `Mark this sorted list as Reviewed (saves the current ${results.length} student${results.length !== 1 ? 's' : ''} and flips the folder from "Review" → "Reviewed")`;
            const alreadyReviewed = snapshotMeta && snapshotMeta.status === 'reviewed';
            return (
              <>
                <button
                  onClick={handleImportSnapshot}
                  disabled={!canImport || snapshotBusy}
                  title={reviewTitle}
                  style={{
                    background: !canImport ? '#ccc' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    cursor: !canImport ? 'not-allowed' : 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: !canImport ? 'none' : '0 2px 8px rgba(99, 102, 241, 0.3)'
                  }}
                >
                  <i className={snapshotBusy ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-magnifying-glass'}></i>
                  Review
                </button>
                {results.length === 0 && (
                  <button
                    onClick={() => handleSaveSnapshot('reviewed')}
                    disabled={!canSave || snapshotBusy}
                    title={reviewedTitle}
                    style={{
                      background: !canSave ? '#ccc' : (alreadyReviewed ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'),
                      color: 'white',
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      cursor: !canSave ? 'not-allowed' : 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      boxShadow: !canSave ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    <i className={snapshotBusy ? 'fa-solid fa-spinner fa-spin' : (alreadyReviewed ? 'fa-solid fa-circle-check' : 'fa-solid fa-check')}></i>
                    {alreadyReviewed ? 'Reviewed ✓' : 'Reviewed'}
                  </button>
                )}
              </>
            );
          })()}

          {isAnalyzing && (
            <button onClick={handleStopAnalyzing} className="stop-btn">
              Stop Sorting
            </button>
          )}

          {results.length > 0 && (
            <>
              <button 
                onClick={handleReanalyzeSelected}
                disabled={isAnalyzing || selectedImages.length === 0}
                className="reanalyze-btn"
                style={{
                  background: selectedImages.length > 0 ? 'linear-gradient(135deg, #10B981, #059669)' : '#ccc',
                  color: 'white',
                  padding: '4px 10px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: selectedImages.length > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '0.75rem'
                }}
              >
                Reanalyze Selected ({selectedImages.length})
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={selectedImages.length === 0}
                style={{
                  background: selectedImages.length > 0 ? 'linear-gradient(135deg, #F59E0B, #D97706)' : '#ccc',
                  color: 'white',
                  padding: '4px 10px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: selectedImages.length > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '0.75rem'
                }}
                title="Delete selected records"
              >
                Delete Selected ({selectedImages.length})
              </button>
              <button onClick={handleDeleteAll} className="delete-all-btn">
                Delete All
              </button>
            </>
          )}
        </div>
        )}

        {progress && (
          <div className="progress-indicator">
            <p>{progress}</p>
            {progressDetails && progressDetails.status === 'processing' && (
              <div className="progress-details">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${(progressDetails.completed / progressDetails.total) * 100}%` }}
                  />
                </div>
                <p className="progress-stats">
                  {progressDetails.completed} / {progressDetails.total} scans completed
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {summary && (
        <div className="analysis-summary">
          <h3>Analysis Summary</h3>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">Total Images:</span>
              <span className="stat-value">{summary.total_images}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Unique Students:</span>
              <span className="stat-value">{summary.unique_students}</span>
            </div>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="results-section" style={calendarCollapsed ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
          {/* Single compact row that fuses three previously stacked rows:
              (1) "Analysis Results (N records)" + Saved-with-folder pill +
                  folder badge + Last Analyzed pill
              (2) Legend chips (Invalid Roll / AI Error / Selected)
              (3) View toggle (Reconciliation | Flat AI View) + status text.
              Wraps gracefully on narrow viewports but stays on one line at
              typical widths so the results table gets more vertical room. */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: '6px',
            padding: '4px 8px',
            background: 'var(--surface, #F3F4F6)',
            borderRadius: '6px'
          }}>
            <h3 style={{ margin: 0, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              Analysis Results ({results.length})
              {selectedFolderId && scansFolders.find(f => String(f.id) === String(selectedFolderId)) && (
                <span style={{
                  fontSize: '0.68rem',
                  fontWeight: '600',
                  color: 'white',
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  padding: '1px 6px',
                  borderRadius: '12px'
                }}>
                  <i className="fa-solid fa-folder" style={{ marginRight: '3px' }}></i>
                  {scansFolders.find(f => String(f.id) === String(selectedFolderId)).title}
                </span>
              )}
              <span
                title="This sorted list is permanently saved with the folder. Every edit auto-saves."
                style={{
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  color: 'white',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  padding: '1px 6px',
                  borderRadius: '12px',
                  cursor: 'help'
                }}
              >
                <i className="fa-solid fa-circle-check" style={{ marginRight: '3px' }}></i>
                Saved · {results.length}
              </span>
            </h3>

            {/* View toggle */}
            <div style={{ display: 'inline-flex', borderRadius: '5px', overflow: 'hidden', border: '1px solid var(--border-color, #d1d5db)' }}>
              <button
                onClick={() => setViewMode('reconciliation')}
                style={{
                  padding: '3px 8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.72rem',
                  background: viewMode === 'reconciliation' ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'transparent',
                  color: viewMode === 'reconciliation' ? 'white' : 'var(--text-primary)'
                }}
              >
                <i className="fa-solid fa-people-arrows" style={{ marginRight: '3px' }}></i>Reconciliation
              </button>
              <button
                onClick={() => setViewMode('flat')}
                style={{
                  padding: '3px 8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.72rem',
                  background: viewMode === 'flat' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'transparent',
                  color: viewMode === 'flat' ? 'white' : 'var(--text-primary)'
                }}
              >
                <i className="fa-solid fa-table" style={{ marginRight: '3px' }}></i>Flat
              </button>
            </div>

            {viewMode === 'reconciliation' && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {importedStudentsLoading
                  ? 'Loading…'
                  : importedStudents.length > 0
                    ? <>Imported <strong>{importedStudents.length}</strong> from <strong>{importedListsMeta.length}</strong> list{importedListsMeta.length !== 1 ? 's' : ''}</>
                    : <span style={{ color: '#dc2626' }}>No matching student list for this folder's class + subject</span>
                }
                {importedStudents.length > 0 && (
                  <button
                    onClick={() => {
                      if (!window.confirm('Clear imported student list for this folder? You can re-import by reselecting the folder.')) return;
                      clearedImportFolderRef.current = String(selectedFolderId);
                      setImportedStudents([]);
                      setImportedListsMeta([]);
                    }}
                    title="Clear imported student list"
                    style={{
                      background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px',
                      padding: '2px 6px', cursor: 'pointer', fontSize: '10px', fontWeight: 600
                    }}
                  >
                    <i className="fa-solid fa-trash" style={{ marginRight: '3px' }}></i>Clear
                  </button>
                )}
              </span>
            )}

            {/* Vertical divider before legend */}
            <span style={{ width: 1, height: 20, background: 'var(--border-color, #d1d5db)' }}></span>

            {/* Legend chips — compact icon swatches with shortened labels.
                Each chip toggles a sort highlight when clicked. */}
            <div
              onClick={() => { setSortConfig({ column: null, direction: 'asc' }); setHighlightSort(prev => prev === 'zero' ? null : 'zero'); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '3px 7px', borderRadius: '5px', background: highlightSort === 'zero' ? '#FEF08A' : 'transparent', border: highlightSort === 'zero' ? '2px solid #CA8A04' : '2px solid transparent', fontSize: '0.75rem' }}
              title="Click to sort: Invalid roll number rows to top"
            >
              <div style={{ width: '14px', height: '14px', background: '#FEF9C3', border: '1px solid #FDE047', borderRadius: '3px' }}></div>
              <span>Invalid Roll</span>
            </div>
            <div
              onClick={() => { setSortConfig({ column: null, direction: 'asc' }); setHighlightSort(prev => prev === 'error' ? null : 'error'); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '3px 7px', borderRadius: '5px', background: highlightSort === 'error' ? '#FFE0E0' : 'transparent', border: highlightSort === 'error' ? '2px solid #EF4444' : '2px solid transparent', fontSize: '0.75rem' }}
              title="AI Reading Error (Same name/different roll OR Same roll/different name)"
            >
              <div style={{ width: '14px', height: '14px', background: 'var(--select-warning-bg, #FFE0E0)', border: '1px solid #FCA5A5', borderRadius: '3px' }}></div>
              <span>AI Error</span>
            </div>
            <div
              onClick={() => { setSortConfig({ column: null, direction: 'asc' }); setHighlightSort(prev => prev === 'selected' ? null : 'selected'); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '3px 7px', borderRadius: '5px', background: highlightSort === 'selected' ? '#E0F2FE' : 'transparent', border: highlightSort === 'selected' ? '2px solid #3B82F6' : '2px solid transparent', fontSize: '0.75rem' }}
              title="Click to sort: Selected rows to top"
            >
              <div style={{ width: '14px', height: '14px', background: 'var(--select-blue-bg, #E0F2FE)', border: '1px solid #7DD3FC', borderRadius: '3px' }}></div>
              <span>Selected</span>
            </div>

            {/* Right-side actions + Last Analyzed pill */}
            {results.length > 0 && (
              <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleLoadOcrClick}
                  disabled={isAnalyzing || gettingPdf || gettingOcr || !folderPath}
                  title={!folderPath
                    ? 'Select a folder first'
                    : 'Import from local ocr_results.json — choose Overwrite or Append/Sync'}
                  style={{
                    background: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? '#ccc' : 'linear-gradient(135deg, #CA8A04 0%, #A16207 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    cursor: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? 'not-allowed' : 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: (isAnalyzing || gettingPdf || gettingOcr || !folderPath) ? 'none' : '0 2px 8px rgba(202,138,4,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <i className={gettingOcr ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-language'}></i>
                  {gettingOcr ? 'Getting Sorting…' : 'Get Sorting'}
                </button>
                {(() => {
                  const canSave = !isAnalyzing && !!folderPath && results.length > 0;
                  const reviewedTitle = !folderPath
                    ? 'Select a folder first'
                    : results.length === 0
                      ? 'Click "Review" to load the sorted list first, then mark it Reviewed'
                      : `Mark this sorted list as Reviewed (saves the current ${results.length} student${results.length !== 1 ? 's' : ''} and flips the folder from "Review" → "Reviewed")`;
                  const alreadyReviewed = snapshotMeta && snapshotMeta.status === 'reviewed';
                  return (
                    <button
                      onClick={() => handleSaveSnapshot('reviewed')}
                      disabled={!canSave || snapshotBusy}
                      title={reviewedTitle}
                      style={{
                        background: !canSave ? '#ccc' : 'linear-gradient(135deg, #CA8A04 0%, #A16207 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        cursor: !canSave ? 'not-allowed' : 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: !canSave ? 'none' : '0 2px 8px rgba(202,138,4,0.3)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <i className={snapshotBusy ? 'fa-solid fa-spinner fa-spin' : (alreadyReviewed ? 'fa-solid fa-circle-check' : 'fa-solid fa-check')}></i>
                      {alreadyReviewed ? 'Reviewed ✓' : 'Reviewed'}
                    </button>
                  );
                })()}
                {results[0].created_at && (
                  <div style={{
                    background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    whiteSpace: 'nowrap'
                  }}>
                    <i className="fa-solid fa-clock"></i>
                    Last Analyzed: {new Date(results[0].created_at).toLocaleString('en-PK', {
                      timeZone: 'Asia/Karachi',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })} PKT
                  </div>
                )}
              </div>
            )}
          </div>

          {viewMode === 'reconciliation' && (() => {
            const { matched, missingStudents, unmatchedResults } = buildReconciliation();

            // Apply Expected / Image column sort to the left imported list
            // and (for Image) the unmatched pane.
            let displayImported = [...importedStudents];
            if (sortConfig.column === 'expected_name') {
              displayImported.sort((a, b) => {
                const aVal = String(a.student_name || '').toLowerCase();
                const bVal = String(b.student_name || '').toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
              });
            } else if (sortConfig.column === 'image_no') {
              const firstImage = (stu) => {
                const rows = matched.find(m => m.expected === stu)?.aiRows || [];
                return rows[0]?.image_no || '';
              };
              displayImported.sort((a, b) =>
                compareImageNames(firstImage(a), firstImage(b), sortConfig.direction)
              );
            }

            let displayUnmatched = [...unmatchedResults];
            if (sortConfig.column === 'image_no') {
              displayUnmatched.sort((a, b) =>
                compareImageNames(a.image_no, b.image_no, sortConfig.direction)
              );
            } else if (sortConfig.column === 'expected_name') {
              // Unmatched has no Expected column — sort by AI Read name instead
              displayUnmatched.sort((a, b) => {
                const aVal = String(a.roll_name || '').toLowerCase();
                const bVal = String(b.roll_name || '').toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
              });
            }
            // Rebalanced after the initial over-tightening — the first
            // pass at `6px 10px` / `0.82rem` felt cramped per user
            // feedback. New baseline: rows are roomy enough to scan
            // comfortably (~10px vertical) while still denser than the
            // pre-tightened `12px 14px`. Numeric cells centred via
            // `cellPadCenter` below.
            const cellPad = { padding: '11px 14px', verticalAlign: 'middle', borderTop: '1px solid var(--border-color, #e5e7eb)', fontSize: '0.86rem' };
            const cellPadCenter = { ...cellPad, textAlign: 'center' };
            const headStyle = { padding: '11px 14px', textAlign: 'left', background: 'var(--surface, #F3F4F6)', fontWeight: 700, fontSize: '0.76rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary, #475569)', borderBottom: '2px solid var(--border-color, #d1d5db)' };
            const headStyleCenter = { ...headStyle, textAlign: 'center' };
            const sectionLabelRow = (label, color, count) => (
              <tr>
                <td colSpan={7} style={{ padding: '10px 12px', background: color, fontWeight: 700, color: 'white', fontSize: '0.85rem' }}>
                  {label} ({count})
                </td>
              </tr>
            );
            const renderAiCell = (r, onNameClick) => {
              if (editingCell?.id === r.id && editingCell?.field === 'roll_name') {
                return (
                  <input
                    type="text"
                    value={editingCell.value}
                    onChange={(e) => handleCellChange(e.target.value)}
                    onBlur={handleCellBlur}
                    onKeyDown={handleCellKeyDown}
                    autoFocus
                    onFocus={(e) => {
                      if (!editingCell.selectInsideParens) return;
                      const el = e.target;
                      const match = /\(([^)]*)\)/.exec(el.value);
                      if (!match) return;
                      const start = match.index + 1;
                      const end = start + match[1].length;
                      const place = () => el.setSelectionRange(start, end);
                      requestAnimationFrame(place);
                      setTimeout(place, 0);
                    }}
                    onMouseUp={(e) => {
                      if (!editingCell.selectInsideParens || e.target.dataset.parenPlaced) return;
                      const el = e.target;
                      const match = /\(([^)]*)\)/.exec(el.value);
                      if (!match) return;
                      el.dataset.parenPlaced = '1';
                      const start = match.index + 1;
                      el.setSelectionRange(start, start + match[1].length);
                    }}
                    style={{ width: '100%', padding: '4px', border: '2px solid #3B82F6', borderRadius: '4px', fontSize: 'inherit', fontFamily: 'inherit' }}
                  />
                );
              }
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    onClick={onNameClick ? (e) => { e.stopPropagation(); onNameClick(); } : undefined}
                    title={onNameClick ? 'Open this image and edit the roll inside the parentheses' : undefined}
                    style={onNameClick ? { cursor: 'pointer' } : undefined}
                  >
                    <strong>{r.roll_name}</strong> <span style={{ color: '#6b7280' }}>({r.unique_code})</span>
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCopiedRollName(r.roll_name); showMessage(`Copied: ${r.roll_name}`, 'success'); }}
                    title="Copy roll name"
                    style={{
                      background: copiedRollName === r.roll_name ? '#059669' : '#6366F1',
                      color: 'white', border: 'none', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px'
                    }}
                  >
                    <i className="fa-solid fa-copy" />
                  </button>
                  {copiedRollName && copiedRollName !== r.roll_name && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePasteRollName(r.id, r.roll_name); }}
                      title={`Paste: ${copiedRollName}`}
                      style={{
                        background: '#F59E0B',
                        color: 'white', border: 'none', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px'
                      }}
                    >
                      <i className="fa-solid fa-paste" />
                    </button>
                  )}
                </div>
              );
            };
            const renderExpectedCell = (expected) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <strong>{expected.student_name}</strong>
                <button
                  onClick={(e) => { e.stopPropagation(); setCopiedRollName(expected.student_name); showMessage(`Copied: ${expected.student_name}`, 'success'); }}
                  title="Copy expected name (then click paste on the AI row)"
                  style={{
                    background: copiedRollName === expected.student_name ? '#059669' : '#6366F1',
                    color: 'white', border: 'none', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px'
                  }}
                >
                  <i className="fa-solid fa-copy" />
                </button>
              </div>
            );
            const renderActions = (r) => (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
                <button
                  onClick={() => handlePreviewImage(r.image_no, r.folder_path)}
                  disabled={previewLoading === (r.image_no || '').split(',')[0].trim()}
                  title="View image"
                  style={{ background: '#7C3AED', color: 'white', border: 'none', padding: '5px 9px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', lineHeight: 1.2 }}
                >
                  <i className="fa-solid fa-image" />
                </button>
                <button
                  onClick={() => handleDeleteRecord(r.id)}
                  title="Delete row"
                  style={{ background: '#EF4444', color: 'white', border: 'none', padding: '5px 9px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, lineHeight: 1.2 }}
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            );
            // Section-header style that sits at the top of each side-table.
            // Sticky so it stays visible while the user scrolls long lists.
            const stickyHead = { ...headStyle, position: 'sticky', top: 0, zIndex: 1 };
            // Tall scroll container — the table keeps its own scrollbar so
            // the column headers (sticky panel header + sticky <thead>) stay
            // pinned while the user pages through long lists. Height depends
            // on whether the calendar+folder rail is showing: when it's
            // collapsed (`calendarCollapsed`) the page chrome above is short
            // so we give the panel ~calc(100vh - 280px) and it fits cleanly
            // inside the viewport with no page-level scroll. With the
            // calendar visible, page scroll is expected and the looser
            // calc(100vh - 320px) still leaves the panels generously sized.
            const panelStyle = {
              flex: 1,
              minWidth: 0,
              ...(calendarCollapsed
                ? { height: '100%', minHeight: 0 }
                : { maxHeight: 'calc(100vh - 320px)' }),
              overflowY: 'auto',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: '8px',
              background: 'var(--card-bg, #ffffff)'
            };
            const panelHeader = (color, label, count) => (
              <div style={{
                position: 'sticky', top: 0, zIndex: 2,
                background: color, color: 'white',
                padding: '8px 12px', fontWeight: 700, fontSize: '0.85rem'
              }}>
                {label} ({count})
              </div>
            );

            const emptyAll = importedStudents.length === 0 && unmatchedResults.length === 0;
            if (emptyAll) {
              return (
                <div className="results-table-container">
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px' }}>
                    No imported student list available — switch to Flat AI View or import a student list for this class+subject in the Students tab.
                  </div>
                </div>
              );
            }

            return (
              // Two-pane reconciliation: imported students on the LEFT
              // (matched + missing inline), unmatched AI rows on the RIGHT.
              // Each pane has its own scroll container with maxHeight: 70vh
              // so scrolling one doesn't move the other.
              //
              // Horizontal padding centres the panel pair so the tables
              // don't run flush to the page edges. Inline style block
              // injects a row-hover tint via background-image so it
              // overlays the inline `background-color` on tinted rows
              // (duplicate-roll red, name-mismatch yellow) without
              // overriding their tint colour.
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'center', padding: '0 72px', ...(calendarCollapsed ? { flex: 1, minHeight: 0 } : null) }}>
                <style>{`
                  .reconciliation-table tbody tr { transition: background-image 0.12s ease; }
                  .reconciliation-table tbody tr:hover > td {
                    background-image: linear-gradient(rgba(59,130,246,0.14), rgba(59,130,246,0.14));
                  }
                `}</style>

                {/* LEFT — Imported students (matched + missing).
                    Width rule: 50% when the Unmatched pane is also
                    showing (mistakes still present) so the matched
                    table dominates without crowding out the unmatched
                    pane; 60% once everything is matched and the
                    Unmatched pane is hidden, so the table breathes out
                    a bit instead of stretching to the container's
                    full inner width. */}
                <div style={{
                  ...panelStyle,
                  flex: unmatchedResults.length > 0 ? '0 0 50%' : '0 0 60%'
                }}>
                  {panelHeader('#7c3aed', 'Students from Imported List', importedStudents.length)}
                  <table className="results-table reconciliation-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th
                          style={{ ...stickyHead, top: '34px', width: '24%', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleReconciliationSort('expected_name')}
                          title="Sort by Expected name"
                        >
                          Expected{sortIndicator('expected_name')}
                        </th>
                        <th style={{ ...stickyHead, top: '34px', width: '28%' }}>AI Read</th>
                        <th
                          style={{ ...stickyHead, top: '34px', width: '17%', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleReconciliationSort('image_no')}
                          title="Sort by Image"
                        >
                          Image{sortIndicator('image_no')}
                        </th>
                        <th style={{ ...stickyHead, top: '34px', width: 56, ...{ textAlign: 'center' } }}>Sht</th>
                        <th style={{ ...stickyHead, top: '34px', width: 56, ...{ textAlign: 'center' } }}>Btc</th>
                        <th style={{ ...stickyHead, top: '34px', width: '15%' }}>Status</th>
                        <th style={{ ...stickyHead, top: '34px', width: 78, ...{ textAlign: 'center' } }}>Acts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayImported.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                          No imported student list for this folder.
                        </td></tr>
                      ) : displayImported.map(expected => {
                        // Match by ROLL CODE (not name) — find the matched
                        // bucket whose expected student is this same row.
                        const expectedRoll = extractRollCode(expected.student_name);
                        const aiRows = matched.find(m => m.expected === expected)?.aiRows || [];

                        if (aiRows.length === 0) {
                          return (
                            <tr key={`s-${expected.id || expected.student_name}`} style={{ background: '#F9FAFB' }}>
                              <td style={cellPad}>{renderExpectedCell(expected)}</td>
                              <td style={cellPad} colSpan={4}><em style={{ color: '#6b7280', fontSize: '0.78rem' }}>— no AI match —</em></td>
                              <td style={cellPad}><span style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.78rem' }}>✗ Missing</span></td>
                              <td style={cellPadCenter}></td>
                            </tr>
                          );
                        }

                        return aiRows.map((r, idx) => {
                          // Row tint priority: duplicate roll (red) >
                          // name mismatch (yellow) > clean (transparent).
                          // Duplicate-roll always wins because two students
                          // sharing one roll is a bigger problem than a
                          // single-row name typo.
                          const rowBg = r._isDuplicateRoll
                            ? '#FEE2E2'
                            : r._nameMismatch
                              ? '#FEF9C3'
                              : 'transparent';
                          return (
                            <tr key={`s-${expected.id || expected.student_name}-${r.id}`} style={{ background: rowBg }}>
                              <td style={cellPad}>
                                {idx === 0 ? renderExpectedCell(expected) : <span style={{ color: '#9ca3af' }}>↳</span>}
                              </td>
                              <td
                                style={{ ...cellPad, cursor: 'pointer' }}
                                onDoubleClick={() => handleCellEdit(r.id, 'roll_name', r.roll_name)}
                                title="Double-click to edit"
                              >
                                {renderAiCell(r)}
                              </td>
                              <td style={{ ...cellPad, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.image_no}>{r.image_no}</td>
                              <td style={cellPadCenter}>{r.sheet_count}</td>
                              <td style={cellPadCenter}>{r.batch_size_final}</td>
                              <td style={cellPad}>
                                {r._isDuplicateRoll ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ color: '#b91c1c', fontWeight: 700 }}>
                                      ⚠ DUPLICATE roll{r._nameMismatch ? ' + name mismatch' : ''}
                                    </span>
                                    {r._nameMismatch && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); acceptExpectedName(r.id, expected.student_name); }}
                                        title={`Replace "${r.roll_name}" with "${expected.student_name}" from the imported list`}
                                        style={{
                                          background: '#10b981', color: 'white', border: 'none',
                                          padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
                                          fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap'
                                        }}
                                      >
                                        <i className="fa-solid fa-check" />
                                      </button>
                                    )}
                                  </div>
                                ) : r._nameMismatch ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ color: '#a16207', fontWeight: 700 }}>
                                      ⚠ Name mismatch
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); acceptExpectedName(r.id, expected.student_name); }}
                                      title={`Replace "${r.roll_name}" with "${expected.student_name}" from the imported list`}
                                      style={{
                                        background: '#10b981', color: 'white', border: 'none',
                                        padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
                                        fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap'
                                      }}
                                    >
                                      <i className="fa-solid fa-check" />
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: '#059669', fontWeight: 600 }}>✓ OK</span>
                                )}
                              </td>
                              <td style={cellPadCenter}>{renderActions(r)}</td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>

                {/* RIGHT — Unmatched AI rows. No "Expected" column since the
                    whole point of this pane is rows that don't match anything.
                    Hidden entirely when there are 0 unmatched rows so the left
                    pane gets the full width (it's the common happy-path case
                    after a clean Sort run). */}
                {unmatchedResults.length > 0 && (
                <div style={{ ...panelStyle, flex: '0 0 35%' }}>
                  {panelHeader('#dc2626', '⚠ Unmatched AI Rows', unmatchedResults.length)}
                  <table className="results-table reconciliation-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ ...stickyHead, top: '34px', width: '36%' }}>AI Read</th>
                        <th
                          style={{ ...stickyHead, top: '34px', width: '28%', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleReconciliationSort('image_no')}
                          title="Sort by Image"
                        >
                          Image{sortIndicator('image_no')}
                        </th>
                        <th style={{ ...stickyHead, top: '34px', width: 50, textAlign: 'center' }}>Sht</th>
                        <th style={{ ...stickyHead, top: '34px', width: 50, textAlign: 'center' }}>Btc</th>
                        <th style={{ ...stickyHead, top: '34px', width: 78, textAlign: 'center' }}>Acts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayUnmatched.map(r => (
                        <tr key={`u-${r.id}`} style={{ background: r._isDuplicateRoll ? '#FEE2E2' : '#FEF2F2' }}>
                          <td
                            style={cellPad}
                            title={r._isDuplicateRoll
                              ? 'Duplicate roll — another row in this folder shares the same unique_code. Delete the wrong one(s) and the remaining row will match its imported student.'
                              : undefined}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                              {r._isDuplicateRoll && (
                                <span
                                  title="Two or more AI rows share this roll number"
                                  style={{
                                    background: '#b91c1c',
                                    color: 'white',
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '1px 5px',
                                    borderRadius: 4,
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  ⚠ DUP
                                </span>
                              )}
                              {renderAiCell(r, () => {
                                if (r.image_no) handlePreviewImage(r.image_no, r.folder_path);
                                handleCellEdit(r.id, 'roll_name', r.roll_name, { selectInsideParens: true });
                              })}
                            </div>
                          </td>
                          <td style={{ ...cellPad, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.image_no}>{r.image_no}</td>
                          <td style={cellPadCenter}>{r.sheet_count}</td>
                          <td style={cellPadCenter}>{r.batch_size_final}</td>
                          <td style={cellPadCenter}>{renderActions(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}

              </div>
            );
          })()}

          {viewMode === 'flat' && (
          <div className="results-table-container" style={calendarCollapsed ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}>
            <table className="results-table">
              <thead>
                <tr>
                  <th>
                    <input 
                      type="checkbox" 
                      checked={selectedImages.length === results.length && results.length > 0}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th onClick={() => handleSort('roll_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Roll_Name
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'roll_name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th onClick={() => handleSort('image_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Image_No
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'image_no' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th onClick={() => handleSort('unique_code')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Unique_Code
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'unique_code' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th onClick={() => handleSort('sheet_count')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Sheet_Count
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'sheet_count' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th onClick={() => handleSort('batch_size_final')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Batch_Size_Final
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'batch_size_final' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
              {(() => {
                if (sortConfig.column) return results;
                if (highlightSort === 'error') {
                  const errorRows = results.filter(r => hasAIError(r));
                  const rest = results.filter(r => !hasAIError(r));
                  return [...errorRows, ...rest];
                }
                if (highlightSort === 'selected') {
                  const selRows = results.filter(r => selectedImages.includes(r.image_no));
                  const rest = results.filter(r => !selectedImages.includes(r.image_no));
                  return [...selRows, ...rest];
                }
                // Default / 'zero': invalid roll rows on top, then errors, then normal
                const isInvalidRoll = r => !/^[A-Za-z]\d{2}$/.test(r.unique_code);
                const zeroRows = results.filter(r => isInvalidRoll(r));
                const errorRows = results.filter(r => !isInvalidRoll(r) && hasAIError(r));
                const normalRows = results.filter(r => !isInvalidRoll(r) && !hasAIError(r));
                return [...zeroRows, ...errorRows, ...normalRows];
              })().map((result, index) => {
                  const isZeroCode = !/^[A-Za-z]\d{2}$/.test(result.unique_code);
                  const hasError = !isZeroCode && hasAIError(result);
                  const backgroundColor = isZeroCode
                    ? '#FEF9C3' // Yellow for invalid roll number
                    : hasError
                      ? 'var(--select-warning-bg, #FFE0E0)' // Light red for AI errors
                      : selectedImages.includes(result.image_no)
                        ? 'var(--select-blue-bg, #E0F2FE)' // Light blue for selected
                        : 'transparent';
                  
                  return (
                  <tr
                    key={index}
                    onClick={(e) => {
                      // Single-click anywhere on the row toggles selection,
                      // EXCEPT when the click landed on a form control / link
                      // (those have their own handlers) or while a cell on
                      // this row is being edited (clicks inside the edit
                      // input must not deselect the row).
                      const tag = (e.target.tagName || '').toLowerCase();
                      if (['input', 'button', 'select', 'textarea', 'a', 'label'].includes(tag)) return;
                      if (editingCell && editingCell.id === result.id) return;
                      handleSelectImage(result.image_no);
                    }}
                    style={{ background: backgroundColor, cursor: 'pointer' }}
                  >
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedImages.includes(result.image_no)}
                        onChange={() => handleSelectImage(result.image_no)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'roll_name', result.roll_name)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'roll_name' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span>{result.roll_name}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCopiedRollName(result.roll_name); showMessage(`Copied: ${result.roll_name}`, 'success'); }}
                            title="Copy roll name"
                            style={{
                              background: copiedRollName === result.roll_name ? '#059669' : '#6366F1',
                              color: 'white', border: 'none', borderRadius: '4px',
                              width: '22px', height: '22px', cursor: 'pointer',
                              fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <i className="fa-solid fa-copy" />
                          </button>
                          {copiedRollName && copiedRollName !== result.roll_name && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePasteRollName(result.id, result.roll_name); }}
                              title={`Paste: ${copiedRollName}`}
                              style={{
                                background: '#F59E0B',
                                color: 'white', border: 'none', borderRadius: '4px',
                                width: '22px', height: '22px', cursor: 'pointer',
                                fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <i className="fa-solid fa-paste" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'image_no', result.image_no)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'image_no' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.image_no
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'unique_code', result.unique_code)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'unique_code' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.unique_code
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'sheet_count', result.sheet_count)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'sheet_count' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.sheet_count
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'batch_size_final', result.batch_size_final)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'batch_size_final' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.batch_size_final
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => handlePreviewImage(result.image_no, result.folder_path)}
                          disabled={previewLoading === result.image_no.split(',')[0].trim()}
                          title="View image"
                          style={{
                            background: '#7C3AED',
                            color: 'white',
                            border: 'none',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          <i className="fa-solid fa-image" />
                        </button>
                        <button
                          onClick={() => handleDeleteRecord(result.id)}
                          style={{
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#DC2626'}
                          onMouseLeave={(e) => e.target.style.background = '#EF4444'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {editingRubricId && (() => {
        const folder = (scansFolders || []).find((f) => String(f.id) === String(editingRubricId)) || { id: editingRubricId };
        const original = (rubricPreviewCache[String(editingRubricId)] && rubricPreviewCache[String(editingRubricId)].text) || '';
        const unchanged = rubricEditSaving || rubricEditDraft === original;
        return (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 10000,
              background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 24
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--card-bg, #fff)',
                color: 'var(--text-primary, #1f2937)',
                borderRadius: 16,
                width: 'min(1200px, calc(100vw - 48px))',
                height: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
                padding: '18px 20px 14px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                <strong style={{ fontSize: 16, color: '#059669' }}>
                  Edit rubric — {folderDisplayTitle(folder)}
                </strong>
                <button
                  type="button"
                  onClick={cancelFolderRubricEdit}
                  disabled={rubricEditSaving}
                  title="Close"
                  style={{
                    background: 'transparent',
                    color: 'var(--text-secondary, #6b7280)',
                    border: 'none',
                    fontSize: 22,
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: '0 4px'
                  }}
                >
                  ×
                </button>
              </div>
              <textarea
                dir="auto"
                autoFocus
                value={rubricEditDraft}
                onChange={(ev) => { setRubricEditDraft(ev.target.value); setRubricEditNote(''); }}
                onKeyDown={(ev) => {
                  if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') {
                    ev.preventDefault();
                    saveFolderRubricEdit(ev, folder);
                  } else if (ev.key === 'Escape') {
                    ev.preventDefault();
                    cancelFolderRubricEdit(ev);
                  }
                }}
                spellCheck={false}
                style={{
                  flex: 1,
                  width: '100%',
                  minHeight: 0,
                  resize: 'none',
                  boxSizing: 'border-box',
                  padding: '12px 14px',
                  border: '1px solid #10b981',
                  borderRadius: 8,
                  font: 'inherit',
                  fontSize: '1rem',
                  lineHeight: 1.65,
                  color: 'var(--text-primary, #1f2937)',
                  background: 'var(--card-bg, #fff)'
                }}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={(ev) => saveFolderRubricEdit(ev, folder)}
                  disabled={unchanged}
                  style={{
                    background: unchanged ? '#e5e7eb' : '#059669',
                    color: unchanged ? '#6b7280' : 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 16px',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: unchanged ? 'default' : 'pointer'
                  }}
                >
                  {rubricEditSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={cancelFolderRubricEdit}
                  disabled={rubricEditSaving}
                  style={{
                    background: 'transparent',
                    color: 'var(--text-primary, #1f2937)',
                    border: '1px solid var(--border, #E5E7EB)',
                    borderRadius: 6,
                    padding: '8px 16px',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <span style={{ fontSize: 13, color: rubricEditNote ? '#b91c1c' : 'var(--text-secondary, #6b7280)' }}>
                  {rubricEditNote || 'Edits apply to every student in this folder. Ctrl+Enter saves. Escape cancels.'}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {previewImage && (
        <DraggableImagePanel
          url={previewImage.url}
          name={previewImage.name}
          onClose={() => setPreviewImage(null)}
          // Sort tab only: frame the zoomed page, with no empty
          // space beside the paper, and keep the name / roll header in view.
          width="78vw"
          imageHeight="32vh"
          stickTop
          hugPaper
          initialFocus="top20"
          initialZoom={4.76}
          defaultTool="hand"
          availableTools={['hand', 'rect', 'eraser']}
          fixedStrokeWidth={5}
        />
      )}

      {reLateLogOpen && (
        <div
          onClick={() => setReLateLogOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px',
              padding: '22px 22px 16px', maxWidth: '760px', width: '92%',
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1F2937' }}>
                Re-Late log
              </h3>
              <button
                type="button"
                onClick={() => setReLateLogOpen(false)}
                style={{
                  border: '1px solid #E5E7EB', borderRadius: 8,
                  background: 'white', color: '#374151',
                  padding: '4px 10px', fontWeight: 700, cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            {reLateLogLoading && (
              <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>Loading…</p>
            )}
            {!reLateLogLoading && reLateLogError && (
              <p style={{ margin: 0, fontSize: 14, color: '#B91C1C' }}>{reLateLogError}</p>
            )}
            {!reLateLogLoading && !reLateLogError && reLateLogRows.length === 0 && (
              <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
                No image adds or deletes have been logged yet. New changes will appear here with their time.
              </p>
            )}
            {!reLateLogLoading && !reLateLogError && reLateLogRows.length > 0 && (
              <div style={{ overflowY: 'auto' }}>
                {reLateLogRows.map((row) => {
                  const added = row.action !== 'deleted';
                  const removed = reLateRemovedLines(row.detail);
                  return (
                    <div
                      key={row.id}
                      style={{
                        padding: '8px 0',
                        borderTop: '1px solid #F3F4F6',
                        fontSize: 13,
                        color: '#1F2937'
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(140px, 1.1fr) minmax(0, 1.6fr) 72px 78px',
                          gap: 8,
                          alignItems: 'center'
                        }}
                      >
                        <span>{formatFolderPkt(row.created_at)}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {reLateClassLabel(row)}
                        </span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.image_number}</span>
                        <span style={{ fontWeight: 700, color: added ? '#0369A1' : '#C2410C' }}>
                          {added ? 'Added' : 'Deleted'}
                        </span>
                      </div>
                      {removed.map((line, index) => (
                        <div key={`${row.id}-${index}`} style={{ marginTop: 2, color: '#4B5563', lineHeight: 1.4 }}>
                          {line}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Get Sorting — Entire Class vs Late Test/Re-test confirmation */}
      {ocrSortDialog && (
        <div
          onClick={() => setOcrSortDialog(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px',
              padding: '28px 28px 22px', maxWidth: '520px', width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #E0F2FE, #BAE6FD)',
              border: '2px solid #0EA5E9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <i className="fa-solid fa-language" style={{ fontSize: '22px', color: '#0284C7' }}></i>
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 700, color: '#1F2937', textAlign: 'center' }}>
              Get Sorting
            </h3>
            <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#374151', lineHeight: 1.55, textAlign: 'center' }}>
              Import roll/name from <strong>ocr_results.json</strong> for{' '}
              <strong>{ocrSortDialog.folderTitle}</strong> (odd pages only).
            </p>
            <p style={{ margin: '0 0 22px', fontSize: '13px', color: '#6B7280', lineHeight: 1.55 }}>
              {ocrSortDialog.hasExistingList
                ? 'This folder already has a sorted list. Choose how to import:'
                : 'This will import all OCR front pages into the sorted table.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {ocrSortDialog.hasExistingList && (
                <button
                  onClick={() => {
                    setOcrSortDialog(null);
                    runLoadOcr({ overwrite: false });
                  }}
                  style={{
                    padding: '11px 16px', borderRadius: '8px', border: 'none',
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    color: 'white', fontWeight: 700, fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(16,185,129,0.35)'
                  }}
                >
                  Late Test/Re-test
                </button>
              )}
              <button
                onClick={() => {
                  setOcrSortDialog(null);
                  runLoadOcr({ overwrite: true });
                }}
                style={{
                  padding: '11px 16px', borderRadius: '8px', border: 'none',
                  background: ocrSortDialog.hasExistingList
                    ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                    : 'linear-gradient(135deg, #0EA5E9, #0284C7)',
                  color: 'white', fontWeight: 700, fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: ocrSortDialog.hasExistingList
                    ? '0 4px 12px rgba(245,158,11,0.35)'
                    : '0 4px 12px rgba(14,165,233,0.35)'
                }}
              >
                {ocrSortDialog.hasExistingList
                  ? 'Entire Class'
                  : 'Import all from OCR'}
              </button>
              <button
                onClick={() => setOcrSortDialog(null)}
                style={{
                  padding: '10px 16px', borderRadius: '8px',
                  border: '1px solid #D1D5DB', background: 'white',
                  color: '#374151', fontWeight: 600, fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blocking pre-flight dialog: 0 images, duplicate images, or a late paper that cannot open */}
      <FailureNotice notice={blockedDialog} onClose={() => setBlockedDialog(null)} />
      {reportFolder && (
        <QuestionReportSheet
          folder={reportFolder}
          title={folderDisplayTitle(reportFolder)}
          onClose={() => setReportFolder(null)}
          onSwitchFolder={setReportFolder}
          onReviewChange={(review) => applyFolderReview(reportFolder.id, review)}
        />
      )}
      {quickTranscript && (
        <AssessmentTab
          quickTranscript={quickTranscript}
          onChatterResolved={refreshOcrNoise}
          onThinkingAccepted={refreshThinkingMarks}
          onQuickTranscriptClose={(notice) => {
            const mode = quickTranscript && quickTranscript.mode;
            const refresh = quickTranscript && quickTranscript.refresh;
            const studentName = quickTranscript && quickTranscript.name;
            setQuickTranscript(null);
            if (refresh === 'blank') refreshUnexpectedBlanks();
            else if (refresh === 'chatter') refreshOcrNoise();
            else if (refresh === 'unmatched') refreshUnmatchedLabels();
            else if (refresh === 'thinking') refreshThinkingMarks();
            else if (refresh === 'late') refreshLatePapers();
            else if (refresh === 'missing') fetchScansFolders();
            else if (mode !== 'comment') refreshUnexpectedBlanks();
            if (notice && mode === 'late') {
              setBlockedDialog(latePaperOpenFailure(notice, studentName));
            } else if (notice) {
              setMessage({ show: true, text: String(notice), type: 'error' });
            }
          }}
        />
      )}
    </div>
  );
}
